
// ── Libraries loaded only when needed ────────────────────────────────────
// The map (Leaflet) and the in-browser ticket reader (Tesseract) are big and
// most visits never use them, so they are fetched the first time a map
// scrolls into view or a ticket is scanned — not with every page.
// Chart.js and supabase-js are in the page head (deferred) where needed:
// deploy/update_pages.py manages those tags.
const CN_LIBS = {
  leafletCss: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css',
  leaflet: 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js',
  tesseract: 'https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js',
  chart: 'https://cdn.jsdelivr.net/npm/chart.js@4.4.0/dist/chart.umd.min.js',
};
const _cnLoading = {};
function cnLoadScript(url) {
  if (!_cnLoading[url]) _cnLoading[url] = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = url; s.async = true;
    s.onload = () => resolve();
    s.onerror = () => { delete _cnLoading[url]; reject(new Error('could not load ' + url)); };
    document.head.appendChild(s);
  });
  return _cnLoading[url];
}
function cnLoadCss(url) {
  if (!_cnLoading[url]) _cnLoading[url] = new Promise((resolve) => {
    const l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = url;
    l.onload = l.onerror = () => resolve();
    document.head.appendChild(l);
  });
  return _cnLoading[url];
}
function cnLoadLeaflet() {
  if (typeof L !== 'undefined') return Promise.resolve();
  return Promise.all([cnLoadCss(CN_LIBS.leafletCss), cnLoadScript(CN_LIBS.leaflet)]);
}
// Run fn once the element is near the screen (or right away without
// IntersectionObserver).
function cnWhenVisible(el, fn) {
  if (!el) return;
  if (!('IntersectionObserver' in window)) { fn(); return; }
  const io = new IntersectionObserver((entries) => {
    if (entries.some(e => e.isIntersecting)) { io.disconnect(); fn(); }
  }, { rootMargin: '300px' });
  io.observe(el);
}

// GA4 event helper. assets/analytics.js (loaded on every page) installs the
// full version (skips automatic re-runs, coalesces calculator bursts); this
// fallback only applies if that file failed to load. No-op without gtag.
if (typeof window.track !== 'function') window.track = function(eventName, params){
  try {
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, params || {});
    }
  } catch(_) {}
};

// ── CLICKABLE STATE OVERLAY (shared by home + winner map) ─────────────────
// Map from GeoJSON feature name to /states/<slug> (the page's canonical URL)
const STATE_SLUG = {
  "Alabama":"alabama","Alaska":"alaska","Arizona":"arizona","Arkansas":"arkansas",
  "California":"california","Colorado":"colorado","Connecticut":"connecticut",
  "Delaware":"delaware","District of Columbia":"washington-dc","Florida":"florida",
  "Georgia":"georgia","Hawaii":"hawaii","Idaho":"idaho","Illinois":"illinois",
  "Indiana":"indiana","Iowa":"iowa","Kansas":"kansas","Kentucky":"kentucky",
  "Louisiana":"louisiana","Maine":"maine","Maryland":"maryland",
  "Massachusetts":"massachusetts","Michigan":"michigan","Minnesota":"minnesota",
  "Mississippi":"mississippi","Missouri":"missouri","Montana":"montana",
  "Nebraska":"nebraska","Nevada":"nevada","New Hampshire":"new-hampshire",
  "New Jersey":"new-jersey","New Mexico":"new-mexico","New York":"new-york",
  "North Carolina":"north-carolina","North Dakota":"north-dakota","Ohio":"ohio",
  "Oklahoma":"oklahoma","Oregon":"oregon","Pennsylvania":"pennsylvania",
  "Rhode Island":"rhode-island","South Carolina":"south-carolina",
  "South Dakota":"south-dakota","Tennessee":"tennessee","Texas":"texas",
  "Utah":"utah","Vermont":"vermont","Virginia":"virginia","Washington":"washington",
  "West Virginia":"west-virginia","Wisconsin":"wisconsin","Wyoming":"wyoming"
  // Puerto Rico intentionally omitted — no page exists
};

let _statesGeoJSONCache = null;
async function loadStatesGeoJSON() {
  if (_statesGeoJSONCache) return _statesGeoJSONCache;
  // assets/ is resolved relative to the current page, so from /states/*.html
  // we prepend ../. Use a robust path.
  const base = location.pathname.includes('/states/') ? '../assets/' : 'assets/';
  try {
    const res = await fetch(base + 'us-states.geo.json', { cache: 'force-cache' });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    _statesGeoJSONCache = await res.json();
    return _statesGeoJSONCache;
  } catch (e) {
    console.warn('states geojson load failed:', e);
    return null;
  }
}

/**
 * Attach a clickable state overlay to an existing Leaflet map.
 * @param {L.Map} map         the Leaflet map instance
 * @param {Object} opts
 *   .source      - GA4 source tag ("home_map" | "winner_map") for track()
 *   .onClick     - optional function(feature, e); if provided, called instead of
 *                  the default nav behavior. Return false to also suppress nav.
 *   .detailed    - if true, slightly stronger hover style (winner map)
 * @returns {L.GeoJSON|null}
 */
async function attachStateClickLayer(map, opts) {
  opts = opts || {};
  if (!map || typeof L === 'undefined') return null;
  const geo = await loadStatesGeoJSON();
  if (!geo) return null;

  const isStatePage = location.pathname.includes('/states/');
  const base = isStatePage ? '../states/' : 'states/';
  const source = opts.source || 'map';
  const detailed = !!opts.detailed;

  const baseStyle = {
    color: '#3b82f6',
    weight: detailed ? 1 : 0.8,
    opacity: detailed ? 0.55 : 0.35,
    fillColor: '#1D4ED8',
    fillOpacity: detailed ? 0.08 : 0.04
  };
  const hoverStyle = {
    color: '#60a5fa',
    weight: 2,
    opacity: 1,
    fillColor: '#60a5fa',
    fillOpacity: detailed ? 0.28 : 0.20
  };

  const layer = L.geoJSON(geo, {
    style: () => baseStyle,
    onEachFeature: (feature, lyr) => {
      const name = feature && feature.properties && feature.properties.name;
      const slug = STATE_SLUG[name];
      if (!slug) {
        // hide features with no state page (e.g. Puerto Rico)
        lyr.setStyle({ opacity: 0, fillOpacity: 0, interactive: false });
        return;
      }
      const dest = base + slug;   // canonical URL (no .html)

      // Accessibility: make each path focusable + screen-reader friendly
      if (lyr._path) {
        lyr._path.setAttribute('tabindex', '0');
        lyr._path.setAttribute('role', 'link');
        lyr._path.setAttribute('aria-label', 'Open ' + name + ' lottery page');
      }

      lyr.bindTooltip(name + ' →', { sticky: true, direction: 'top', opacity: 0.9, className: 'state-tip' });

      lyr.on('mouseover', (e) => {
        e.target.setStyle(hoverStyle);
        if (e.target.bringToFront) e.target.bringToFront();
      });
      lyr.on('mouseout', (e) => { layer.resetStyle(e.target); });

      const go = (ev) => {
        try { window.track('state_nav', { to: slug, source: source }); } catch(_){}
        if (typeof opts.onClick === 'function') {
          const ret = opts.onClick(feature, ev);
          if (ret === false) return; // caller handled; skip nav
        }
        window.location.href = dest;
      };
      lyr.on('click', go);
      if (lyr._path) {
        lyr._path.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); go(ev); }
        });
      }
    }
  }).addTo(map);

  return layer;
}

// ── MULTI-PAGE NAVIGATION MAP ─────────────────────────────────────────────
const PAGE_MAP = {
  'home':       'index.html',
  'analyzer':   'analyzer.html',
  'analyzerMM': 'analyzer-mm.html',
  'trends':     'trends.html',
  'map':        'winner-map.html',
  'calc':       'calculator.html',
  'scanner':    'scanner.html',
  'results':    'results.html',
  'account':    'account.html',  'privacy':    'privacy.html',
  'terms':      'terms.html',
  'math':       'math.html',
  'random':     'random.html',
  'groupplay':  'group-play.html',
  'howtoplay':  'how-to-play.html'
};

function showPage(pg, _unused) {
  const url = PAGE_MAP[pg];
  if (url) { window.location.href = url; }
}

function markActiveNav() {
  const currentPage = document.body.dataset.page || 'home';
  document.querySelectorAll('.nav-link').forEach(l => {
    l.classList.remove('active');
    l.removeAttribute('aria-current');
  });
  const btn = document.querySelector('[data-page="' + currentPage + '"]');
  if (btn) { btn.classList.add('active'); btn.setAttribute('aria-current', 'page'); }
}

// The anon/publishable key is SAFE to include in client-side code.
// Supabase Row Level Security (RLS) protects your data — the anon key
// can only do what your RLS policies permit (SELECT for public tables).
//
// TO ROTATE: Supabase Dashboard → Settings → API → "Reveal" → copy new key here.
// FOR PRODUCTION BUILD: Replace with import.meta.env.VITE_SUPABASE_URL etc. (Vite)
//   or process.env.NEXT_PUBLIC_SUPABASE_URL (Next.js) if you move to a build pipeline.
const SITE_CONFIG = {
  supabaseUrl: 'https://nfyikscwiccugssahnex.supabase.co',
  supabaseKey: 'sb_publishable_R7OnsLjl4fXReEMhGG31rw_uCeTxPEM',
  // Affiliate links — replace YOUR_*_AFFILIATE_LINK placeholders in the HTML above
  // with your real affiliate URLs before publishing.
  // DraftKings:  https://dkng.co/YOUR_REF_CODE
  // Jackpocket:  https://jackpocket.com/r/YOUR_REF_CODE
  // BetMGM:      https://mediaserver.betmgmpartners.com/renderBanner.do?zone=YOUR_ZONE
  // Kalshi:      https://kalshi.com/sign-up/?referral=YOUR_REF_CODE
};

// ── SUPABASE ──────────────────────────────────────────────
const _supa = supabase.createClient(
  SITE_CONFIG.supabaseUrl,
  SITE_CONFIG.supabaseKey
);
window._supa = _supa; // expose for inline scripts (e.g. winners.html) that check window._supa

// EGRESS-OPTIMIZED: reads from static /data/*.json snapshots (generated daily
// by scripts/build-data-snapshots.js) instead of hitting Supabase on every
// page load. Old code (paginated draw_history + weather_history fetch) was
// pulling ~670 KB per visit. Snapshots are served by Apache with long cache
// headers = effectively free bandwidth.
//
// FRESHNESS: the snapshot is rebuilt once a night on the server, but draws land
// in Supabase at different times (Wednesday Powerball draws arrive the next
// morning). So after reading the snapshot we ask Supabase for anything NEWER
// than it — normally 0–2 tiny rows — and merge them in. New draws then show up
// as soon as draw-feed saves them instead of waiting up to a day.
const DRAW_COLS = 'draw_date,game,pick1,pick2,pick3,pick4,pick5,special_ball,number_sum,had_winner,jackpot_amount';
const WX_COLS = 'draw_date,station_id,temperature_f,dew_point_f,humidity_pct,wind_dir,wind_speed_mph,pressure_inhg,sky_condition';
async function _fetchSnapshot(path) {
  try {
    // no-cache = always revalidate with the server (cheap 304 when unchanged)
    const r = await fetch(path, { cache: 'no-cache' });
    if (!r.ok) return { rows: [] };
    const j = await r.json();
    return { rows: Array.isArray(j?.rows) ? j.rows : [], generated_at: j?.generated_at || null };
  } catch (e) { console.warn(path + ' fetch failed:', e); return { rows: [] }; }
}
async function _fetchNewerThan(table, cols, sinceISO, extra) {
  try {
    let q = _supa.from(table).select(cols).gt('draw_date', sinceISO).order('draw_date', { ascending: false }).limit(200);
    if (extra) q = extra(q);
    const { data, error } = await q;
    if (error) throw error;
    return data || [];
  } catch (e) { console.warn(`live ${table} top-up failed:`, e); return []; }
}
// All draws, newest first. Filled by loadDraws(); stays empty on pages that
// don't need draw data (it used to be undeclared there, which threw
// "DD is not defined" on every state/guide/legal page load).
var DD = [];
async function loadDraws() {
  const [snapD, snapW] = await Promise.all([
    _fetchSnapshot('/data/draws.json'), _fetchSnapshot('/data/weather-history.json')
  ]);
  // Oldest "latest date" across both games, so neither game misses a draw
  const latestBy = (rows) => { const m = {}; rows.forEach(r => { if (!m[r.game] || r.draw_date > m[r.game]) m[r.game] = r.draw_date; }); return m; };
  const lb = latestBy(snapD.rows);
  const fallbackSince = new Date(Date.now() - 45 * 864e5).toISOString().slice(0, 10);
  const drawSince = (lb.powerball && lb.megamillions) ? (lb.powerball < lb.megamillions ? lb.powerball : lb.megamillions) : fallbackSince;
  const wxLatest = snapW.rows.reduce((m, r) => r.draw_date > m ? r.draw_date : m, '');
  const wxSince = wxLatest && wxLatest < drawSince ? wxLatest : drawSince;
  const [freshD, freshW] = await Promise.all([
    _fetchNewerThan('draw_history', DRAW_COLS, drawSince),
    _fetchNewerThan('weather_history', WX_COLS, wxSince, q => q.eq('is_draw_night', true))
  ]);
  // Merge (live rows win), newest first
  const byKey = new Map();
  [...snapD.rows, ...freshD].forEach(r => byKey.set(r.game + '|' + r.draw_date, r));
  const rawDraws = [...byKey.values()].sort((a, b) => a.draw_date < b.draw_date ? 1 : a.draw_date > b.draw_date ? -1 : 0);
  const wxKey = new Map();
  [...snapW.rows, ...freshW].forEach(r => wxKey.set(r.station_id + '|' + r.draw_date, r));
  const rawWx = [...wxKey.values()];
  window._dataInfo = { snapshotAt: snapD.generated_at, liveDraws: freshD.length, liveWx: freshW.length,
                       newestPB: rawDraws.find(d => d.game === 'powerball')?.draw_date || null,
                       newestMM: rawDraws.find(d => d.game === 'megamillions')?.draw_date || null };
  if (freshD.length) console.info(`[data] snapshot ${snapD.generated_at} + ${freshD.length} newer draw(s) from Supabase`);

  const allDraws = rawDraws.map(d => {
    const [yr, mo, dy] = d.draw_date.split('-');
    return {
      iso: d.draw_date, // YYYY-MM-DD — use this for comparisons and sorting
      date: `${+mo}-${+dy}-${yr}`,
      month: +mo, day: +dy, year: +yr,
      game: d.game,
      picks: [d.pick1, d.pick2, d.pick3, d.pick4, d.pick5],
      pb: d.special_ball,
      sum: d.number_sum,
      had_winner: d.had_winner || false,
      jackpot: d.jackpot_amount || 0,
      temp: null, dew: null, hum: null, wind: null, ws: null, pres: null, cond: null
    };
  });

  // Merge weather data by (date, station)
  if (rawWx.length) {
    const wxMap = {};
    rawWx.forEach(w => { wxMap[w.draw_date + '_' + w.station_id] = w; });
    allDraws.forEach(d => {
      const sid = d.game === 'powerball' ? 'KTLH' : 'KATL';
      const key = `${d.year}-${String(d.month).padStart(2,'0')}-${String(d.day).padStart(2,'0')}_${sid}`;
      const w = wxMap[key];
      if (w) { d.temp=w.temperature_f; d.dew=w.dew_point_f; d.hum=w.humidity_pct; d.wind=w.wind_dir; d.ws=w.wind_speed_mph; d.pres=w.pressure_inhg; d.cond=w.sky_condition; }
    });
  }

  DD = allDraws;
  renderAccountStats();
  return allDraws;
}

function renderAccountStats() {
  if (!DD.length) return;
  const pb = DD.filter(d=>d.game==='powerball');
  const mm = DD.filter(d=>d.game==='megamillions');
  const wxCount = DD.filter(d=>d.temp!==null).length;
  const pbWx = pb.filter(d=>d.temp!==null).length;
  const mmWx = mm.filter(d=>d.temp!==null).length;
  const s = (id,v) => { const el=document.getElementById(id); if(el) el.textContent=v; };
  s('stat-total-draws', DD.length.toLocaleString());
  s('stat-wx-pct', Math.round(wxCount/DD.length*100));
  s('stat-pb-draws', pb.length.toLocaleString());
  const pbDates = pb.map(d=>d.iso).sort(); s('stat-pb-range', pbDates.length ? pbDates[0]+' → '+pbDates[pbDates.length-1] : '—');
  s('stat-pb-wx', pbWx+' draws ('+Math.round(pbWx/pb.length*100)+'%)');
  s('stat-mm-draws', mm.length.toLocaleString());
  const mmDates = mm.map(d=>d.iso).sort(); s('stat-mm-range', mmDates.length ? mmDates[0]+' → '+mmDates[mmDates.length-1] : '—');
  s('stat-mm-wx', mmWx+' draws ('+Math.round(mmWx/(mm.length||1)*100)+'%)');
  // Update dynamic draw counts in analyzer
  const pbDcEl = document.getElementById('pb-draw-count-temp');
  if (pbDcEl) pbDcEl.textContent = pb.length.toLocaleString() + ' draws';
  const mmDcEl = document.getElementById('mm-draw-count');
  if (mmDcEl) mmDcEl.textContent = mm.length.toLocaleString() + ' MM draws';
}

// ── THEME ─────────────────────────────────────────────────
let currentTheme = localStorage.getItem('llm-theme') || 'dark';
let currentCB    = localStorage.getItem('llm-cb')    || '';
let fontSize     = parseInt(localStorage.getItem('llm-fs') || '0');
function applyTheme() {
  document.documentElement.setAttribute('data-theme', currentTheme);
  currentCB ? document.documentElement.setAttribute('data-cb', currentCB)
            : document.documentElement.removeAttribute('data-cb');
  document.documentElement.style.fontSize = (100 + fontSize * 10) + '%';
  const ts = document.getElementById('themeSelect'); if (ts) ts.value = currentTheme;
  const cb = document.getElementById('cbSelect');    if (cb) cb.value = currentCB;
  localStorage.setItem('llm-theme', currentTheme);
  localStorage.setItem('llm-cb',    currentCB);
  localStorage.setItem('llm-fs',    fontSize);
  updateChartDefaults();
}
function setTheme(t){ currentTheme=t; applyTheme(); }
function setCB(v)   { currentCB=v;   applyTheme(); }
function setFontSize(d){ fontSize=Math.max(-1,Math.min(3,fontSize+d)); applyTheme(); }
function cycleTheme(){ const o=['dark','light','hc']; currentTheme=o[(o.indexOf(currentTheme)+1)%3]; applyTheme(); showToast('Theme: '+currentTheme); }
function toggleA11y(){ const p=document.getElementById('a11yPanel'),b=document.getElementById('a11yBtn'); b.setAttribute('aria-expanded',p.classList.toggle('open')); }
function updateChartDefaults(){ if(typeof Chart==='undefined') return; const l=currentTheme==='light'; Chart.defaults.color=l?'#475569':'#8892a4'; Chart.defaults.borderColor=l?'rgba(0,0,0,.06)':'rgba(255,255,255,.04)'; }

// ── NAV ───────────────────────────────────────────────────
let mapInited=false, trendsInited=false, calcInited=false;

// ── TOAST ─────────────────────────────────────────────────
function showToast(msg,ms=2800){ const t=document.getElementById('toast'); const m=document.getElementById('toastMsg'); if(!t||!m){ console.info('[toast]', msg); return; } m.textContent=msg; t.classList.add('show'); clearTimeout(t._to); t._to=setTimeout(()=>t.classList.remove('show'),ms); }

// ── MOON ──────────────────────────────────────────────────
function moonPhase(d=new Date()){ const names=['🌑 New','🌒 Wax.Cresc','🌓 1st Qtr','🌔 Wax.Gibb','🌕 Full','🌖 Wan.Gibb','🌗 Last Qtr','🌘 Wan.Cresc'],ref=new Date(2024,0,11),c=29.53059,pos=((((d-ref)/86400000)%c)+c)%c; return { name:names[Math.round(pos/(c/8))%8], illum:Math.round(50*(1-Math.cos(2*Math.PI*pos/c))), pos }; }

// ── WEATHER ───────────────────────────────────────────────
const CONDS=['Fair','Partly Cloudy','Mostly Cloudy','Cloudy','Rain'];
const ICONS=['☀️','⛅','🌥️','☁️','🌧️'];
let forecasts=[];

function simWeather(baseTemp,seed=0) {
  const r=(n)=>((n*9301+49297)%233280)/233280; // deterministic pseudo-rand
  const ci=Math.floor(r(seed+1)*CONDS.length);
  const winds=['N','NNE','NE','E','SE','SSE','S','SW','W','NW','NNW','CALM'];
  const hum=Math.round(58+r(seed+2)*35);
  const temp=Math.round(baseTemp+(r(seed+3)-0.5)*6);
  const pres=+(29.72+r(seed+4)*0.55).toFixed(2);
  const ws=Math.floor(r(seed+5)*18);
  return { temp, hum, pres, wind:winds[Math.floor(r(seed+6)*winds.length)], ws, cond:CONDS[ci], icon:ICONS[ci], dew:Math.round(temp-(100-hum)*0.36) };
}


// ── LIVE FORECAST from weather_live table ─────────────────────

// ── LOAD STATE TAXES from Supabase ────────────────────────────
// NOTE: state_taxes.effective_lottery_rate is stored as a DECIMAL FRACTION
// (0.04 for Ohio, 0.133 for California). We normalize to whole-percent
// numbers (4, 13.3) at render time so the rest of the calc code (which
// divides the option value by 100) produces the right fraction.
async function loadStateTaxes() {
  const sel = document.getElementById('c-state');
  if (!sel) return;
  // Render a fraction (0.04) as a whole-percent number.
  // pctVal  = precise numeric value used in the option's value attribute
  //           (e.g. "4", "13.3") so the rest of the calc math divides cleanly.
  // pctDisp = user-facing label padded to 2 decimal places (e.g. "4.00", "13.30").
  const pctVal = (frac) => {
    const p = (Number(frac) || 0) * 100;
    return (Math.round(p * 100) / 100).toString();
  };
  const pctDisp = (frac) => {
    const p = (Number(frac) || 0) * 100;
    return (Math.round(p * 100) / 100).toFixed(2);
  };
  try {
    const { data } = await _supa
      .from('state_taxes')
      .select('state_code,state_name,effective_lottery_rate')
      .order('state_name', { ascending: true });
    if (!data?.length) {
      sel.innerHTML = '<option value="4|OH">Ohio — 4%</option>';
      return;
    }
    const norm = data.map(r => ({
      code: r.state_code,
      name: r.state_name,
      // pctVal is the whole-percent number stored as string, e.g. "13.3"
      // pctDisp is the user-facing label padded to 2 decimals, e.g. "13.30"
      pctVal:  pctVal(r.effective_lottery_rate),
      pctDisp: pctDisp(r.effective_lottery_rate)
    }));
    // Group by rate range (using the whole-percent number)
    const numeric = (x) => parseFloat(x.pctVal) || 0;
    const noTax = norm.filter(r => numeric(r) === 0);
    const low   = norm.filter(r => numeric(r) > 0 && numeric(r) < 4);
    const mid   = norm.filter(r => numeric(r) >= 4 && numeric(r) < 6);
    const high  = norm.filter(r => numeric(r) >= 6);
    const opt   = (r) => `<option value="${r.pctVal}|${r.code}">${r.name} — ${r.pctDisp}%</option>`;

    let html = '';
    if (noTax.length) html += '<optgroup label="No State Tax (0%)">'   + noTax.map(opt).join('') + '</optgroup>';
    if (low.length)   html += '<optgroup label="Low Tax (1–4%)">'      + low.map(opt).join('')   + '</optgroup>';
    if (mid.length)   html += '<optgroup label="Moderate Tax (4–6%)">' + mid.map(opt).join('')   + '</optgroup>';
    if (high.length)  html += '<optgroup label="Higher Tax (6%+)">'    + high.map(opt).join('')  + '</optgroup>';
    sel.innerHTML = html;

    // Default to Ohio
    const ohOpt = sel.querySelector('option[value*="|OH"]');
    if (ohOpt) ohOpt.selected = true;
  } catch(e) {
    console.warn('state_taxes fetch error:', e);
    sel.innerHTML = '<option value="4|OH">Ohio — 4% (DB unavailable)</option>';
  }
}


// ── BACKFILL MISSED DRAWS ─────────────────────────────────────
async function backfillMissedDraws() {
  const statusEl = document.getElementById('backfill-status');
  const s = (msg) => { if(statusEl) statusEl.innerHTML = msg; console.log('Backfill:', msg); };
  s('⏳ Checking for missed draws...');

  try {
    // Get all draw dates we have. PostgREST returns at most 1,000 rows per
    // request, so page through — otherwise every draw after the first 1,000
    // was reported as "missed".
    const existing = [];
    for (let from = 0; ; from += 1000) {
      const { data: page, error } = await _supa
        .from('draw_history')
        .select('draw_date,game')
        .order('draw_date', { ascending: true })
        .range(from, from + 999);
      if (error) throw error;
      existing.push(...(page || []));
      if (!page || page.length < 1000) break;
    }

    if (!existing?.length) {
      s('❌ No draws in database. Run EF3 draw-feed first.');
      return;
    }

    const haveDates = new Set(existing.map(r => r.draw_date + '_' + r.game));
    const earliest = existing[0].draw_date;
    const latest = existing[existing.length - 1].draw_date;

    // Generate expected draw dates
    // PB: Mon(1), Wed(3), Sat(6) — but before Feb 2022 it was Wed(3), Sat(6) only
    // MM: Tue(2), Fri(5)
    const expected = [];
    const start = new Date(earliest + 'T12:00:00Z');
    const end = new Date(Date.now() - 864e5);  // through yesterday (today's draw may not be in yet)
    const d = new Date(start);
    while (d <= end) {
      const dow = d.getUTCDay();
      const ds = d.toISOString().slice(0, 10);
      // Powerball days
      if (dow === 1 || dow === 3 || dow === 6) {
        // Mon draws started Aug 23, 2021
        if (dow === 1 && ds < '2021-08-23') { /* skip */ }
        else if (!haveDates.has(ds + '_powerball')) expected.push({ date: ds, game: 'powerball' });
      }
      // Mega Millions days
      if (dow === 2 || dow === 5) {
        if (!haveDates.has(ds + '_megamillions')) expected.push({ date: ds, game: 'megamillions' });
      }
      d.setUTCDate(d.getUTCDate() + 1);
    }

    if (expected.length === 0) {
      s('✅ No missed draws found! All draw dates are accounted for.');
      return;
    }

    s(`⚠️ Found <strong>${expected.length}</strong> missed draws. Showing gaps:`);

    // Group by month for readability
    const byMonth = {};
    expected.forEach(e => {
      const mo = e.date.slice(0, 7);
      if (!byMonth[mo]) byMonth[mo] = [];
      byMonth[mo].push(e);
    });

    let detail = '<div style="max-height:300px;overflow-y:auto;margin-top:10px">';
    for (const [mo, draws] of Object.entries(byMonth).sort()) {
      detail += `<div style="margin-bottom:8px"><strong style="color:var(--accent)">${mo}</strong>: `;
      detail += draws.map(d => `<span style="font-size:11px;padding:2px 6px;border-radius:4px;background:${d.game==='powerball'?'rgba(239,71,111,.15)':'rgba(0,180,216,.15)'};color:${d.game==='powerball'?'var(--danger)':'var(--accent)'}">${d.date.slice(5)} ${d.game==='powerball'?'PB':'MM'}</span>`).join(' ');
      detail += '</div>';
    }
    detail += '</div>';
    detail += `<div style="margin-top:12px;font-size:12px;color:var(--text2)">
      <strong>To backfill:</strong> Run the <strong>draw-feed</strong> edge function (EF3) — it will check the official lottery APIs and fill in any draws that are missing from the database.
      The function checks for duplicates, so it's safe to run multiple times.
      <br><br>
      <strong>For weather data:</strong> Run the <strong>noaa-backfill</strong> edge function (EF1) after draws are populated — it will fetch NOAA weather observations for each draw date.
    </div>`;

    s(detail);
  } catch(e) {
    s('❌ Error checking draws: ' + e.message);
  }
}

async function loadLiveForecast() {
  try {
    // Pull more rows — weather_live stores overlapping forecast cycles,
    // so many rows can share the same hour. We sort newest first and dedupe
    // by hour below so each hour renders exactly once.
    const { data } = await _supa
      .from('weather_live')
      .select('station_id,valid_time,temperature_f,humidity_pct,wind_dir,wind_speed_mph,pressure_inhg,dew_point_f,short_forecast')
      .eq('station_id', 'KTLH')  // Powerball draw site
      .order('valid_time', { ascending: false })
      .limit(96);
    if (!data?.length) return;

    const items = dedupeHourlyForecasts(data);

    if (items.length > 0) {
      window._liveForecasts = items;
      renderForecast(); // re-render with live data
      selectDrawDateWeather();
    }
  } catch(e) { console.warn('weather_live forecast error:', e); }
}

async function loadLiveMMForecast() {
  try {
    const { data } = await _supa
      .from('weather_live')
      .select('station_id,valid_time,temperature_f,humidity_pct,wind_dir,wind_speed_mph,pressure_inhg,dew_point_f,short_forecast')
      .eq('station_id', 'KATL')  // Mega Millions draw site (Atlanta)
      .order('valid_time', { ascending: false })
      .limit(96);
    if (!data?.length) return;

    const items = dedupeHourlyForecasts(data);

    if (items.length > 0) {
      window._mmLiveForecasts = items;
      renderMMForecast(); // re-render with live data
      selectMMDrawDateWeather();
    }
  } catch(e) { console.warn('MM weather_live forecast error:', e); }
}

// ── Next-draw-date helpers ────────────────────────────────────
// Returns the ET calendar date (YYYY-MM-DD) of the next scheduled draw for
// the given game. If the current ET time is *before* today's draw cutoff
// (10:59 PM ET for PB, 11:00 PM ET for MM) AND today is a draw day, returns
// today; otherwise returns the next upcoming draw day. Used by the analyzer
// forecast strip so it only shows the draw-day's hourly weather.
const _ET_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23', weekday: 'short'
});
const _DOW = { Sun:0, Mon:1, Tue:2, Wed:3, Thu:4, Fri:5, Sat:6 };
// Eastern-time calendar parts for an instant, using the real America/New_York
// rules (DST starts 2nd Sunday of March, ends 1st Sunday of November).
function _etDateParts(d = new Date()) {
  const p = {}; _ET_FMT.formatToParts(d).forEach(x => { p[x.type] = x.value; });
  const y = +p.year, mo = +p.month - 1, day = +p.day;
  return { y, mo, d: day, dow: _DOW[p.weekday], hh: +p.hour % 24, mm: +p.minute,
           iso: `${p.year}-${p.month}-${p.day}` };
}
function getNextDrawDateISO(game) {
  // PB draws Mon(1)/Wed(3)/Sat(6) at 22:59 ET. MM draws Tue(2)/Fri(5) at 23:00 ET.
  const drawDays = game === 'mm' ? [2, 5] : [1, 3, 6];
  const cutoffMin = game === 'mm' ? 23 * 60 : 22 * 60 + 59; // minutes after midnight ET
  const now = _etDateParts();
  // If today is a draw day and the draw hasn't happened yet, today counts.
  if (drawDays.includes(now.dow) && now.hh * 60 + now.mm < cutoffMin) return now.iso;
  // Otherwise walk forward up to 7 days
  for (let offset = 1; offset <= 7; offset++) {
    const futureUTC = new Date(Date.UTC(now.y, now.mo, now.d + offset, 12, 0, 0));
    const ep = _etDateParts(futureUTC);
    if (drawDays.includes(ep.dow)) return ep.iso;
  }
  return now.iso; // fallback (shouldn't happen)
}

// Dedupes overlapping forecast cycles from weather_live: keeps one row per
// (calendar-date, ET-hour). Rows must be passed in newest-first order; the
// first row seen for a given hour wins (= most recent forecast cycle).
// Returns hours sorted oldest → newest for UI rendering.
function _numOrNull(v, digits) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v); if (!Number.isFinite(n)) return null;
  return digits ? +n.toFixed(digits) : Math.round(n);
}
const _dash = (v, suffix = '') => (v === null || v === undefined || v === '') ? '—' : v + suffix;
function dedupeHourlyForecasts(rows) {
  const now = new Date();
  const seen = new Map(); // key: "YYYY-MM-DD_HH" (ET) → item

  rows.forEach(r => {
    const vt = new Date(r.valid_time);
    const et = _etDateParts(vt);
    const hh = et.hh;
    const dayKey = et.iso;
    const key = `${dayKey}_${hh}`;
    if (seen.has(key)) return; // already have the newest cycle for this hour

    const isNow = Math.abs(vt - now) < 3600000;
    const isPast = vt < now;
    const isDraw = (hh === 22 || hh === 23); // draw 10-11 PM ET
    const label = hh === 0 ? '12am' : hh < 12 ? hh + 'am' : hh === 12 ? '12pm' : (hh - 12) + 'pm';

    seen.set(key, {
      label: isDraw ? '🎰 ' + label : label,
      _sortKey: vt.getTime(),
      etDate: dayKey,        // ET calendar date — used to filter to draw-day only
      etHour: hh,            // ET hour 0–23
      // Missing values stay null. The NWS hourly forecast has no pressure, and
      // older rows have no humidity/dew point; we never substitute made-up numbers.
      temp: _numOrNull(r.temperature_f, 0),
      hum:  _numOrNull(r.humidity_pct, 0),
      pres: _numOrNull(r.pressure_inhg, 2),
      wind: r.wind_dir || null,
      ws:   _numOrNull(r.wind_speed_mph, 0),
      dew:  _numOrNull(r.dew_point_f, 0),
      cond: r.short_forecast || null,
      icon: wxIcon(r.short_forecast || ''),
      isDraw, isNow, isPast
    });
  });

  return Array.from(seen.values())
    .sort((a, b) => a._sortKey - b._sortKey);
}

function buildForecast() {
  // Only show the upcoming draw day — Powerball: Mon/Wed/Sat 10:59 PM ET.
  const drawISO = getNextDrawDateISO('pb');

  // Live data path: filter weather_live rows down to the draw-day's ET hours.
  if (window._liveForecasts && window._liveForecasts.length > 0) {
    const dayOnly = window._liveForecasts.filter(f => f.etDate === drawISO);
    if (dayOnly.length > 0) return dayOnly;
    // If the draw-day rows haven't landed in the DB yet, fall through to sim
    // so the strip never goes empty.
  }

  // No live NOAA rows for the draw day yet (the hourly forecast only reaches
  // ~4 days out). Return nothing rather than invent weather; the strip shows a
  // note and the user can type the expected conditions.
  return [];
}

// Format an ISO date (YYYY-MM-DD) as "Wed, May 27" for the analyzer header.
function _fmtDrawDayLabel(iso) {
  if (!iso) return 'Draw Day';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function renderForecast() {
  forecasts=buildForecast();
  const strip=document.getElementById('fxStrip'); if(!strip) return;
  // Update the day-label in the strip header so the user can see WHICH day's
  // weather is shown (e.g. "Wed, May 27 · Draw Night").
  const dayLbl = document.getElementById('fxDayLabel');
  if (dayLbl) {
    const drawISO = (forecasts[0] && forecasts[0].etDate) || getNextDrawDateISO('pb');
    dayLbl.textContent = _fmtDrawDayLabel(drawISO) + ' · Draw Night';
  }
  strip.innerHTML=forecasts.map((fx,i)=>`
    <div class="fx-hour${fx.isDraw?' draw-time':''}${fx.isPast?' past':''}${fx.isNow?' sel':''}"
         role="listitem" tabindex="0"
         onclick="selectForecast(${i})"
         onkeydown="if(event.key==='Enter'||event.key===' ')selectForecast(${i})"
         aria-label="${fx.label} ${_dash(fx.temp,'°F')} ${fx.cond||''}${fx.isDraw?' DRAW TIME':''}"
         data-fxi="${i}" style="${fx.isPast?'opacity:.55':''}">
      <div class="fx-time">${fx.label}</div>
      <div class="fx-icon" aria-hidden="true">${fx.icon}</div>
      <div class="fx-temp">${_dash(fx.temp,'°')}</div>
      <div class="fx-sub">${_dash(fx.pres,'"')}</div>
    </div>`).join('');
  if(!forecasts.length){
    strip.innerHTML=`<div style="font-size:12px;color:var(--text3);padding:8px">No NOAA hourly forecast for ${_fmtDrawDayLabel(getNextDrawDateISO('pb'))} yet (it reaches about 4 days out). Enter the expected conditions above, or leave a box blank to skip that factor.</div>`;
    return;
  }
  // Auto-select 11 PM ET (historical draw-night weather is the ~11:53 PM reading), else any draw slot
  let drawIdx=forecasts.findIndex(f=>f.etHour===23);
  if(drawIdx<0) drawIdx=forecasts.findIndex(f=>f.isDraw);
  const nowIdx=forecasts.findIndex(f=>f.isNow);
  const pick=drawIdx>=0?drawIdx:nowIdx>=0?nowIdx:0;
  if(forecasts.length>0) selectForecast(pick);
}

function selectForecast(i) {
  document.querySelectorAll('.fx-hour').forEach(el=>el.classList.remove('sel'));
  const el=document.querySelector(`[data-fxi="${i}"]`); if(el) el.classList.add('sel');
  const fx=forecasts[i]; if(!fx) return;
  // Null-guarded: analyzer inputs exist only on analyzer.html, the now-display
  // partially on other pages — set whatever is present on the current page.
  const _set=(id,prop,val)=>{ const e=document.getElementById(id); if(e) e[prop]=val; };
  const _v=(x)=>x===null||x===undefined?'':x;
  _set('ana-temp','value',_v(fx.temp));
  _set('ana-hum','value',_v(fx.hum));
  _set('ana-pres','value',_v(fx.pres));
  _set('ana-ws','value',_v(fx.ws));
  if(fx.wind) _set('ana-wdir','value',fx.wind);
  if(fx.cond) _set('ana-cond','value',_condToOption(fx.cond));
  _set('ana-dew','value',_v(fx.dew));
  // Update current display
  _set('ana-now-temp','textContent',_dash(fx.temp,'°'));
  _set('ana-now-icon','textContent',fx.icon);
  _set('ana-now-cond','textContent',fx.cond||'—');
  _set('ana-now-hum','textContent',_dash(fx.hum,'%'));
  _set('ana-now-pres','textContent',_dash(fx.pres,'"'));
  _set('ana-now-wind','textContent',(fx.wind||'—')+' '+_dash(fx.ws,'mph'));
  _set('ana-now-dew','textContent',_dash(fx.dew,'°F'));
  const mp=moonPhaseForISO(fx.etDate||getNextDrawDateISO('pb'));
  const moonEl=document.getElementById('ana-now-moon'); if(moonEl) moonEl.textContent=mp.name+' '+mp.illum+'%';
  const moonPv=document.getElementById('moonPhaseVal'); if(moonPv) moonPv.textContent=mp.name;
  // Auto re-run analyzer when forecast selection changes
  if(document.body.dataset.page==='analyzer' && DD.length>0) {
    clearTimeout(window._fxRerunTimer);
    window._fxRerunTimer=setTimeout(()=>{ if(typeof runAnalyzer==='function') runAnalyzer(); },300);
  }
}


// ══════════════════════════════════════════════════════════════════════════════
// MM ANALYZER — ENGINE + FORECAST
// ══════════════════════════════════════════════════════════════════════════════
let mmForecasts = [];

function buildMMForecast() {
  // Only show the upcoming draw day — Mega Millions: Tue/Fri 11:00 PM ET.
  const drawISO = getNextDrawDateISO('mm');

  // Live data path: filter weather_live rows down to the draw-day's ET hours.
  if (window._mmLiveForecasts && window._mmLiveForecasts.length > 0) {
    const dayOnly = window._mmLiveForecasts.filter(f => f.etDate === drawISO);
    if (dayOnly.length > 0) return dayOnly;
  }

  return []; // no live rows for the draw day yet — never invent weather
}

function renderMMForecast() {
  mmForecasts = buildMMForecast();
  const strip = document.getElementById('mm-fxStrip'); if (!strip) return;
  const dayLbl = document.getElementById('mm-fxDayLabel');
  if (dayLbl) {
    const drawISO = (mmForecasts[0] && mmForecasts[0].etDate) || getNextDrawDateISO('mm');
    dayLbl.textContent = _fmtDrawDayLabel(drawISO) + ' · Draw Night';
  }
  strip.innerHTML = mmForecasts.map((fx, i) => `
    <div class="fx-hour${fx.isDraw ? ' draw-time' : ''}${fx.isPast ? ' past' : ''}${fx.isNow ? ' sel' : ''}"
         role="listitem" tabindex="0"
         onclick="selectMMForecast(${i})"
         onkeydown="if(event.key==='Enter'||event.key===' ')selectMMForecast(${i})"
         aria-label="${fx.label} ${_dash(fx.temp,'°F')} ${fx.cond||''}${fx.isDraw ? ' DRAW TIME' : ''}"
         data-mmfxi="${i}" style="${fx.isPast ? 'opacity:.55' : ''}">
      <div class="fx-time">${fx.label}</div>
      <div class="fx-icon" aria-hidden="true">${fx.icon}</div>
      <div class="fx-temp">${_dash(fx.temp,'°')}</div>
      <div class="fx-sub">${_dash(fx.pres,'"')}</div>
    </div>`).join('');
  if (!mmForecasts.length) {
    strip.innerHTML = `<div style="font-size:12px;color:var(--text3);padding:8px">No NOAA hourly forecast for ${_fmtDrawDayLabel(getNextDrawDateISO('mm'))} yet (it reaches about 4 days out). Enter the expected conditions above, or leave a box blank to skip that factor.</div>`;
    return;
  }
  let drawIdx = mmForecasts.findIndex(f => f.etHour === 23);
  if (drawIdx < 0) drawIdx = mmForecasts.findIndex(f => f.isDraw);
  selectMMForecast(drawIdx >= 0 ? drawIdx : mmForecasts.findIndex(f => f.isNow));
}

function selectMMForecast(i) {
  document.querySelectorAll('[data-mmfxi]').forEach(el => el.classList.remove('sel'));
  const el = document.querySelector(`[data-mmfxi="${i}"]`); if (el) el.classList.add('sel');
  const fx = mmForecasts[i]; if (!fx) return;
  const _set = (id, prop, val) => { const e = document.getElementById(id); if (e) e[prop] = val; };
  const _v = (x) => x === null || x === undefined ? '' : x;
  _set('mm-ana-temp', 'value', _v(fx.temp));
  _set('mm-ana-hum', 'value', _v(fx.hum));
  _set('mm-ana-pres', 'value', _v(fx.pres));
  _set('mm-ana-ws', 'value', _v(fx.ws));
  if (fx.wind) _set('mm-ana-wdir', 'value', fx.wind);
  if (fx.cond) _set('mm-ana-cond', 'value', _condToOption(fx.cond));
  _set('mm-ana-dew', 'value', _v(fx.dew));
  _set('mm-now-temp', 'textContent', _dash(fx.temp, '°'));
  _set('mm-now-icon', 'textContent', fx.icon);
  _set('mm-now-cond', 'textContent', fx.cond || '—');
  _set('mm-now-hum', 'textContent', _dash(fx.hum, '%'));
  _set('mm-now-pres', 'textContent', _dash(fx.pres, '"'));
  _set('mm-now-wind', 'textContent', (fx.wind || '—') + ' ' + _dash(fx.ws, 'mph'));
  _set('mm-now-dew', 'textContent', _dash(fx.dew, '°F'));
  const mp = moonPhaseForISO(fx.etDate || getNextDrawDateISO('mm'));
  const moonEl = document.getElementById('mm-now-moon'); if (moonEl) moonEl.textContent = mp.name + ' ' + mp.illum + '%';
  const moonPv = document.getElementById('mm-moonPhaseVal'); if (moonPv) moonPv.textContent = mp.name;
}

function selectMMDrawDateWeather() {
  if (!mmForecasts.length) return;
  let drawSlot = mmForecasts.findIndex(f => f.etHour === 23);
  if (drawSlot < 0) drawSlot = mmForecasts.findIndex(f => f.isDraw);
  if (drawSlot >= 0) selectMMForecast(drawSlot);
}

// MM Metrics state
const mmActiveMetrics = new Set(['temp', 'hum', 'pres', 'wind']);
const mmWeights = { temp: 30, hum: 25, pres: 25, wind: 20 };
const mmCustomMetrics = [];

function toggleMMMetric(btn) {
  const METRICS = ['temp','hum','pres','wind'];
  const m = btn.dataset.metric;
  if (mmActiveMetrics.has(m)) {
    mmActiveMetrics.delete(m);
    btn.classList.remove('on'); btn.setAttribute('aria-pressed', 'false');
    const freed = mmWeights[m];
    mmWeights[m] = 0;
    const active = METRICS.filter(x => mmActiveMetrics.has(x));
    if (active.length > 0 && freed > 0) {
      const share = Math.floor(freed / active.length);
      active.forEach(x => { mmWeights[x] += share; });
      const remainder = freed - share * active.length;
      if (remainder > 0) mmWeights[active[0]] += remainder;
    }
  } else {
    mmActiveMetrics.add(m);
    btn.classList.add('on'); btn.setAttribute('aria-pressed', 'true');
    const target = 25;
    const active = METRICS.filter(x => mmActiveMetrics.has(x) && x !== m);
    const steal = Math.min(target, active.reduce((s, x) => s + mmWeights[x], 0));
    if (active.length > 0 && steal > 0) {
      const perOther = Math.floor(steal / active.length);
      active.forEach(x => { mmWeights[x] = Math.max(0, mmWeights[x] - perOther); });
    }
    mmWeights[m] = steal;
    const total = METRICS.reduce((s, x) => s + mmWeights[x], 0);
    if (total !== 100 && total > 0) {
      const scale = 100 / total;
      METRICS.forEach(x => { mmWeights[x] = Math.round(mmWeights[x] * scale); });
      const adj = 100 - METRICS.reduce((s, x) => s + mmWeights[x], 0);
      if (adj !== 0) mmWeights[METRICS.find(x => mmWeights[x] + adj >= 0)] += adj;
    }
  }
  METRICS.forEach(x => {
    const sl = document.getElementById('mm-r-' + x), lb = document.getElementById('mm-w-' + x);
    if (sl) sl.value = mmWeights[x]; if (lb) lb.textContent = mmWeights[x] + '%';
  });
  updateMMWeightBar();
}
function updateMMWeight(m, v) {
  const METRICS = ['temp','hum','pres','wind'];
  const newVal = Math.max(0, Math.min(100, +v));
  const delta = newVal - mmWeights[m];
  if (delta === 0) return;
  const others = METRICS.filter(x => x !== m);
  const othersTotal = others.reduce((s, x) => s + mmWeights[x], 0);
  if (othersTotal > 0) {
    others.forEach(x => {
      mmWeights[x] = Math.max(0, Math.round(mmWeights[x] - delta * (mmWeights[x] / othersTotal)));
    });
    const total = newVal + others.reduce((s, x) => s + mmWeights[x], 0);
    const drift = 100 - total;
    if (drift !== 0) {
      const fix = others.find(x => mmWeights[x] + drift >= 0 && mmWeights[x] + drift <= 100);
      if (fix) mmWeights[fix] += drift;
    }
  } else {
    const share = Math.round((100 - newVal) / others.length);
    others.forEach(x => { mmWeights[x] = share; });
  }
  mmWeights[m] = newVal;
  METRICS.forEach(x => {
    const sl = document.getElementById('mm-r-' + x);
    const lb = document.getElementById('mm-w-' + x);
    if (sl) sl.value = mmWeights[x];
    if (lb) lb.textContent = mmWeights[x] + '%';
  });
  updateMMWeightBar();
}
function mmUpdateWeight(m, v) { updateMMWeight(m, v); } // alias for HTML oninput
function updateMMWeightBar() {
  const total = Object.values(mmWeights).reduce((s, v) => s + v, 0) || 1;
  ['temp', 'hum', 'pres', 'wind'].forEach(m => { const el = document.getElementById('mm-wbar-' + m); if (el) el.style.width = Math.round(mmWeights[m] / total * 100) + '%'; });
  const el = document.getElementById('mm-weightTotal'); if (el) { el.textContent = 'Total: ' + total + '%'; el.style.color = total > 100 ? 'var(--danger)' : total === 100 ? 'var(--ok)' : 'var(--warn)'; }
}

function toggleMMMetricsHelp() {
  const p = document.getElementById('mm-metricsHelp'), b = document.getElementById('mm-metricsHelpBtn');
  const open = p.style.display === 'none'; p.style.display = open ? 'block' : 'none';
  b.setAttribute('aria-expanded', open); b.textContent = open ? '✕ Close' : '❓ How it works';
}

const MM_CM_HINTS = {
  decay:    'Fast-decay recency: weights recent draws far more heavily (half-life scales with intensity). Backtested winner.',
  unpop:    'Anti-popular (EV): damps common picks (1–31 birthdays, lucky 7/11/13, round numbers) and lifts 38+ to reduce jackpot-sharing.',
  high:     'Multiplies scores of numbers 48–70. At intensity 10: +50% boost.',
  low:      'Multiplies scores of numbers 1–23. At intensity 10: +50% boost.',
  mid:      'Multiplies scores of numbers 24–47. The "middle range" bias.',
  odd:      'Multiplies scores of all odd numbers.',
  even:     'Multiplies scores of all even numbers.',
  prime:    'Boosts prime numbers (2,3,5,7,11…).',
  seed:     'Boosts numbers near your seed (±5). Enter a number in the label.',
  lucky:    'Enter your lucky numbers in the Label field (comma-separated, e.g. "7,14,22"). Those exact balls get a boost.',
  due:      'Boosts ball numbers that have NOT appeared in recent draws. Intensity controls drought sensitivity.',
  hot:      'Boosts numbers that have appeared frequently in recent draws. Higher intensity = smaller, hotter window.',
  cold:     'Boosts numbers that have appeared rarely in recent draws. Pairs well with gambler&#39;s-fallacy plays.',
  sumrange: 'Enter target sum range in Label as "min-max" (e.g. "140-210"). Historical MM mean ≈ 175.',
  noconsec: 'Penalizes ball numbers whose immediate neighbors (±1) also score high, reducing consecutive-number combos.'
};
function updateMMCMHint() {
  const t = document.getElementById('mm-cm-type').value, el = document.getElementById('mm-cm-hint');
  if (el) el.innerHTML = MM_CM_HINTS[t] || '';
  const nameInput = document.getElementById('mm-cm-name');
  if (nameInput) {
    const placeholders = {lucky:'Lucky nums: 7,14,22,…', sumrange:'Sum range: 140-210', due:'Label (optional)', hot:'Label (optional)', cold:'Label (optional)', noconsec:'Label (optional)'};
    nameInput.placeholder = placeholders[t] || 'Label (optional)';
  }
}

function addMMCustomMetric() {
  const type = document.getElementById('mm-cm-type').value;
  const intensity = Math.min(10, Math.max(1, +document.getElementById('mm-cm-intensity').value || 5));
  const name = document.getElementById('mm-cm-name').value.trim() || type;
  mmCustomMetrics.push({ name, type, intensity });
  renderMMCustomMetrics();
  document.getElementById('mm-cm-name').value = '';
  showToast('MM Added: ' + name);
}
function renderMMCustomMetrics() {
  document.getElementById('mm-custom-list').innerHTML = mmCustomMetrics.map((m, i) => `
    <div style="display:flex;align-items:center;gap:7px;padding:6px 0;border-bottom:1px solid var(--border)">
      <span style="font-size:14px">${CM_ICONS[m.type] || '⭐'}</span>
      <span style="flex:1;font-size:12px;color:var(--text)">${m.name}</span>
      <span class="chip chip-warn" style="font-size:9px">×${m.intensity}</span>
      <button onclick="mmCustomMetrics.splice(${i},1);renderMMCustomMetrics()" style="background:none;border:none;color:var(--danger);cursor:pointer;font-size:15px;padding:0 2px" aria-label="Remove ${m.name}">×</button>
    </div>`).join('');
}

function applyMMCustomBoost(scores, maxBall = 70) {
  mmCustomMetrics.forEach(m => {
    const boost = 1 + (m.intensity / 10) * 0.5;
    const seed = parseInt(m.name) || 0;

    if (m.type === 'lucky') {
      const luckyNums = new Set(m.name.split(',').map(s=>parseInt(s.trim())).filter(n=>n>=1&&n<=maxBall));
      for (let n=1; n<=maxBall; n++) { if (luckyNums.has(n)) scores[n] *= boost; }

    } else if (m.type === 'due') {
      const threshold = (11 - m.intensity) * 8;
      const { draws } = _getRecentDrawNumbers('megamillions', threshold);
      const seenRecently = new Set();
      draws.forEach(d => d.picks.forEach(n => seenRecently.add(n)));
      for (let n=1; n<=maxBall; n++) { if (!seenRecently.has(n)) scores[n] *= boost; }

    } else if (m.type === 'hot') {
      const window = m.intensity * 5;
      const { counts } = _getRecentDrawNumbers('megamillions', window);
      const maxCount = Math.max(1, ...Object.values(counts));
      for (let n=1; n<=maxBall; n++) {
        const freq = (counts[n]||0) / maxCount;
        if (freq > 0.5) scores[n] *= (1 + freq * (m.intensity/10) * 0.5);
      }

    } else if (m.type === 'cold') {
      const window = m.intensity * 5;
      const { counts } = _getRecentDrawNumbers('megamillions', window);
      const maxCount = Math.max(1, ...Object.values(counts));
      for (let n=1; n<=maxBall; n++) {
        const freq = (counts[n]||0) / maxCount;
        if (freq < 0.3) scores[n] *= boost;
      }

    } else if (m.type === 'sumrange') {
      const parts = m.name.match(/(\d+)\s*[-–]\s*(\d+)/);
      if (parts) {
        const targetMid = (parseInt(parts[1]) + parseInt(parts[2])) / 2;
        const targetAvgBall = targetMid / 5;
        const tolerance = (parseInt(parts[2]) - parseInt(parts[1])) / 10;
        for (let n=1; n<=maxBall; n++) {
          if (Math.abs(n - targetAvgBall) <= tolerance) scores[n] *= boost;
        }
      }

    } else if (m.type === 'noconsec') {
      const vals = Object.values(scores).sort((a,b)=>b-a);
      const threshold_score = vals[Math.floor(vals.length*0.25)] || 0;
      const penalty = 1 / boost;
      for (let n=1; n<=maxBall; n++) {
        const hasConsecHighNeighbor = (scores[n-1] && scores[n-1] >= threshold_score) ||
                                      (scores[n+1] && scores[n+1] >= threshold_score);
        if (hasConsecHighNeighbor && scores[n] >= threshold_score) scores[n] *= penalty;
      }

    } else if (m.type === 'decay') {
      const halfLife = Math.max(8, m.intensity * 8);
      const recent = DD.filter(d => d.game === 'megamillions' && d.picks && d.picks.length === 5);
      const dw = {};
      recent.forEach((d, i) => {
        const w = Math.pow(0.5, i / halfLife);
        d.picks.forEach(n => { if (n>=1 && n<=maxBall) dw[n] = (dw[n]||0) + w; });
      });
      const dvals = Object.values(dw);
      if (dvals.length) {
        const mx = Math.max(...dvals), mn = Math.min(...dvals), r = (mx-mn)||1;
        for (let n=1; n<=maxBall; n++) scores[n] *= 1 + (m.intensity/10) * ((dw[n]||mn)-mn)/r;
      }

    } else if (m.type === 'unpop') {
      const lucky = new Set([7,11,13,21,3]);
      const damp = 1 - (m.intensity/10) * 0.4;
      const lift = 1 + (m.intensity/10) * 0.4;
      for (let n=1; n<=maxBall; n++) {
        if (n <= 31) scores[n] *= damp;
        if (lucky.has(n)) scores[n] *= damp;
        if (n % 10 === 0 || (n % 11 === 0 && n <= 99)) scores[n] *= damp;
        if (n >= 38) scores[n] *= lift;
      }

    } else {
      for (let n = 1; n <= maxBall; n++) {
        let match = false;
        if (m.type === 'high' && n >= 48) match = true;
        else if (m.type === 'low' && n <= 23) match = true;
        else if (m.type === 'mid' && n >= 24 && n <= 47) match = true;
        else if (m.type === 'odd' && n % 2 === 1) match = true;
        else if (m.type === 'even' && n % 2 === 0) match = true;
        else if (m.type === 'prime' && PRIMES.has(n)) match = true;
        else if (m.type === 'seed' && seed > 0 && Math.abs(n - seed) <= 5) match = true;
        if (match) scores[n] *= boost;
      }
    }
  });
}



// ═══════════════════════════════════════════════════════════════════════════════
// ANALYZER V2 — Full binned, recency-weighted, bonus-scored, IQR-validated algo
// ═══════════════════════════════════════════════════════════════════════════════

// Moon phase illumination % for a given date (approximate synodic cycle)
function moonIllumForDate(year, month, day) {
  const jd = 367*year - Math.floor(7*(year+Math.floor((month+9)/12))/4) +
             Math.floor(275*month/9) + day + 1721013.5;
  const cycle = 29.530588853;
  const knownNew = 2451550.1; // known new moon Jan 6 2000
  const phase = ((jd - knownNew) % cycle + cycle) % cycle;
  return Math.round(50 * (1 - Math.cos(2 * Math.PI * phase / cycle)));
}

// ── BINNING HELPERS ────────────────────────────────────────────────────────────
function _bTemp(t)  { return t  == null ? null : Math.floor(t  / 5) * 5; }   // 5 °F
function _bHum(h)   { return h  == null ? null : Math.floor(h  / 10) * 10; } // 10 %
function _bPres(p)  { return p  == null ? null : Math.round(p * 5) / 5; }    // 0.2 inHg
function _bWs(w)    { return w  == null ? null : Math.floor(w  / 5) * 5; }   // 5 mph
function _bMoon(i)  { return Math.floor(i / 25) * 25; }                       // 25 %
// Sky condition → common bucket. History stores METAR cloud codes (CLR, FEW,
// SCT, BKN, OVC, VV); the dropdown says "Fair"/"Partly Cloudy"; the NOAA
// forecast says "Mostly Clear" etc. Comparing raw strings never matched, so
// every comparison goes through this.
function _bCond(c) {
  if (!c) return null;
  const u = String(c).toUpperCase();
  if (/THUNDER|STORM|\bTS/.test(u))                         return 'storm';
  if (/RAIN|SHOWER|DRIZZLE|\bRA\b|\bDZ\b|\bSH/.test(u))      return 'rain';
  if (/FOG|MIST|HAZE|\bVV\b|\bFG\b|\bBR\b/.test(u))          return 'fog';
  if (/PARTLY|\bSCT\b/.test(u))                              return 'partly';
  if (/CLOUDY|OVERCAST|\bBKN\b|\bOVC\b/.test(u))              return 'cloudy';
  if (/CLEAR|SUNNY|FAIR|\bCLR\b|\bSKC\b|\bFEW\b/.test(u))     return 'clear';
  return 'other';
}
const _COND_OPTION = { clear:'Fair', partly:'Partly Cloudy', cloudy:'Mostly Cloudy', rain:'Rain', storm:'Thunderstorm', fog:'Fog' };
function _condToOption(text) { return _COND_OPTION[_bCond(text)] || 'Partly Cloudy'; }
// Moon phase for a YYYY-MM-DD draw date (the analyzer used to use "today").
function moonPhaseForISO(iso) {
  if (!iso) return moonPhase();
  const [y, m, d] = iso.split('-').map(Number);
  return moonPhase(new Date(y, m - 1, d, 23, 0, 0));
}
function _bWindDir(w) {
  if (!w) return null;
  const s = String(w).trim().toUpperCase();
  if (/VAR|CALM|^0*$/.test(s)) return 'calm';
  const TABLE = {N:0,NNE:22,NE:45,ENE:67,E:90,ESE:112,SE:135,SSE:157,
                 S:180,SSW:202,SW:225,WSW:247,W:270,WNW:292,NW:315,NNW:337};
  let deg = TABLE[s];
  if (deg === undefined) deg = parseFloat(s);
  if (isNaN(deg)) return 'other';
  if (deg < 45 || deg >= 315) return 'N';
  if (deg < 135) return 'E';
  if (deg < 225) return 'S';
  return 'W';
}
function _bComfort(t, h) {
  if (t == null || h == null) return null;
  const ci = t - 0.55 * (1 - h / 100) * (t - 58);
  return Math.floor(ci / 5) * 5;
}
function _bDewSpread(t, dew) {
  if (t == null || dew == null) return null;
  return Math.floor((t - dew) / 5) * 5;
}

// ── CORE V2 ENGINE ─────────────────────────────────────────────────────────────
function analyzerV2(game) {
  const isPB      = game === 'powerball';
  const maxBall   = isPB ? 69 : 70;
  const maxSp     = isPB ? 26 : 24; // Mega Ball has been 1–24 since 2025-04-08
  const activeM   = isPB ? activeMetrics  : mmActiveMetrics;
  const wts       = isPB ? weights        : mmWeights;
  const customFn  = isPB ? (s,m)=>applyCustomBoost(s,m) : (s,m)=>applyMMCustomBoost(s,m);

  // Filter to the current white-ball matrix (PB 69 since 2015-10-07, MM 70 since 2017-10-31)
  const ERA_ISO = isPB ? '2015-10-07' : '2017-10-31';
  let draws = DD.filter(d => d.game === game && d.picks && d.picks.length === 5 && d.iso >= ERA_ISO);
  // Apply user-selected date range filter
  const _dFrom = isPB ? pbDateFrom : mmDateFrom;
  const _dTo   = isPB ? pbDateTo   : mmDateTo;
  if (_dFrom || _dTo) {
    draws = draws.filter(d => inRange(d.iso, _dFrom, _dTo));
  }
  // draws[0] = most recent (loadDraws sorts newest first)
  const n = draws.length;

  // Read UI inputs
  const pfx = isPB ? 'ana' : 'mm-ana';
  // A blank box means "unknown" and that factor is skipped (no silent defaults).
  const g   = id => { const v = parseFloat(document.getElementById(id)?.value); return Number.isFinite(v) ? v : null; };
  const curTemp = g(`${pfx}-temp`);
  const curHum  = g(`${pfx}-hum`);
  const curPres = g(`${pfx}-pres`);
  const curWs   = g(`${pfx}-ws`);
  const curWind = document.getElementById(`${pfx}-wdir`)?.value || '';
  const curCond = document.getElementById(`${pfx}-cond`)?.value || '';
  const curCondB = _bCond(curCond);
  const curDew  = g(`${pfx}-dew`);
  // Moon, month and weekday come from the upcoming DRAW date, not today.
  const drawISO = getNextDrawDateISO(isPB ? 'pb' : 'mm');
  const mp = moonPhaseForISO(drawISO);
  const [_dy, _dm, _dd] = drawISO.split('-').map(Number);
  const curMonth = _dm;
  const curDOW   = new Date(_dy, _dm - 1, _dd).toLocaleDateString('en-US', { weekday: 'long' });
  // Derived dimensions (notebook v2)
  const curComfort  = (curTemp != null && curHum != null) ? curTemp - 0.55 * (1 - curHum / 100) * (curTemp - 58) : null;
  const curDewSpread = (curTemp != null && curDew != null) ? curTemp - curDew : null;

  // Pre-compute bins for every draw (one pass)
  const precomp = draws.map((d, idx) => ({
    d, idx,
    bTemp:    _bTemp(d.temp),
    bHum:     _bHum(d.hum),
    bPres:    _bPres(d.pres),
    bWs:      _bWs(d.ws),
    bWind:    _bWindDir(d.wind),
    bCond:    _bCond(d.cond),
    bComfort: _bComfort(d.temp, d.hum),
    bDewSpr:  _bDewSpread(d.temp, d.dew),
    bMoon:    _bMoon(moonIllumForDate(d.year, d.month, d.day)),
    month:    d.month,
    dow:      new Date(d.year, d.month - 1, d.day).toLocaleDateString('en-US', { weekday: 'long' }),
    comfort:  (d.temp != null && d.hum != null) ? d.temp - 0.55 * (1 - d.hum / 100) * (d.temp - 58) : null,
    dewSpread:(d.temp != null && d.dew != null) ? d.temp - d.dew : null,
    recency:  Math.exp(-0.001 * idx)
  }));

  // ══════════════════════════════════════════════════════════════════════════
  // SCORING ENGINE V4 — Distance-weighted continuous scoring
  //
  //   Instead of binning draws into coarse buckets (which produces identical
  //   number frequencies because lottery is uniform), V4 weights EVERY draw
  //   by its continuous distance to the current weather inputs.
  //
  //   A draw at exactly 81°F gets max weight when temp=81. A draw at 72°F
  //   gets less. A draw at 55°F gets almost zero. This means changing temp
  //   by even 1° shifts which draws contribute most → different numbers.
  //
  //   Each weather dimension is a Gaussian kernel: w = exp(-(delta/sigma)²)
  //   Combined weight = product of all active dimension weights × recency.
  // ══════════════════════════════════════════════════════════════════════════

  const scores = {};
  for (let i = 1; i <= maxBall; i++) scores[i] = 0;

  const wSum = Math.max(1, Object.values(wts).reduce((s, v) => s + v, 0));
  const SCORE_SCALE = 100;

  // Sigma (spread) for each weather dimension — controls how quickly
  // draws lose influence as they move away from the target value.
  // Smaller sigma = more weather-sensitive (numbers change with smaller input changes)
  // Wider sigmas for additive kernels — each dim contributes independently
  // so we can be more generous with how far draws influence
  const SIGMA = { temp: 6, hum: 10, pres: 0.25, wind: 6 };

  // ── STEP 1: Compute per-draw similarity weight ────────────────────────
  // ADDITIVE kernels: each weather dimension contributes independently.
  //
  // Why additive, not multiplicative?
  // Multiplicative (v4a) means ALL dimensions must match — if the user enters
  // an extreme value in ANY dimension (e.g. Wind=50 mph, rare in data), the
  // product → 0 for every draw, making all other dimensions irrelevant.
  //
  // Additive means: temp similarity contributes its share, humidity contributes
  // its share, etc. Changing temp from 72→91 shifts the temp contribution
  // even if wind kernel is zero for most draws.
  //
  // wxWeight = Σ (dimSliderWeight/wSum * Gaussian_i)  for active weather dims
  //          + small baseline so draws with no weather data still contribute via recency

  const drawWeights = precomp.map(p => {
    const d = p.d;
    let wxWeight = 0;
    let activeDimSum = 0; // sum of active weather dim slider weights

    // Temperature kernel
    if (activeM.has('temp') && d.temp != null && curTemp != null) {
      const delta = (d.temp - curTemp) / SIGMA.temp;
      const k = Math.exp(-delta * delta);
      const dimW = (wts.temp || 0) / wSum;
      wxWeight += dimW * k;
      activeDimSum += dimW;
    }

    // Humidity kernel
    if (activeM.has('hum') && d.hum != null && curHum != null) {
      const delta = (d.hum - curHum) / SIGMA.hum;
      const k = Math.exp(-delta * delta);
      const dimW = (wts.hum || 0) / wSum;
      wxWeight += dimW * k;
      activeDimSum += dimW;
    }

    // Pressure kernel
    if (activeM.has('pres') && d.pres != null && curPres != null) {
      const delta = (d.pres - curPres) / SIGMA.pres;
      const k = Math.exp(-delta * delta);
      const dimW = (wts.pres || 0) / wSum;
      wxWeight += dimW * k;
      activeDimSum += dimW;
    }

    // Wind speed kernel
    if (activeM.has('wind') && d.ws != null && curWs != null) {
      const delta = (d.ws - curWs) / SIGMA.wind;
      const k = Math.exp(-delta * delta);
      const dimW = (wts.wind || 0) / wSum;
      wxWeight += dimW * k;
      activeDimSum += dimW;
    }

    // Wind direction kernel (circular)
    if (activeM.has('wdir') && curWind) {
      const drawDeg = windDirToDeg(d.wind);
      const curDeg = windDirToDeg(curWind);
      if (drawDeg !== null && curDeg !== null) {
        let diff = Math.abs(drawDeg - curDeg);
        if (diff > 180) diff = 360 - diff;
        const delta = diff / 30;
        const k = Math.exp(-delta * delta);
        const dimW = (wts.wdir || 0) / wSum;
        wxWeight += dimW * k;
        activeDimSum += dimW;
      }
    }

    // Condition: Gaussian-like (1.0 for match, 0.3 for mismatch)
    if (activeM.has('cond') && curCondB && d.cond) {
      const k = (_bCond(d.cond) === curCondB) ? 1.0 : 0.3;
      const dimW = (wts.cond || 0) / wSum;
      wxWeight += dimW * k;
      activeDimSum += dimW;
    }

    // Moon: illumination distance
    if (activeM.has('moon')) {
      const drawIllum = moonIllumForDate(d.year, d.month, d.day);
      if (drawIllum != null) {
        const delta = (drawIllum - mp.illum) / 15;
        const k = Math.exp(-delta * delta);
        const dimW = (wts.moon || 0) / wSum;
        wxWeight += dimW * k;
        activeDimSum += dimW;
      }
    }

    // ── AUTO-DIMENSIONS (always on, notebook v2 weights) ──────────────
    // These don't have sliders — they use fixed weights from the methodology doc.
    // Total auto-dim budget is kept small (~0.15) so sliders still dominate.

    // Dew Point kernel (1.0× methodology weight → 0.02 budget)
    if (d.dew != null && curDew != null) {
      const delta = (d.dew - curDew) / 6; // sigma=6°F like temp
      wxWeight += 0.02 * Math.exp(-delta * delta);
    }

    // Dew Point Spread / dryness kernel (1.1× → 0.022 budget)
    if (p.dewSpread != null && curDewSpread != null) {
      const delta = (p.dewSpread - curDewSpread) / 5; // sigma=5°F
      wxWeight += 0.022 * Math.exp(-delta * delta);
    }

    // Comfort Index kernel (1.2× → 0.024 budget)
    if (p.comfort != null && curComfort != null) {
      const delta = (p.comfort - curComfort) / 6; // sigma=6°F
      wxWeight += 0.024 * Math.exp(-delta * delta);
    }

    // Condition × Temperature combo (1.5× → 0.03 budget — strongest auto-dim)
    if (d.cond && curCondB && d.temp != null && curTemp != null) {
      const condMatch = (_bCond(d.cond) === curCondB) ? 1.0 : 0.0;
      const tempDelta = (d.temp - curTemp) / 6;
      const tempK = Math.exp(-tempDelta * tempDelta);
      wxWeight += 0.03 * condMatch * tempK;
    }

    // Month kernel (0.7× → 0.014 budget, circular distance)
    if (p.month) {
      let monthDiff = Math.abs(p.month - curMonth);
      if (monthDiff > 6) monthDiff = 12 - monthDiff;
      const k = Math.exp(-(monthDiff / 1.5) * (monthDiff / 1.5)); // sigma ~1.5 months
      wxWeight += 0.014 * k;
    }

    // Day of Week match (0.7× → 0.014 budget, binary match)
    if (p.dow && curDOW) {
      wxWeight += (p.dow === curDOW) ? 0.014 : 0.0;
    }

    // Normalize: if active dims cover 60% of wSum, scale wxWeight to [0, 0.6]
    // Add a small baseline (0.05) so draws with no weather data still contribute
    // through recency and bonus metrics
    if (activeDimSum > 0) {
      wxWeight = 0.01 + wxWeight; // low baseline so weather signal dominates
    } else {
      wxWeight = 0.01; // no weather dims active → equal base weight
    }

    // Combine with recency decay
    return { d, picks: d.picks, weight: wxWeight * p.recency };
  });

  // ── STEP 2: Weather-based number scoring ──────────────────────────────
  // Weight each number by how often it appears in high-similarity draws.
  // This IS the weather signal — closer weather = more influence.
  const weatherShare = (['temp','hum','pres','wind','wdir','cond','moon']
    .reduce((s, k) => s + (activeM.has(k) ? (wts[k]||0) : 0), 0)) / wSum;

  {
    const freq = {};
    for (let i = 1; i <= maxBall; i++) freq[i] = 0;
    let totalW = 0;
    drawWeights.forEach(({picks, weight}) => {
      picks.forEach(num => {
        if (num >= 1 && num <= maxBall) freq[num] += weight;
      });
      totalW += weight;
    });
    // Normalize so weather dims contribute weatherShare * SCORE_SCALE
    if (totalW > 0) {
      const scale = weatherShare * SCORE_SCALE / totalW;
      for (let i = 1; i <= maxBall; i++) {
        scores[i] += freq[i] * scale;
      }
    }
  }

  // ── STEP 3: BONUS SCORES (normalized same way) ────────────────────────

  // Cold / Overdue — gap length per number
  if (activeM.has('cold') && wts.cold > 0) {
    const coldWeight = (wts.cold || 0) / wSum;
    let maxGap = 0;
    const gaps = {};
    for (let num = 1; num <= maxBall; num++) {
      const lastIdx = draws.findIndex(d => d.picks.includes(num));
      gaps[num] = lastIdx === -1 ? n : lastIdx;
      if (gaps[num] > maxGap) maxGap = gaps[num];
    }
    if (maxGap > 0) {
      const gapTotal = Object.values(gaps).reduce((s, v) => s + v, 0) || 1;
      const scale = coldWeight * SCORE_SCALE / gapTotal;
      for (let num = 1; num <= maxBall; num++) {
        scores[num] += gaps[num] * scale;
      }
    }
  }

  // Hot-streak — recent frequency
  if (activeM.has('hot') && wts.hot > 0) {
    const hotWeight = (wts.hot || 0) / wSum;
    const recent20 = draws.slice(0, 20);
    const hotCounts = {};
    for (let num = 1; num <= maxBall; num++) {
      hotCounts[num] = recent20.filter(d => d.picks.includes(num)).length;
    }
    const hotTotal = Object.values(hotCounts).reduce((s, v) => s + v, 0) || 1;
    const scale = hotWeight * SCORE_SCALE / hotTotal;
    for (let num = 1; num <= maxBall; num++) {
      scores[num] += hotCounts[num] * scale;
    }
  }

  // Pair co-occurrence
  const pairPool = draws.slice(0, Math.min(300, n));
  const pairCnt = {};
  pairPool.forEach(d => {
    const p = d.picks.filter(x => x >= 1 && x <= maxBall);
    for (let i = 0; i < p.length; i++)
      for (let j = i + 1; j < p.length; j++) {
        const key = p[i] < p[j] ? `${p[i]}-${p[j]}` : `${p[j]}-${p[i]}`;
        pairCnt[key] = (pairCnt[key] || 0) + 1;
      }
  });
  const pps = pairPool.length || 1;
  if (activeM.has('pairs') && wts.pairs > 0) {
    const pairWeight = (wts.pairs || 0) / wSum;
    const pairDensity = {};
    Object.entries(pairCnt).sort((a, b) => b[1] - a[1]).slice(0, 30).forEach(([key, cnt]) => {
      const [a, b] = key.split('-').map(Number);
      pairDensity[a] = (pairDensity[a] || 0) + cnt;
      pairDensity[b] = (pairDensity[b] || 0) + cnt;
    });
    const pdTotal = Object.values(pairDensity).reduce((s, v) => s + v, 0) || 1;
    const scale = pairWeight * SCORE_SCALE / pdTotal;
    for (let num = 1; num <= maxBall; num++) {
      scores[num] += (pairDensity[num] || 0) * scale;
    }
  }

  // Triplet co-occurrence
  if (activeM.has('trips') && wts.trips > 0) {
    const tripWeight = (wts.trips || 0) / wSum;
    const tripCnt = {};
    pairPool.forEach(d => {
      const p = d.picks.filter(x => x >= 1 && x <= maxBall).sort((a, b) => a - b);
      for (let i = 0; i < p.length - 2; i++)
        for (let j = i + 1; j < p.length - 1; j++)
          for (let k = j + 1; k < p.length; k++) {
            const key = p[i] + '-' + p[j] + '-' + p[k];
            tripCnt[key] = (tripCnt[key] || 0) + 1;
          }
    });
    const tripDensity = {};
    Object.entries(tripCnt).sort((a, b) => b[1] - a[1]).slice(0, 15).forEach(([key, cnt]) => {
      key.split('-').map(Number).forEach(num => {
        tripDensity[num] = (tripDensity[num] || 0) + cnt;
      });
    });
    const tdTotal = Object.values(tripDensity).reduce((s, v) => s + v, 0) || 1;
    const scale = tripWeight * SCORE_SCALE / tdTotal;
    for (let num = 1; num <= maxBall; num++) {
      scores[num] += (tripDensity[num] || 0) * scale;
    }
  }

  // Apply user custom-bias boosts
  customFn(scores, maxBall);

  // ══════════════════════════════════════════════════════════════════════════
  // STEP 5: SEEDED PROBABILITY SAMPLING
  //
  //   Old approach: top-25 deterministic combo enumeration.
  //   Problem: bonus dimensions (hot/cold/pairs) anchor the same numbers at
  //   the top regardless of weather inputs.
  //
  //   New approach: convert scores → probabilities via softmax, then SAMPLE
  //   5 numbers using a PRNG seeded by the weather inputs. Same weather →
  //   same picks (deterministic). Different weather → different seed →
  //   completely different picks. Higher-scoring numbers are more LIKELY
  //   to be picked but don't ALWAYS dominate.
  //
  //   Structural quality (sum range, odd/even, spread) is enforced via
  //   rejection sampling: generate candidate combos until one passes.
  // ══════════════════════════════════════════════════════════════════════════

  // ── 5a. Score capping — mild, just to prevent extreme outliers ──────────
  const scoreArr = Object.values(scores).sort((a,b) => a-b);
  const medianScore = scoreArr[Math.floor(scoreArr.length / 2)] || 1;
  const scoreCap = medianScore * 3;
  for (let i = 1; i <= maxBall; i++) {
    if (scores[i] > scoreCap) scores[i] = scoreCap;
  }

  // ── 5b. Convert scores to softmax probabilities ─────────────────────────
  const maxScoreVal = Math.max(...Object.values(scores));
  const tau = medianScore * 0.4 || 0.5; // softmax temperature — lower = more peaked
  let sumExp = 0;
  const probs = {};
  for (let i = 1; i <= maxBall; i++) {
    probs[i] = Math.exp((scores[i] - maxScoreVal) / tau);
    sumExp += probs[i];
  }
  for (let i = 1; i <= maxBall; i++) probs[i] /= sumExp;

  // Debug info
  const sorted25 = Object.entries(probs).sort((a,b)=>b[1]-a[1]).slice(0,10).map(([k,v])=>`${k}:${(v*100).toFixed(1)}%`);
  console.log('[V5 DEBUG] Weather:', { curTemp, curHum, curPres, curWs, curDew, curComfort, curDewSpread, curDOW, curMonth, drawISO });
  console.log('[V5 DEBUG] Softmax top-10 probs:', sorted25.join(', '));

  // ── 5c. Seeded PRNG from weather inputs ──────────────────────────────────
  // mulberry32 — fast 32-bit seeded PRNG
  function _mulberry32(seed) {
    return function() {
      seed |= 0; seed = seed + 0x6D2B79F5 | 0;
      let t = Math.imul(seed ^ seed >>> 15, 1 | seed);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  // Hash all weather inputs AND slider weights into a single seed.
  // Including weights means any slider change re-seeds the PRNG → different picks.
  let _seed = (Math.round((curTemp||0) * 1000) ^ Math.round((curHum||0) * 777)
    ^ Math.round((curPres||0) * 9999) ^ Math.round((curWs||0) * 555)
    ^ Math.round((curDew||0) * 333)
    ^ (curWind ? curWind.charCodeAt(0) * 13 : 0)
    ^ (curCond ? curCond.charCodeAt(0) * 17 : 0)) >>> 0;
  // Mix in every active metric's slider weight
  ALL_METRICS.forEach((m, i) => {
    if (activeM.has(m)) {
      _seed = (_seed ^ ((wts[m] || 0) * (i + 7) * 31)) >>> 0;
    }
  });
  _seed = _seed >>> 0;
  const _rng = _mulberry32(_seed);

  // ── 5d. Historical distribution stats (for quality filters) ──────────────
  let sumPool = draws.filter(d => d.sum).map(d => d.sum);
  if (sumPool.length < 20) sumPool = draws.map(d => d.picks ? d.picks.reduce((s,n)=>s+n,0) : 0).filter(s => s > 0);
  const ss = [...sumPool].sort((a,b)=>a-b);
  const q1     = ss[Math.floor(ss.length * 0.10)] ?? 80;
  const q3     = ss[Math.floor(ss.length * 0.90)] ?? 240;
  const meanSum = sumPool.length > 0 ? sumPool.reduce((s,v)=>s+v,0) / sumPool.length : 160;
  const stdSum  = sumPool.length > 1 ? Math.sqrt(sumPool.reduce((s,v)=>s+(v-meanSum)**2,0)/(sumPool.length-1)) : 30;

  const half  = Math.floor(maxBall / 2);
  const third = Math.floor(maxBall / 3);

  // ── 5e. Weighted sampling with rejection ─────────────────────────────────
  // Sample 5 numbers from the probability distribution. If the combo fails
  // structural quality checks, resample. Max 200 attempts then take best.
  function _sampleCombo(rng, probsCopy) {
    const picked = [];
    const avail = Object.assign({}, probsCopy);
    for (let p = 0; p < 5; p++) {
      const r = rng();
      let cumul = 0;
      const entries = Object.entries(avail);
      let chosen = +entries[entries.length - 1][0]; // float round-off fallback: last still-available number
      const totalP = Object.values(avail).reduce((s,v) => s+v, 0);
      for (const [num, prob] of entries) {
        cumul += prob / totalP;
        if (r <= cumul) { chosen = +num; break; }
      }
      picked.push(chosen);
      delete avail[chosen];
    }
    return picked.sort((a,b) => a-b);
  }

  function _qualityScore(combo) {
    const cSum = combo.reduce((s,n) => s+n, 0);
    let q = 0;
    // Sum in range
    if (cSum >= q1 && cSum <= q3) q += 2;
    // Sum proximity bonus (if sumprox active)
    if (activeM.has('sumprox') && (wts.sumprox || 0) > 0) {
      const dev = (cSum - meanSum) / (stdSum || 30);
      q += ((wts.sumprox || 0) / 25) * Math.exp(-0.5 * dev * dev);
    }
    // Odd/even balance
    const odds = combo.filter(x => x%2===1).length;
    if (odds >= 2 && odds <= 3) q += 1;
    // Range coverage
    const hasLow  = combo.some(x => x <= third);
    const hasMid  = combo.some(x => x > third && x <= third*2);
    const hasHigh = combo.some(x => x > third*2);
    if (hasLow && hasMid && hasHigh) q += 1.5;
    // Spread
    const spread = combo[4] - combo[0];
    if (spread >= 25) q += 1;
    if (spread < 15) q -= 2;
    // Consecutive penalty
    let consec = 0;
    for (let i = 0; i < combo.length - 1; i++) {
      if (combo[i+1] - combo[i] === 1) consec++;
    }
    if (consec >= 2) q -= 1.5;
    return q;
  }

  let bestCombo = null, bestQuality = -Infinity;
  for (let attempt = 0; attempt < 200; attempt++) {
    const candidate = _sampleCombo(_rng, probs);
    const cSum = candidate.reduce((s,n) => s+n, 0);
    // Hard reject: sum way out of range
    if (cSum < q1 * 0.8 || cSum > q3 * 1.2) continue;
    const q = _qualityScore(candidate);
    if (q > bestQuality) { bestQuality = q; bestCombo = candidate; }
    if (q >= 4.5) break; // good enough — stop early
  }

  // Fallback: if all 200 attempts failed quality, just take the last one
  if (!bestCombo) {
    bestCombo = _sampleCombo(_rng, probs);
    bestCombo.sort((a,b) => a-b);
  }
  console.log('[V5 DEBUG] Best combo:', bestCombo.join(','), 'quality:', bestQuality.toFixed(2), 'seed:', _seed);

  // ── 5e. SPECIAL BALL (distance-weighted like main picks) ─────────────────
  const spSc = {};
  for (let i=1; i<=maxSp; i++) spSc[i] = 0;
  drawWeights.forEach(({d, weight}) => {
    if (!isPB && d.iso < '2025-04-08') return; // old 1–25 Mega Ball matrix
    if (d.pb >= 1 && d.pb <= maxSp) spSc[d.pb] += weight;
  });
  const spOrder = Object.entries(spSc).sort((a,b)=>b[1]-a[1]).map(e => +e[0]);
  const topSp = pickSpecialAvoiding(spOrder, bestCombo, maxSp).special;

  // ── DATA COVERAGE ─────────────────────────────────────────────────────────
  // Share of draws that have draw-night weather. This used to be shown as a
  // 0–97 "confidence"/"Strong" score, but it measures data completeness only;
  // it says nothing about the chance of winning (walk-forward tests: chance level).
  const wxCount   = draws.filter(d => d.temp != null).length;
  const binCount  = sumPool.length;
  const confScore = Math.round(100 * wxCount / Math.max(1, n));

  // Which draws actually influenced the result — top 5 by distance weight
  const usedDraws = drawWeights
    .filter(dw => dw.weight > 0.001)
    .sort((a,b) => b.weight - a.weight)
    .slice(0, 5)
    .map(dw => dw.d);

  // Debug info for on-page diagnostics
  const _dbgTop5 = Object.entries(probs).sort((a,b)=>b[1]-a[1]).slice(0,5).map(([k,v])=>`${k}:${(v*100).toFixed(1)}%`).join(' ');
  const _dbgBot3 = Object.entries(probs).sort((a,b)=>a[1]-b[1]).slice(0,3).map(([k,v])=>`${k}:${(v*100).toFixed(2)}%`).join(' ');
  return { picks: bestCombo, special: topSp, confScore, n, wxCount, usedDraws, binCount, _dbgTop5, _dbgBot3 };
}
// ── MM WRAPPER ────────────────────────────────────────────────────────────────



function renderMMFreqBars(data) {
  const el = document.getElementById('mm-nat-hot'); if (!el) return;
  if (!data || data.length === 0) { el.innerHTML = '<div style="color:var(--text3);font-size:12px">No MM data yet — run draw-feed</div>'; return; }
  const freq = {}; for (let i = 1; i <= 70; i++) freq[i] = 0;
  data.forEach(d => d.picks.forEach(n => { if (n >= 1 && n <= 70) freq[n]++; }));
  const sorted = Object.entries(freq).map(([n, c]) => [+n, c]).sort((a, b) => b[1] - a[1]).slice(0, 20);
  const maxF = Math.max(...sorted.map(([, c]) => c));
  el.innerHTML = sorted.map(([n, c], i) => `<div class="fbar-row"><div class="fbar-num">${n}</div><div class="fbar-track"><div class="fbar-fill" style="width:${Math.round(c/maxF*100)}%;background:linear-gradient(90deg,${i<5?'#1e5ac8':'#6ba3ff'},${i<5?'#6ba3ff':'#1e5ac8'})">${c}</div></div><div class="fbar-ct">${c}</div></div>`).join('');
}

function updateMMCountdown() {
  const now = new Date(), day = now.getDay();
  const hrs = 23 - now.getHours(), mins = 59 - now.getMinutes();
  const mmDays = [2, 5]; // Tue, Fri
  const el = document.getElementById('mm-countdown'); if (!el) return;
  function daysUntil(days) { return Math.min(...days.map(d => (d - day + 7) % 7 || 7)); }
  if (mmDays.includes(day)) {
    el.textContent = (hrs >= 0 && hrs <= 3) ? `🎰 MM Draw in ${hrs}h ${mins}m!` : '🎰 Mega Millions draw tonight 11:00 PM ET';
  } else {
    const nd = daysUntil(mmDays);
    el.textContent = `Mega Millions in ${nd} day(s)`;
  }
}

function updateCountdown(){
  const now=new Date(), day=now.getDay();
  const hrs=22-now.getHours(), mins=59-now.getMinutes();
  const pbDays=[1,3,6], mmDays=[2,5], noDrawDays=[0,4];
  const el=document.getElementById('ana-countdown'); if(!el) return;
  function daysUntil(days){ return Math.min(...days.map(d=>(d-day+7)%7||7)); }
  if(noDrawDays.includes(day)){
    const np=daysUntil(pbDays), nm=daysUntil(mmDays);
    const ng=np<=nm?'Powerball':'Mega Millions', nd=Math.min(np,nm);
    el.textContent=`No draws today — ${ng} in ${nd} day(s)`;
  } else if(pbDays.includes(day)){
    el.textContent=(hrs>=0&&hrs<=3)?`🎰 PB Draw in ${hrs}h ${mins}m!`:'🎰 Powerball draw tonight 10:59 PM ET';
  } else if(mmDays.includes(day)){
    el.textContent=(hrs>=0&&hrs<=3)?`🎰 MM Draw in ${hrs}h ${mins}m!`:'🎰 Mega Millions draw tonight 11:00 PM ET';
  } else {
    const np=daysUntil(pbDays), nm=daysUntil(mmDays);
    el.textContent=np<=nm?`Powerball in ${np} day(s)`:`Mega Millions in ${nm} day(s)`;
  }
}

// ── ANALYZER: Next Draw Date + Countdown ────────────────────────
function updateAnalyzerDrawDate() {
  const now = new Date();
  const pbDays = [1, 3, 6]; // Mon, Wed, Sat
  let next = new Date(now);
  // Find the next PB draw day
  for (let i = 0; i <= 7; i++) {
    const check = new Date(now.getTime() + i * 86400000);
    const dow = check.getDay();
    if (pbDays.includes(dow)) {
      // If it's today, check if the draw already happened (after 11 PM ET)
      if (i === 0) {
        const etHour = now.getUTCHours() - 4; // rough ET offset
        if (etHour >= 23) continue; // draw already happened
      }
      next = check;
      break;
    }
  }
  const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const dateStr = `${dayNames[next.getDay()]}, ${monthNames[next.getMonth()]} ${next.getDate()}, ${next.getFullYear()}`;
  const el = document.getElementById('ana-next-draw-date');
  if (el) el.textContent = dateStr;
  // Countdown
  const drawTime = new Date(next);
  drawTime.setHours(23, 0, 0, 0); // ~11 PM local
  const diff = drawTime - now;
  const cdEl = document.getElementById('ana-next-draw-countdown');
  if (cdEl) {
    if (diff <= 0) {
      cdEl.textContent = '🎰 Draw in progress!';
      cdEl.style.color = 'var(--ok)';
    } else {
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      cdEl.textContent = d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
    }
  }
}

// ── TRENDS ────────────────────────────────────────────────
let freqChartInst=null;
// ── GAME HELPERS ─────────────────────────────────────────
let trendGame = 'pb';
function getGameDD(game) {
  const g = game || trendGame;
  if (g === 'pb') return DD.filter(d => d.game === 'powerball');
  if (g === 'mm') return DD.filter(d => d.game === 'megamillions');
  return DD;
}
function setTrendGame(game) {
  trendGame = game;
  const sel = document.getElementById('trend-game-sel');
  if (sel) sel.value = game;
  const hd = document.getElementById('trend-game-hd');
  const sub = document.getElementById('trend-game-sub');
  if (game === 'pb') {
    if (hd) hd.textContent = '🔴 Powerball® Trends';
    if (sub) sub.textContent = 'White balls 1–69 · Powerball 1–26 · Draws since Oct 2015';
  } else if (game === 'mm') {
    if (hd) hd.textContent = '🔵 Mega Millions® Trends';
    if (sub) sub.textContent = 'White balls 1–70 · Mega Ball 1–25 · Draws since Oct 2017';
  }
  try { updateTrendRange(); } catch(_) {}
}
function setPoolGame(game) {
  const sel = document.getElementById('gp-game');
  if (sel) { sel.value = game === 'pb' ? 'pb' : 'mm'; if(typeof gpUpdate==='function') gpUpdate(); }
}
// ── PAST RESULTS ─────────────────────────────────────────
let _resultsGame = 'pb', _resultsOffset = 0, _resultsPageSize = 10;

function setResultsGame(game) {
  _resultsGame = game;
  _resultsOffset = 0;
  ['pb','mm'].forEach(g => {
    const b = document.getElementById('res-tab-'+g);
    if (b) { b.classList.toggle('btn-primary', g===game); b.classList.toggle('btn-outline', g!==game); }
  });
  const s = document.getElementById('res-summary');
  if (s) s.innerHTML = '';
  loadResults();
}

async function loadResults() {
  const game  = _resultsGame === 'pb' ? 'powerball' : 'megamillions';
  const limit = parseInt(document.getElementById('res-limit')?.value || 10);
  _resultsPageSize = limit || 10;
  _resultsOffset   = 0;
  const from  = document.getElementById('res-from')?.value;
  const to    = document.getElementById('res-to')?.value;
  const tbody = document.getElementById('res-tbody');
  const status= document.getElementById('res-status');
  // Rows already on screen (the prerendered latest draws, or the previous
  // query) stay until the new ones arrive, so the page doesn't jump.
  if (tbody) {
    if (tbody.querySelector('td[colspan]') || !tbody.rows.length) tbody.innerHTML = `<tr><td colspan="6" style="text-align:center;padding:24px;color:var(--text3)">⏳ Loading…</td></tr>`;
    else tbody.style.opacity = '.55';
  }

  try {
    let q = _supa.from('draw_history')
      .select(`draw_date,game,pick1,pick2,pick3,pick4,pick5,special_ball,jackpot_amount,had_winner`)
      .eq('game', game)
      .order('draw_date', { ascending: false });
    if (from) q = q.gte('draw_date', from);
    if (to)   q = q.lte('draw_date', to);
    if (limit > 0) q = q.limit(limit);

    // Also pull draw_news for cash_value + winner_state
    let newsMap = {};
    try {
      let nq = _supa.from('draw_news')
        .select('draw_date,game,cash_value,had_winner,winner_state,winner_count,headline')
        .eq('game', game)
        .order('draw_date', { ascending: false });
      if (from) nq = nq.gte('draw_date', from);
      if (to)   nq = nq.lte('draw_date', to);
      if (limit > 0) nq = nq.limit(limit);
      const { data: nd } = await nq;
      if (nd) nd.forEach(r => { newsMap[r.draw_date] = r; });
    } catch(_) {}

    const { data, error } = await q;
    if (error) throw error;
    if (tbody) tbody.style.opacity = '';
    _renderResults(data || [], newsMap, limit);
  } catch(e) {
    if (tbody) tbody.style.opacity = '';
    if (tbody) tbody.innerHTML = `<tr><td colspan="6" style="text-align:center;padding:24px;color:var(--danger)">❌ ${e.message || 'DB error — make sure draw_history is populated.'}</td></tr>`;
  }
}

async function loadResultsMore() {
  _resultsOffset += _resultsPageSize;
  const game  = _resultsGame === 'pb' ? 'powerball' : 'megamillions';
  const from  = document.getElementById('res-from')?.value;
  const to    = document.getElementById('res-to')?.value;
  try {
    let q = _supa.from('draw_history')
      .select('draw_date,game,pick1,pick2,pick3,pick4,pick5,special_ball,jackpot_amount,had_winner')
      .eq('game', game).order('draw_date',{ascending:false})
      .range(_resultsOffset, _resultsOffset + _resultsPageSize - 1);
    if (from) q = q.gte('draw_date', from);
    if (to)   q = q.lte('draw_date', to);
    const { data } = await q;
    if (data?.length) _appendResults(data, {});
    const lm = document.getElementById('res-load-more');
    if (lm && data?.length < _resultsPageSize) lm.style.display = 'none';
  } catch(_) {}
}

function clearResultsFilter() {
  const f = document.getElementById('res-from'), t = document.getElementById('res-to');
  if (f) f.value = ''; if (t) t.value = '';
  document.getElementById('res-limit').value = '10';
  loadResults();
}

function _renderResults(rows, newsMap, limit) {
  const tbody  = document.getElementById('res-tbody');
  const status = document.getElementById('res-status');
  const lm     = document.getElementById('res-load-more');
  const sumEl  = document.getElementById('res-summary');
  if (!rows.length) {
    if (tbody) tbody.innerHTML = `<tr><td colspan="6" style="text-align:center;padding:24px;color:var(--text3)">No draws found. Run EF3 draw-feed to populate draw_history.</td></tr>`;
    return;
  }
  const game = _resultsGame;
  const spLabel = game === 'pb' ? 'PB' : 'MB';
  const spColor = game === 'pb' ? '#dc3232' : '#1e5ac8';
  const officialBase = game === 'pb'
    ? 'https://www.powerball.com/draw-result?gc=powerball&date='
    : 'https://www.megamillions.com/Winning-Numbers';

  // Summary chips
  const winners = rows.filter(r => (newsMap[r.draw_date]?.had_winner) || r.had_winner).length;
  if (sumEl) sumEl.innerHTML =
    `<span style="padding:4px 10px;border-radius:12px;background:rgba(0,180,216,.1);border:1px solid rgba(0,180,216,.25);font-size:11px;color:var(--accent);font-weight:600">${rows.length} draws shown</span>` +
    (winners ? `<span style="padding:4px 10px;border-radius:12px;background:rgba(255,209,102,.1);border:1px solid rgba(255,209,102,.3);font-size:11px;color:var(--warn);font-weight:600">🏆 ${winners} jackpot winner${winners>1?'s':''}</span>` : '') +
    `<span style="padding:4px 10px;border-radius:12px;background:rgba(255,255,255,.04);border:1px solid var(--border);font-size:11px;color:var(--text2)">${rows[rows.length-1]?.draw_date||''} → ${rows[0]?.draw_date||''}</span>`;

  const html = rows.map(r => {
    const news = newsMap[r.draw_date] || {};
    const nums = [r.pick1,r.pick2,r.pick3,r.pick4,r.pick5].filter(Boolean);
    const sp   = r.special_ball;
    const jp   = r.jackpot_amount || news.jackpot_amount;
    const cash = news.cash_value;
    const won  = news.had_winner || r.had_winner;
    const ws   = news.winner_state ? ` (${news.winner_state})` : '';
    const dt   = r.draw_date ? new Date(r.draw_date+'T12:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'}) : r.draw_date;
    const fmtM = n => !n ? '—' : n>=1e9?'$'+(n/1e9).toFixed(2)+'B':'$'+(n/1e6).toFixed(1)+'M';
    const ballHtml = nums.map(n=>`<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:var(--bg3);border:1.5px solid var(--accent);font-size:11px;font-weight:700;color:var(--accent);margin:1px">${n}</span>`).join('') +
      (sp ? `<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:${spColor};font-size:11px;font-weight:700;color:#fff;margin:1px 1px 1px 6px">${sp}</span><span style="font-size:9px;color:var(--text3);margin-left:2px">${spLabel}</span>` : '');
    const officialUrl = game==='pb' ? `${officialBase}${r.draw_date}` : officialBase;
    return `<tr style="border-bottom:1px solid var(--border);transition:background .1s" onmouseenter="this.style.background='rgba(255,255,255,.03)'" onmouseleave="this.style.background=''">
      <td style="padding:10px 12px;white-space:nowrap;font-size:11px;color:var(--text2)"><a href="${cnDrawHref(game, r.draw_date)}" style="color:var(--accent);text-decoration:none">${dt}</a></td>
      <td style="padding:10px 12px">${ballHtml}</td>
      <td style="padding:10px 12px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:12px;color:var(--text)">${fmtM(jp)}</td>
      <td style="padding:10px 12px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:12px;color:var(--text2)">${fmtM(cash)}</td>
      <td style="padding:10px 12px;text-align:center">
        ${won ? `<span style="font-size:11px;font-weight:700;color:var(--warn)">🏆 YES${ws}</span>` : `<span style="font-size:10px;color:var(--text3)">Rolled</span>`}
      </td>
      <td style="padding:10px 12px;text-align:center"><a href="${officialUrl}" target="_blank" rel="noopener" style="font-size:11px;color:var(--accent);text-decoration:none" aria-label="Official results for ${dt}">↗</a></td>
    </tr>`;
  }).join('');
  // The page arrives with the latest draws already in the table
  // (scripts/prerender.js). If they are the same draws, keep those rows:
  // replacing identical content only makes the page repaint late (and counts
  // as a slow "largest paint" for Google).
  const pre = tbody && tbody.querySelectorAll('tr[data-date]');
  const same = pre && pre.length === rows.length && game === 'pb' &&
    [...pre].every((tr, i) => tr.getAttribute('data-date') === rows[i].draw_date);
  if (tbody && !same) tbody.innerHTML = html;
  if (lm) lm.style.display = (limit > 0 && rows.length >= limit) ? 'block' : 'none';
  if (status) status.textContent = `Showing ${rows.length} draws · Source: draw_history + draw_news`;
}

function _appendResults(rows, newsMap) {
  const tbody = document.getElementById('res-tbody');
  if (!tbody || !rows.length) return;
  const game  = _resultsGame;
  const spLabel = game==='pb'?'PB':'MB', spColor=game==='pb'?'#dc3232':'#1e5ac8';
  const html = rows.map(r => {
    const news=newsMap[r.draw_date]||{};
    const nums=[r.pick1,r.pick2,r.pick3,r.pick4,r.pick5].filter(Boolean);
    const sp=r.special_ball, jp=r.jackpot_amount||news.jackpot_amount, cash=news.cash_value, won=news.had_winner||r.had_winner, ws=news.winner_state?` (${news.winner_state})`:'';
    const dt=r.draw_date?new Date(r.draw_date+'T12:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'}):r.draw_date;
    const fmtM=n=>!n?'—':n>=1e9?'$'+(n/1e9).toFixed(2)+'B':'$'+(n/1e6).toFixed(1)+'M';
    const bh=nums.map(n=>`<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:var(--bg3);border:1.5px solid var(--accent);font-size:11px;font-weight:700;color:var(--accent);margin:1px">${n}</span>`).join('')+(sp?`<span style="display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:${spColor};font-size:11px;font-weight:700;color:#fff;margin:1px 1px 1px 6px">${sp}</span><span style="font-size:9px;color:var(--text3);margin-left:2px">${spLabel}</span>`:'');
    const ou=game==='pb'?`https://www.powerball.com/draw-result?gc=powerball&date=${r.draw_date}`:'https://www.megamillions.com/Winning-Numbers';
    return `<tr style="border-bottom:1px solid var(--border)" onmouseenter="this.style.background='rgba(255,255,255,.03)'" onmouseleave="this.style.background=''"><td style="padding:10px 12px;white-space:nowrap;font-size:11px;color:var(--text2)"><a href="${cnDrawHref(game, r.draw_date)}" style="color:var(--accent);text-decoration:none">${dt}</a></td><td style="padding:10px 12px">${bh}</td><td style="padding:10px 12px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:12px">${fmtM(jp)}</td><td style="padding:10px 12px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:12px;color:var(--text2)">${fmtM(cash)}</td><td style="padding:10px 12px;text-align:center">${won?`<span style="font-size:11px;font-weight:700;color:var(--warn)">🏆 YES${ws}</span>`:`<span style="font-size:10px;color:var(--text3)">Rolled</span>`}</td><td style="padding:10px 12px;text-align:center"><a href="${ou}" target="_blank" rel="noopener" style="font-size:11px;color:var(--accent);text-decoration:none">↗</a></td></tr>`;
  }).join('');
  tbody.insertAdjacentHTML('beforeend', html);
}

// ── GAME NAV DROPDOWNS ───────────────────────────────────
function toggleGameNav(which) {
  const ids = ['pb','mm'];
  ids.forEach(id => {
    const m = document.getElementById('nav-'+id+'-menu');
    const b = document.getElementById('nav-'+id+'-btn');
    if (!m) return;
    const opening = (id === which) && m.style.display === 'none';
    m.style.display = opening ? 'block' : 'none';
    if (b) b.setAttribute('aria-expanded', opening);
  });
}
function closeGameNavs() {
  ['pb','mm'].forEach(id => {
    const m = document.getElementById('nav-'+id+'-menu');
    const b = document.getElementById('nav-'+id+'-btn');
    if (m) m.style.display = 'none';
    if (b) b.setAttribute('aria-expanded','false');
  });
}
document.addEventListener('click', e => {
  if (!e.target.closest('.nav-drop-wrap')) closeGameNavs();
});

function filterDD(f){ if(f==='all') return getGameDD(); if(f==='low-pres') return DD.filter(d=>d.pres<29.9); if(f==='high-pres') return DD.filter(d=>d.pres>=30.0); if(f==='cold') return DD.filter(d=>d.temp<55); if(f==='cool') return DD.filter(d=>d.temp>=55&&d.temp<65); if(f==='mild') return DD.filter(d=>d.temp>=65&&d.temp<75); if(f==='warm') return DD.filter(d=>d.temp>=75&&d.temp<85); if(f==='high-hum') return DD.filter(d=>d.hum>=80); if(f==='low-hum') return DD.filter(d=>d.hum<60); if(f==='calm') return DD.filter(d=>d.ws===0||d.wind==='CALM'); if(f==='windy') return DD.filter(d=>d.ws>=10); return DD; }
function getFreq(data,maxBall=69){ const f={}; for(let i=1;i<=maxBall;i++) f[i]=0; data.forEach(d=>d.picks.forEach(n=>{ if(n>=1&&n<=maxBall) f[n]++; })); return f; }
function getPairs(data,limit=20){ const p={}; data.forEach(d=>{ const s=d.picks.slice().sort((a,b)=>a-b); for(let i=0;i<s.length;i++) for(let j=i+1;j<s.length;j++){ const k=s[i]+'-'+s[j]; p[k]=(p[k]||0)+1; } }); return Object.entries(p).sort((a,b)=>b[1]-a[1]).slice(0,limit); }

function renderFreqBars(data,elId,hot=true,limit=15){ const freq=getFreq(data),sorted=Object.entries(freq).map(([n,c])=>[+n,c]).sort((a,b)=>hot?b[1]-a[1]:a[1]-b[1]),top=sorted.slice(0,limit),maxF=Math.max(...top.map(([,c])=>c)),el=document.getElementById(elId); if(!el) return; el.innerHTML=top.map(([n,c],i)=>`<div class="fbar-row"><div class="fbar-num">${n}</div><div class="fbar-track"><div class="fbar-fill" style="width:${Math.round(c/maxF*100)}%;background:linear-gradient(90deg,${hot&&i<5?'var(--danger)':'var(--accent)'},${hot&&i<5?'var(--warn)':'var(--ok)'})">${c}</div></div><div class="fbar-ct">${c}</div></div>`).join(''); }
function renderPairBubbles(pairs,elId){ const el=document.getElementById(elId); if(!el) return; const maxC=pairs[0]?.[1]||1; el.innerHTML=pairs.map(([k,c])=>{ const [a,b]=k.split('-'),op=(0.35+0.65*(c/maxC)).toFixed(2); return `<div style="background:rgba(0,180,216,${op});border:1px solid rgba(0,180,216,.4);border-radius:9px;padding:6px 10px;text-align:center" title="${a}+${b}: ${c}x" aria-label="${a} and ${b} appeared together ${c} times"><div style="font-family:'IBM Plex Mono',monospace;font-size:13px;font-weight:700;color:var(--text)">${a}·${b}</div><div style="font-size:10px;color:var(--text2)">${c}x</div></div>`; }).join(''); }
function initTrends(){ const gdd=getGameDD(); renderFreqBars(gdd,'nat-hot',true); renderFreqBars(gdd,'nat-cold',false); renderPairBubbles(getPairs(gdd),'nat-pairs'); updateWxTrends(); const freq=getFreq(DD),top15=Object.entries(freq).map(([n,c])=>[+n,c]).sort((a,b)=>b[1]-a[1]).slice(0,15); if(freqChartInst) freqChartInst.destroy(); const ctx=document.getElementById('freqChart'); if(ctx && typeof Chart!=='undefined') freqChartInst=new Chart(ctx,{type:'bar',data:{labels:top15.map(([n])=>n),datasets:[{label:'Times Drawn',data:top15.map(([,c])=>c),backgroundColor:top15.map((_,i)=>i<3?'rgba(239,71,111,.85)':'rgba(0,180,216,.75)'),borderRadius:4,borderWidth:0}]},options:{responsive:true,maintainAspectRatio:false,plugins:{legend:{display:false},tooltip:{callbacks:{label:c=>`Drawn ${c.raw} times`}}},scales:{x:{grid:{color:'rgba(255,255,255,.04)'}},y:{grid:{color:'rgba(255,255,255,.04)'}}}}}); }
function updateWxTrends(){ const f=document.getElementById('wx-filter').value,data=filterDD(f); document.getElementById('wx-count').textContent=data.length.toLocaleString(); renderFreqBars(data,'wx-hot',true,12); renderPairBubbles(getPairs(data,15),'wx-pairs'); const bf=getFreq(getGameDD()),wf=getFreq(data),sig=Object.entries(wf).map(([n,c])=>[+n,(c/data.length)-(bf[n]/DD.length)]).sort((a,b)=>b[1]-a[1]).slice(0,10); const sEl=document.getElementById('wx-signal'); if(sEl) sEl.innerHTML=sig.map(([n,d])=>`<div style="display:flex;align-items:center;gap:7px;margin-bottom:5px"><div style="width:22px;text-align:right;font-family:'IBM Plex Mono',monospace;font-size:11px;font-weight:700">${n}</div><div style="flex:1;height:16px;background:rgba(255,255,255,.05);border-radius:3px;overflow:hidden"><div style="height:100%;width:${Math.min(100,Math.abs(d)*2000)}%;background:${d>0?'var(--ok)':'var(--danger)'};opacity:.8"></div></div><div style="font-size:10px;font-family:'IBM Plex Mono',monospace;color:${d>0?'var(--ok)':'var(--danger)'}">${d>0?'+':''}${(d*100).toFixed(1)}%</div></div>`).join(''); }
function findPairs(){ const num=+document.getElementById('pf-num').value,wx=document.getElementById('pf-wx').value; if(!num||num<1||num>69){ showToast('Enter 1–69'); return; } const data=filterDD(wx),pairCt={}; data.forEach(d=>{ if(d.picks.includes(num)) d.picks.filter(n=>n!==num).forEach(n=>{pairCt[n]=(pairCt[n]||0)+1;}); }); const sorted=Object.entries(pairCt).sort((a,b)=>b[1]-a[1]).slice(0,10),maxC=sorted[0]?.[1]||1,el=document.getElementById('pf-result'); if(!sorted.length){ el.innerHTML='<div style="color:var(--text3)">No results for this condition</div>'; return; } el.innerHTML=`<div style="font-size:11px;color:var(--text2);width:100%;margin-bottom:6px">Pairs with <strong style="color:var(--accent)">${num}</strong> from ${data.length} draws:</div>`+sorted.map(([n,c])=>`<div style="text-align:center" aria-label="${n} paired ${c} times"><div class="ball ball-w ball-sm" style="margin:0 auto 3px;opacity:${(0.5+0.5*c/maxC).toFixed(2)}">${n}</div><div style="font-size:9px;color:var(--text2)">${c}x</div></div>`).join(''); }

// updateTrendRange — dynamic count display
function updateTrendRange(){
  const rangeVal = +document.getElementById('trend-range').value;
  const minCount = +document.getElementById('trend-mincount').value || 1;
  const winOnly  = document.getElementById('trend-win-only')?.checked;
  let data = getGameDD();
  if (rangeVal > 0) data = data.slice(0, rangeVal);
  if (winOnly) {
    const filtered = data.filter(d => d.had_winner);
    const noticeEl = document.getElementById('trend-win-notice');
    if (filtered.length === 0) {
      if (noticeEl) noticeEl.style.display = 'block';
    } else {
      if (noticeEl) noticeEl.style.display = 'none';
      data = filtered;
    }
  }
  const countEl = document.getElementById('trend-game-count');
  if (countEl) countEl.textContent = data.length.toLocaleString();
  const showEl = document.getElementById('trend-showing');
  if (showEl) showEl.textContent = data.length.toLocaleString();
  renderFreqBars(data, 'nat-hot', true);
  renderFreqBars(data, 'nat-cold', false);
  renderPairBubbles(getPairs(data), 'nat-pairs');
  // Frequency table (filtered)
  const freq = getFreq(data), sorted = Object.entries(freq).map(([n,c])=>[+n,c]).sort((a,b)=>b[1]-a[1]);
  const tbody = document.getElementById('freq-table-body');
  if (tbody) {
    const maxC = sorted[0]?.[1] || 1;
    tbody.innerHTML = sorted.filter(([,c])=>c>=minCount).map(([n,c]) =>
      `<tr><td style="font-family:'IBM Plex Mono',monospace;font-weight:700">${n}</td><td>${c}</td><td style="width:120px"><div style="height:8px;background:rgba(0,180,216,.25);border-radius:2px;overflow:hidden"><div style="height:100%;width:${Math.round(c/maxC*100)}%;background:var(--accent)"></div></div></td></tr>`
    ).join('');
  }
  // Chart update
  const top15 = sorted.slice(0, 15);
  if (freqChartInst) {
    freqChartInst.data.labels = top15.map(([n]) => n);
    freqChartInst.data.datasets[0].data = top15.map(([,c]) => c);
    freqChartInst.data.datasets[0].backgroundColor = top15.map((_,i) => i<3?'rgba(239,71,111,.85)':'rgba(0,180,216,.75)');
    freqChartInst.update();
  }
  updateWxTrends();
}

// ── MAP ───────────────────────────────────────────────────
// The winner map itself is in assets/winner-map.js.
// Base map tiles. CARTO's free basemaps started requiring an API key (tiles
// came back watermarked "API KEY REQUIRED"), so use the OpenStreetMap tile
// server (free with attribution, fine for this traffic). Dark themes darken
// the tiles with a CSS filter (.cn-map-tiles in styles.css).
function cnBaseTiles(maxZoom) {
  return L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: maxZoom || 18, className: 'cn-map-tiles',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" rel="noopener">OpenStreetMap</a> contributors'
  });
}

// ── CALCULATOR ────────────────────────────────────────────
let taxDonutInst=null;
const FED=0.37, FED_WH=0.24; // FED = top marginal rate (labels only); real tax uses fedTax()

// ── Federal income tax: progressive brackets ─────────────────────────────────
// 2026 brackets from the IRS tax-year-2026 inflation adjustments (Rev. Proc.
// 2025-32). Each entry is [bracket floor $, rate]. loadFedBrackets() replaces
// these with the newest year in the federal_tax_brackets table when available.
let FED_BRACKETS_YEAR = 2026;
let FED_BRACKETS = {
  single: [[0,.10],[12400,.12],[50400,.22],[105700,.24],[201775,.32],[256225,.35],[640600,.37]],
  mfj:    [[0,.10],[24800,.12],[100800,.22],[211400,.24],[403550,.32],[512450,.35],[768700,.37]],
};
// Tax owed on `income` taxable dollars (assumes the prize is the only income).
function fedTax(income, status = 'single') {
  const b = FED_BRACKETS[status] || FED_BRACKETS.single;
  let tax = 0;
  for (let i = 0; i < b.length; i++) {
    const lo = b[i][0], hi = i + 1 < b.length ? b[i + 1][0] : Infinity;
    if (income <= lo) break;
    tax += (Math.min(income, hi) - lo) * b[i][1];
  }
  return tax;
}
async function loadFedBrackets() {
  try {
    const { data } = await _supa.from('federal_tax_brackets')
      .select('tax_year,filing_status,bracket_floor,rate')
      .order('tax_year', { ascending: false }).order('bracket_floor', { ascending: true });
    if (!data?.length || data[0].tax_year < FED_BRACKETS_YEAR) return;
    const yr = data[0].tax_year, next = {};
    data.filter(r => r.tax_year === yr).forEach(r => {
      (next[r.filing_status] = next[r.filing_status] || []).push([Number(r.bracket_floor), Number(r.rate)]);
    });
    if (next.single?.length) { FED_BRACKETS = { ...FED_BRACKETS, ...next }; FED_BRACKETS_YEAR = yr; }
  } catch (e) { console.warn('federal_tax_brackets load failed:', e); }
}

// ── Annuity schedule ─────────────────────────────────────────────────────────
// Powerball and Mega Millions annuities are 30 payments (1 now + 29 yearly),
// each 5% larger than the last, that add up to the advertised jackpot. So the
// first payment is J·g / ((1+g)^30 − 1) ≈ 1.505% of the jackpot — not J/30.
const ANNUITY_YEARS = 30, ANNUITY_GROWTH = 0.05;
function annuityPayment(jackpot, year /* 1-based */) {
  const g = ANNUITY_GROWTH, n = ANNUITY_YEARS;
  if (year < 1 || year > n) return 0;
  const first = jackpot * g / (Math.pow(1 + g, n) - 1);
  return first * Math.pow(1 + g, year - 1);
}
// State + federal tax on one payment (state is a flat effective rate).
function prizeTax(amount, stateRate) { return fedTax(amount) + amount * (stateRate || 0); }
// CASH_R is updated dynamically from draw_news.cash_value / jackpot when live data loads
// Actual ratio varies significantly by jackpot size + current interest rates (often 45–65%)
let CASH_R = 0.602;
// Live jackpot store — filled by updateHomeJackpots(), consumed by setCalcGame()
let _liveJP = { pb:0, pbCash:0, pbDate:'', mm:0, mmCash:0, mmDate:'' };

// Helper: compute next draw text for a game ("Sat 10:59 PM ET")
function getNextDrawText(game) {
  // Eastern time, and today counts until the draw (the old version used the
  // visitor's clock and always skipped today, e.g. "Wed" on a Monday draw day).
  const iso = getNextDrawDateISO(game === 'pb' ? 'pb' : 'mm');
  const [y, m, d] = iso.split('-').map(Number);
  const day = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  const today = _etDateParts().iso === iso;
  return (today ? 'Tonight' : day) + ' ' + (game === 'pb' ? '10:59 PM ET' : '11:00 PM ET');
}

// Switch calculator between PB and MM — pre-fills jackpot + cash from live data
let _calcGame = 'pb';
function setCalcGame(game) {
  _calcGame = game;
  document.querySelectorAll('.calc-game-btn').forEach(b => {
    b.classList.toggle('btn-primary', b.dataset.g===game);
    b.classList.toggle('btn-outline', b.dataset.g!==game);
  });
  const jp   = game==='pb' ? _liveJP.pb     : _liveJP.mm;
  const cash = game==='pb' ? _liveJP.pbCash : _liveJP.mmCash;
  const jEl  = document.getElementById('c-jackpot');
  const cEl  = document.getElementById('c-cash');
  if (jEl && jp   > 0) { jEl.value = jp;   jEl._userEdited  = false; }
  if (cEl && cash > 0) { cEl.value = cash;  cEl._cashEdited  = false; }
  const lbl = document.getElementById('calc-game-lbl');
  if (lbl) lbl.textContent = game==='pb' ? '🔴 Powerball®' : '🔵 Mega Millions®';
  calcAll();
  // Auto-refresh Growth Strategy chart when the active game changes
  if (typeof calcCompound === 'function' && document.getElementById('compoundChart')) {
    try { calcCompound(); } catch(_){}
  }
  // Auto-refresh VS compare chart if that panel is wired up
  if (typeof calcVS === 'function' && document.getElementById('vsChart')) {
    try { calcVS(); } catch(_){}
  }
}
const fmt=fmtM; // alias — keeps all fmt() calls working
function fmtM(n){ if(!isFinite(n)) return '$0'; const a=Math.abs(n),sign=n<0?'-':''; if(a>=1e9) return sign+'$'+(a/1e9).toFixed(2)+'B'; if(a>=1e6) return sign+'$'+(a/1e6).toFixed(2)+'M'; return sign+'$'+Math.round(a).toLocaleString(); }
function getStateRate(){ return parseFloat((document.getElementById('c-state')?.value||'0|FL').split('|')[0])/100; }

function calcAll(){
  const jackpot=+document.getElementById('c-jackpot')?.value||342000000;
  const sr=parseFloat((document.getElementById('c-state')?.value||'0|FL').split('|')[0])/100;
  updateBigTotals(jackpot, sr);
  // Use user-entered cash option if provided, otherwise compute from live ratio
  const cashInput = parseFloat(document.getElementById('c-cash')?.value||0);
  const cash = (cashInput > 0 && cashInput < jackpot) ? cashInput : jackpot * CASH_R;
  // Show live ratio in the label
  const liveRatioPct = ((cash/jackpot)*100).toFixed(1);
  const cashLblEl = document.getElementById('cr-cash-lbl');
  if (cashLblEl) cashLblEl.textContent = `Cash Value (${liveRatioPct}%)`;
  const fedTotal=fedTax(cash);
  const fedWh=Math.min(cash*FED_WH, fedTotal), fedAdd=Math.max(0, fedTotal-fedWh), stateTax=cash*sr, net=cash-fedWh-fedAdd-stateTax;
  const set=(id,v)=>{ const el=document.getElementById(id); if(el) el.textContent=v; };
  set('cr-adv',fmtM(jackpot)); set('cr-cash',fmtM(cash)); set('cr-fed-wh','−'+fmtM(fedWh));
  set('cr-fed-add','−'+fmtM(fedAdd)); set('cr-state-pct','('+(sr*100).toFixed(2)+'%)');
  set('cr-state-amt','−'+fmtM(stateTax)); set('cr-net',fmtM(net));
  set('c-lump-net',fmtM(net));
  const annYr1G=annuityPayment(jackpot,1), annYr1N=annYr1G-prizeTax(annYr1G,sr);
  set('c-ann-yr1',fmtM(annYr1N)+'/yr');
  // Annuity table — totals always cover all 30 payments (gross total = jackpot)
  let tG=0,tT=0,tN=0, rows='';
  for(let y=1;y<=ANNUITY_YEARS;y++){ const g=annuityPayment(jackpot,y),t=prizeTax(g,sr),n=g-t; tG+=g;tT+=t;tN+=n;
    if(y<=15) rows+=`<tr><td style="font-family:'IBM Plex Mono',monospace">${y}</td><td>${fmtM(g)}</td><td style="color:var(--danger)">${fmtM(t)}</td><td style="color:var(--ok)">${fmtM(n)}</td></tr>`;
  }
  const tbody=document.getElementById('ann-table');
  if(tbody) tbody.innerHTML=rows+`<tr><td colspan="4" style="font-size:10px;color:var(--text2)">… 15 more years not shown above</td></tr>`;
  set('ann-gross-total',fmtM(tG)); set('ann-tax-total',fmtM(tT)); set('ann-net-total',fmtM(tN));
  // Donut chart
  if(taxDonutInst) taxDonutInst.destroy();
  const ctx=document.getElementById('taxDonut'); if(ctx && typeof Chart!=='undefined') taxDonutInst=new Chart(ctx,{type:'doughnut',data:{labels:['Net Take-Home','Fed Withholding 24%','Additional Fed (owed at filing)','State Tax'],datasets:[{data:[net,fedWh,fedAdd,stateTax],backgroundColor:['rgba(6,214,160,.85)','rgba(239,71,111,.8)','rgba(239,71,111,.5)','rgba(255,209,102,.8)'],borderWidth:2,borderColor:['#06d6a0','#ef476f','#ef476f','#ffd166']}]},options:{responsive:true,maintainAspectRatio:false,animation:{duration:400},plugins:{legend:{position:'bottom',labels:{font:{size:10},boxWidth:10,padding:5}}},cutout:'62%'}});
  // NOTE: do NOT call updateHomeJackpots() here — it calls calcAll() at the end,
  // which would create an infinite loop (calcAll → updateHomeJackpots → calcAll …).
  // The calc-page boot already calls updateHomeJackpots() once and refreshes on
  // a 10-min interval.
  const stateAbbr = (document.getElementById('c-state')?.value || '0|FL').split('|')[1] || 'FL';
  window.track('calc_tax', { game: 'powerball', jackpot_m: Math.round(jackpot / 1e6), state: stateAbbr });
}

// Parse "$450 Million" / "$1.2 Billion" → integer dollars
function parseJackpotStr(s) {
  if (!s) return null;
  const clean = String(s).replace(/[$,\s]/g, '').toLowerCase();
  const m = clean.match(/^([\d.]+)(m(?:illion)?|b(?:illion)?)?$/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!n || isNaN(n)) return null;
  if (m[2]?.startsWith('b')) return Math.round(n * 1_000_000_000);
  if (m[2]?.startsWith('m')) return Math.round(n * 1_000_000);
  return n > 999999 ? Math.round(n) : Math.round(n * 1_000_000);
}

// Cash option for the jackpot being shown: draw_news.cash_value belongs to the
// drawn jackpot (jackpot_amount); when the card shows next_jackpot, scale it.
function _nextCash(row, shown) {
  const cash = row.cash_value || 0, drawn = row.jackpot_amount || 0;
  if (!cash || !shown) return 0;
  if (row.next_jackpot && shown === row.next_jackpot && drawn > 0 && drawn !== shown) return Math.round(shown * cash / drawn);
  return cash;
}
// First annuity payment after tax (30 payments growing 5%/yr; same as the calculator)
function _annYr1AfterTax(jp, stateRate) {
  const first = annuityPayment(jp, 1);
  return first - prizeTax(first, stateRate);
}

async function updateHomeJackpots(){
  const sr=getStateRate();
  const s=(id,v)=>{ const el=document.getElementById(id); if(el) el.textContent=v; };
  let pb = 0, mm = 0;

  // 1. Primary: Supabase draw_news table (populated by EF3 draw-feed)
  try {
    const { data } = await _supa
      .from('draw_news')
      .select('game,jackpot_amount,next_jackpot,cash_value,draw_date,headline,detail,had_winner,winner_state,winner_count')
      .order('draw_date',{ascending:false})
      .limit(10);
    if (data?.length) {
      const pbRow = data.find(r=>r.game==='powerball');
      const mmRow = data.find(r=>r.game==='megamillions');

      if (pbRow) {
        pb = pbRow.next_jackpot || pbRow.jackpot_amount || 0;
        // Store for calculator
        _liveJP.pb = pb;
        // draw_news.cash_value is the cash value of the jackpot that was just
        // DRAWN; scale it to the advertised next jackpot shown on the card.
        _liveJP.pbCash = _nextCash(pbRow, pb);
        _liveJP.pbDate = pbRow.draw_date || '';
        // Update news card
        const pbHeadline = pbRow.headline ||
          (pbRow.had_winner
            ? `🏆 JACKPOT WON${pbRow.winner_state?' in '+pbRow.winner_state:''}! Next starts at $20M`
            : `No winner — jackpot rolls to ${pb > 0 ? fmtM(pb) : '—'}`);
        const pbDetail = pbRow.detail ||
          `Draw: ${pbRow.draw_date ? new Date(pbRow.draw_date+'T12:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'} · Next: ${getNextDrawText('pb')}`;
        s('news-pb-headline', pbHeadline);
        s('news-pb-detail', pbDetail);
        // Winner badge
        const pbBadge = document.getElementById('news-pb-badge');
        if (pbBadge) { pbBadge.textContent = pbRow.had_winner ? '🏆 WINNER' : '🔄 ROLLED'; pbBadge.style.color = pbRow.had_winner ? 'var(--warn)' : 'var(--ok)'; }
      }

      if (mmRow) {
        mm = mmRow.next_jackpot || mmRow.jackpot_amount || 0;
        _liveJP.mm = mm;
        _liveJP.mmCash = _nextCash(mmRow, mm);
        _liveJP.mmDate = mmRow.draw_date || '';
        const mmHeadline = mmRow.headline ||
          (mmRow.had_winner
            ? `🏆 JACKPOT WON${mmRow.winner_state?' in '+mmRow.winner_state:''}! Next starts at $20M`
            : `No winner — jackpot rolls to ${mm > 0 ? fmtM(mm) : '—'}`);
        const mmDetail = mmRow.detail ||
          `Draw: ${mmRow.draw_date ? new Date(mmRow.draw_date+'T12:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—'} · Next: ${getNextDrawText('mm')}`;
        s('news-mm-headline', mmHeadline);
        s('news-mm-detail', mmDetail);
        const mmBadge = document.getElementById('news-mm-badge');
        if (mmBadge) { mmBadge.textContent = mmRow.had_winner ? '🏆 WINNER' : '🔄 ROLLED'; mmBadge.style.color = mmRow.had_winner ? 'var(--warn)' : 'var(--ok)'; }
      }

      // Compute live cash ratio from most recent row with both cash_value and jackpot
      const ratioRow = data.find(r => r.cash_value > 0 && (r.jackpot_amount||r.next_jackpot) > 0);
      if (ratioRow) {
        const jp = ratioRow.jackpot_amount || ratioRow.next_jackpot;
        const ratio = ratioRow.cash_value / jp;
        if (ratio > 0.35 && ratio < 0.80) { // wide bounds — ratio varies from ~40% (small JP) to ~70% (large JP)
          CASH_R = parseFloat(ratio.toFixed(4));
          window.CASH_R = CASH_R; // expose for state-page doCalc()
          console.log('[coldnumbers] Live cash ratio updated:', CASH_R, '('+Math.round(CASH_R*100)+'%) from draw_news', ratioRow.draw_date);
        }
      }
    }
  } catch(e){ console.warn('draw_news fetch error:', e); }

  // 2. Fallback: CORS proxy to lottery APIs (only if DB had nothing)
  if (!pb) {
    try {
      const PROXY = 'https://api.allorigins.win/raw?url=';
      const r = await fetch(PROXY + encodeURIComponent('https://www.powerball.com/api/v1/drawings/powerball'), { signal: AbortSignal.timeout(6000) });
      if (r.ok) {
        const arr = await r.json();
        if (arr?.length) {
          const jp = parseJackpotStr(arr[0].field_next_jackpot || arr[0].field_jackpot || arr[0].next_jackpot);
          if (jp && jp > 1000000) pb = jp;
        }
      }
    } catch(_){}
  }

  // 3. If still nothing, show "Run draw-feed" message
  if (!pb) s('home-pb-jackpot', '—');
  if (!mm) s('home-mm-jackpot', '—');

  // Update jackpot displays — prefer actual cash_value from DB, fall back to CASH_R ratio
  if (pb) {
    const pbCash = _liveJP.pbCash > 0 ? _liveJP.pbCash : Math.round(pb * CASH_R);
    const pbNet = pbCash - prizeTax(pbCash, sr);
    const pbRatioPct = (pbCash / pb * 100).toFixed(1);
    s('home-pb-jackpot', fmtM(pb)); s('home-pb-cash', fmtM(pbCash)); s('home-pb-net', fmtM(pbNet));
    s('home-pb-ann', fmtM(_annYr1AfterTax(pb, sr)) + ' first yr');
    const pbLbl = document.getElementById('home-pb-cash-lbl');
    if (pbLbl) pbLbl.textContent = `Cash Value (~${pbRatioPct}%)`;
  }
  if (mm) {
    const mmCash = _liveJP.mmCash > 0 ? _liveJP.mmCash : Math.round(mm * CASH_R);
    const mmNet = mmCash - prizeTax(mmCash, sr);
    const mmRatioPct = (mmCash / mm * 100).toFixed(1);
    s('home-mm-jackpot', fmtM(mm)); s('home-mm-cash', fmtM(mmCash)); s('home-mm-net', fmtM(mmNet));
    s('home-mm-ann', fmtM(_annYr1AfterTax(mm, sr)) + ' first yr');
    const mmLbl = document.getElementById('home-mm-cash-lbl');
    if (mmLbl) mmLbl.textContent = `Cash Value (~${mmRatioPct}%)`;
  }
  // ── Auto-fill ALL jackpot inputs across the page ──────────────────────────
  // Main tax calculator
  const cj = document.getElementById('c-jackpot');
  const cc = document.getElementById('c-cash');
  const activeJP   = _calcGame==='pb' ? _liveJP.pb     : _liveJP.mm;
  const activeCash = _calcGame==='pb' ? _liveJP.pbCash : _liveJP.mmCash;
  if (cj && !cj._userEdited  && activeJP   > 0) cj.value = activeJP;
  if (cc && !cc._cashEdited  && activeCash > 0) cc.value = activeCash;
  // Update cash hint text
  const hint = document.getElementById('c-cash-hint');
  if (hint && _liveJP.pb > 0 && _liveJP.pbCash > 0) {
    const r = (_liveJP.pbCash / _liveJP.pb * 100).toFixed(1);
    hint.textContent = `Cash option auto-loaded from draw_news. Latest PB: ${fmtM(_liveJP.pbCash)} cash on ${fmtM(_liveJP.pb)} jackpot = ${r}% ratio.`;
  }
  // Quick tax inputs — PB
  const qtjPB = document.getElementById('qtax-jackpot-pb');
  const qtcPB = document.getElementById('qtax-cash-pb');
  if (qtjPB && _liveJP.pb > 0)     { qtjPB.value = _liveJP.pb;     }
  if (qtcPB && _liveJP.pbCash > 0) { qtcPB.value = _liveJP.pbCash; }
  calcQuickTax('pb');
  // Quick tax inputs — MM
  const qtjMM = document.getElementById('qtax-jackpot-mm');
  const qtcMM = document.getElementById('qtax-cash-mm');
  if (qtjMM && _liveJP.mm > 0)     { qtjMM.value = _liveJP.mm;     }
  if (qtcMM && _liveJP.mmCash > 0) { qtcMM.value = _liveJP.mmCash; }
  calcQuickTax('mm');
  // Fire main calculator recalc
  calcAll();
  // Auto-run analyzers if data is loaded (only if user hasn't already run them)
  if (DD && DD.length > 0) {
    const pbScoreEl = document.getElementById('scoreVal');
    const mmScoreEl = document.getElementById('mm-scoreVal');
    if (pbScoreEl && pbScoreEl.textContent === '--') setTimeout(runAnalyzer, 800);
    if (mmScoreEl && mmScoreEl.textContent === '--') setTimeout(()=>{
      const mmRunFn = typeof runMMAnalyzer === 'function' ? runMMAnalyzer : null;
      if (mmRunFn) mmRunFn();
    }, 1200);
  }
}


// ══════════════════════════════════════════════════════════════════════════════
// HOME PAGE — LAST DRAWN NUMBERS + MINI MAP
// ══════════════════════════════════════════════════════════════════════════════
// Draw pages (/draw/<game>/<date>.html) are built on the server a few hours
// after the results land. Link to the page once it surely exists; until then
// to the game's results archive.
function cnDrawHref(game, iso) {
  const g = (game === 'pb' || game === 'powerball') ? 'powerball' : 'megamillions';
  const ready = iso && Date.now() - Date.parse(iso + 'T12:00:00Z') > 28 * 3600e3;
  return '/draw/' + g + '/' + (ready ? iso + '.html' : '');
}

function renderLastDrawNumbers() {
  if (!DD || DD.length === 0) return;
  const ballW = n => `<span style="display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;background:var(--bg);border:2px solid var(--accent);font-weight:700;font-size:14px;color:var(--accent)">${n}</span>`;

  // Most recent Powerball draw
  const lastPB = DD.find(d => d.game === 'powerball');
  const pbDate = document.getElementById('home-pb-last-date');
  const pbNums = document.getElementById('home-pb-last-nums');
  const dateLink = d => `<a href="${cnDrawHref(d.game, d.iso)}" style="color:inherit">${d.date}</a>`;
  if (lastPB) {
    if (pbDate) pbDate.innerHTML = dateLink(lastPB);
    if (pbNums) pbNums.innerHTML = lastPB.picks.map(ballW).join('') +
      `<span style="display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;background:#dc3232;color:#fff;font-weight:700;font-size:14px">${lastPB.pb}</span>` +
      `<span style="font-size:10px;color:var(--text3);margin-left:4px">PB</span>`;
  }

  // Most recent Mega Millions draw
  const lastMM = DD.find(d => d.game === 'megamillions');
  const mmDate = document.getElementById('home-mm-last-date');
  const mmNums = document.getElementById('home-mm-last-nums');
  if (lastMM) {
    if (mmDate) mmDate.innerHTML = dateLink(lastMM);
    if (mmNums) mmNums.innerHTML = lastMM.picks.map(ballW).join('') +
      `<span style="display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;background:#ffd166;color:#000;font-weight:700;font-size:14px">${lastMM.pb}</span>` +
      `<span style="font-size:10px;color:var(--text3);margin-left:4px">MB</span>`;
  } else {
    if (mmDate) mmDate.textContent = 'Run draw-feed';
    if (mmNums) mmNums.innerHTML = '<span style="font-size:12px;color:var(--text3)">📡 Run EF3 draw-feed to load MM numbers</span>';
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// GROUP PLAY — EDITABLE TERMS + EMAIL + PAYMENT TRACKING
// ══════════════════════════════════════════════════════════════════════════════
let gpCustomTerms = localStorage.getItem('coldnumbers_gp_terms') || '';

// ══════════════════════════════════════════════════════════════════════════════
// ANALYZER — DEFAULT TO DRAW-DATE 11 PM WEATHER
// ══════════════════════════════════════════════════════════════════════════════
function selectDrawDateWeather() {
  // Find the 11 PM (hour 23, or drawHour=22 for 10:59 PM) forecast slot and select it
  if (typeof forecasts === 'undefined' || forecasts.length === 0) return;
  const drawSlot = forecasts.findIndex(f => f.isDraw);
  if (drawSlot >= 0 && typeof selectForecast === 'function') {
    selectForecast(drawSlot);
  }
}


// ══════════════════════════════════════════════════════════════════════════════
// GROUP PLAY — STATE + CORE FUNCTIONS
// ══════════════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════════════
// GROUP PLAY v2 — EXTENDED STATE
// ══════════════════════════════════════════════════════════════════════════════
let gpDrawHistory = JSON.parse(localStorage.getItem('coldnumbers_gp_draws') || '[]');
let gpRules = JSON.parse(localStorage.getItem('coldnumbers_gp_rules') || '{"multiplier":"yes","split":"equal","miss":"skip","payout":"payout","payoutThreshold":50,"jackpotAfter":"end","v":2}');
let gpCurrentDraw = JSON.parse(localStorage.getItem('coldnumbers_gp_current') || '{"v":2,"kittyStart":0}');

let gpPool = JSON.parse(localStorage.getItem('coldnumbers_gp_pool') || '{"name":"Office Lottery Pool","game":"pb","ticketPrice":2,"ticketsPerPerson":1}');
let gpMembers = JSON.parse(localStorage.getItem('coldnumbers_gp_members') || '[]');
let gpLog = JSON.parse(localStorage.getItem('coldnumbers_gp_log') || '[]');

async function gpLogEntry(action, details) {
  const prev = gpLog.length > 0 ? gpLog[gpLog.length-1].hash : '0000000000000000';
  const ts = new Date().toISOString();
  const raw = ts + '|' + action + '|' + JSON.stringify(details) + '|' + prev;
  let hash = '0'.repeat(16);
  try {
    const enc = new TextEncoder();
    const buf = await crypto.subtle.digest('SHA-256', enc.encode(raw));
    hash = Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,'0')).join('').substring(0,16);
  } catch(e) {}
  const entry = { ts, action, details, prev, hash };
  gpLog.push(entry);
  localStorage.setItem('coldnumbers_gp_log', JSON.stringify(gpLog));
  renderGpLog();
}

function renderGpLog() {
  const el = document.getElementById('gp-log-list');
  if (!el) return;
  if (gpLog.length === 0) {
    el.innerHTML = '<div style="text-align:center;color:var(--text3);font-size:12px;padding:20px">No audit log entries yet. Actions like adding members and saving pool info will be recorded here.</div>';
  } else {
    el.innerHTML = [...gpLog].reverse().map(e => `
      <div style="background:var(--bg2);border:1px solid var(--border);border-radius:7px;padding:8px 12px;font-size:11px">
        <div style="display:flex;gap:8px;align-items:center;margin-bottom:3px">
          <span style="font-weight:700;color:var(--accent)">${e.action}</span>
          <span style="color:var(--text3)">${new Date(e.ts).toLocaleString()}</span>
          <span style="margin-left:auto;font-family:'IBM Plex Mono',monospace;color:var(--text3);font-size:10px">#${e.hash}</span>
        </div>
        <div style="color:var(--text2)">${Object.entries(e.details||{}).map(([k,v])=>`${k}: ${v}`).join(' · ')}</div>
      </div>`).join('');
  }
  const statusEl = document.getElementById('gp-chain-status');
  if (statusEl) statusEl.textContent = '✅ Chain valid — ' + gpLog.length + ' entries';
}

async function gpVerifyChain() {
  if (gpLog.length === 0) { showToast('No log entries to verify'); return; }
  let valid = true;
  const enc = new TextEncoder();
  for (let i = 0; i < gpLog.length; i++) {
    const e = gpLog[i];
    const prev = i > 0 ? gpLog[i-1].hash : '0000000000000000';
    const raw = e.ts + '|' + e.action + '|' + JSON.stringify(e.details) + '|' + prev;
    try {
      const buf = await crypto.subtle.digest('SHA-256', enc.encode(raw));
      const hash = Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,'0')).join('').substring(0,16);
      if (hash !== e.hash || prev !== e.prev) { valid = false; break; }
    } catch(err) {}
  }
  const statusEl = document.getElementById('gp-chain-status');
  if (valid) {
    if (statusEl) { statusEl.textContent = '✅ Chain valid — ' + gpLog.length + ' entries'; statusEl.style.background = 'rgba(6,214,160,.1)'; statusEl.style.color = 'var(--ok)'; statusEl.style.borderColor = 'var(--ok)'; }
    showToast('✅ Audit chain is intact — ' + gpLog.length + ' entries verified');
  } else {
    if (statusEl) { statusEl.textContent = '⚠️ Chain tampered — verification failed'; statusEl.style.background = 'rgba(239,71,111,.1)'; statusEl.style.color = 'var(--danger)'; statusEl.style.borderColor = 'var(--danger)'; }
    showToast('⚠️ Chain verification failed — log may have been tampered with');
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// LIVE DRAW POPUP
// ══════════════════════════════════════════════════════════════════════════════
function checkLiveDrawPopup() {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  const isPBNight = [1,3,6].includes(day);
  const isMMNight = [2,5].includes(day);
  const isDrawWindow = hour >= 20 && hour < 24;
  if ((!isPBNight && !isMMNight) || !isDrawWindow) return;
  const tonight = now.toDateString();
  if (localStorage.getItem('coldnumbers_ldp_noshow') === tonight) return;
  const game = isMMNight ? 'Mega Millions' : 'Powerball';
  const url = isMMNight ? 'https://www.megamillions.com/Winning-Numbers' : 'https://www.powerball.com/powerball/winning-numbers';
  showToast(`🎰 ${game} draws tonight at 11 PM ET — <a href="${url}" target="_blank" style="color:var(--accent)">Check Results</a>`, 8000);
}

// ══════════════════════════════════════════════════════════════════════════════
// HOME PAGE WEATHER SYNC
// ══════════════════════════════════════════════════════════════════════════════
async function syncHomeWeather() {
  // Fetch live weather from weather_live table for both draw sites
  const s = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };
  try {
    s('hw-pb-next', getNextDrawText('pb'));
    s('hw-mm-next', getNextDrawText('mm'));
    const latest = (sid) => _supa.from('weather_live')
      .select('station_id,temperature_f,humidity_pct,wind_dir,wind_speed_mph,pressure_inhg,dew_point_f,short_forecast,valid_time')
      .eq('station_id', sid).eq('is_forecast', false)
      .order('valid_time', { ascending: false }).limit(1);
    const res = await Promise.all([latest('KTLH'), latest('KATL')]);
    const data = res.flatMap(r => r.data || []);
    if (!data.length) return;

    // Tallahassee (PB)
    const tlh = data.find(r => r.station_id === 'KTLH');
    if (tlh) {
      s('hw-pb-temp', tlh.temperature_f == null ? '—' : Math.round(tlh.temperature_f) + '°');
      s('hw-pb-hum', tlh.humidity_pct == null ? '—' : Math.round(tlh.humidity_pct) + '%');
      s('hw-pb-wind', (tlh.wind_dir || 'CALM') + ' ' + Math.round(tlh.wind_speed_mph || 0) + 'mph');
      s('hw-pb-pres', (tlh.pressure_inhg || 29.92).toFixed(2) + '"');
      s('hw-pb-dew', Math.round(tlh.dew_point_f || 0) + '°F');
      const condEl = document.getElementById('hw-pb-cond');
      if (condEl) condEl.textContent = wxIcon(tlh.short_forecast || 'Fair') + ' ' + (tlh.short_forecast || 'Fair');
    }

    // Atlanta (MM)
    const atl = data.find(r => r.station_id === 'KATL');
    if (atl) {
      s('hw-mm-temp', atl.temperature_f == null ? '—' : Math.round(atl.temperature_f) + '°');
      s('hw-mm-hum', atl.humidity_pct == null ? '—' : Math.round(atl.humidity_pct) + '%');
      s('hw-mm-wind', (atl.wind_dir || 'CALM') + ' ' + Math.round(atl.wind_speed_mph || 0) + 'mph');
      s('hw-mm-pres', (atl.pressure_inhg || 29.92).toFixed(2) + '"');
      s('hw-mm-dew', Math.round(atl.dew_point_f || 0) + '°F');
      const condEl = document.getElementById('hw-mm-cond');
      if (condEl) condEl.textContent = wxIcon(atl.short_forecast || 'Fair') + ' ' + (atl.short_forecast || 'Fair');
    }
  } catch(e) { console.warn('weather_live fetch error:', e); }
}

function wxIcon(cond) {
  if (!cond) return '☀️';
  const c = cond.toLowerCase();
  if (c.includes('rain') || c.includes('shower') || c.includes('storm')) return '🌧️';
  if (c.includes('snow') || c.includes('flurr')) return '🌨️';
  if (c.includes('cloud') || c.includes('overcast')) return '☁️';
  if (c.includes('partly') || c.includes('mostly sunny')) return '⛅';
  if (c.includes('fog') || c.includes('mist') || c.includes('haz')) return '🌫️';
  return '☀️';
}

// ══════════════════════════════════════════════════════════════════════════════
// SAVED PICKS — render from localStorage + Supabase
// ══════════════════════════════════════════════════════════════════════════════
function renderSavedPicks() {
  const el = document.getElementById('saved-picks-list');
  if (!el) return;
  let picks = [];
  try { picks = JSON.parse(localStorage.getItem('coldnumbers_saved_picks') || '[]'); } catch(e) {}
  if (picks.length === 0) {
    el.innerHTML = '<div style="text-align:center;color:var(--text3);font-size:12px;padding:20px">No saved picks yet. Generate picks in the Analyzer and save them here.</div>';
    return;
  }
  el.innerHTML = picks.slice().reverse().map((p, i) => `
    <div style="background:var(--bg2);border:1px solid var(--border);border-radius:9px;padding:10px 14px;display:flex;align-items:center;gap:10px;flex-wrap:wrap">
      <span style="font-size:10px;font-weight:700;padding:2px 8px;border-radius:10px;background:${p.game==='pb'?'rgba(239,71,111,.15)':'rgba(0,180,216,.15)'};color:${p.game==='pb'?'var(--danger)':'var(--accent)'}">${p.game==='pb'?'PB':'MM'}</span>
      <span style="font-family:'IBM Plex Mono',monospace;font-weight:700;font-size:13px">${p.numbers}</span>
      <span style="font-size:11px;color:var(--text3);margin-left:auto">${new Date(p.ts).toLocaleDateString()}</span>
      <button class="btn btn-outline" onclick="deleteSavedPick(${picks.length-1-i})" style="font-size:10px;padding:2px 8px;color:var(--danger);border-color:var(--danger)">✕</button>
    </div>`).join('');
}

function deleteSavedPick(idx) {
  let picks = [];
  try { picks = JSON.parse(localStorage.getItem('coldnumbers_saved_picks') || '[]'); } catch(e) {}
  picks.splice(idx, 1);
  localStorage.setItem('coldnumbers_saved_picks', JSON.stringify(picks));
  renderSavedPicks();
}

// ══════════════════════════════════════════════════════════════════════════════
// SCANNER PAGE — RECENT DRAWS
// ══════════════════════════════════════════════════════════════════════════════
function populateRecentDraws() {
  const el = document.getElementById('recent-draws-list');
  if (!el) return;
  const pb = DD.filter(d=>d.game==='powerball').sort((a,b)=>b.iso.localeCompare(a.iso)).slice(0,5);
  const mm = DD.filter(d=>d.game==='megamillions').sort((a,b)=>b.iso.localeCompare(a.iso)).slice(0,5);
  const all = [...pb, ...mm].sort((a,b)=>b.iso.localeCompare(a.iso)).slice(0,8);
  if (all.length === 0) {
    el.innerHTML = '<div style="color:var(--text3);font-size:12px">No draws loaded yet.</div>';
    return;
  }
  el.innerHTML = all.map(d => {
    const nums = d.picks || [];
    const pb_ball = d.pb;  // special_ball for all games stored as d.pb
    const label = d.game === 'powerball' ? '🔴 PB' : '🔵 MM';
    return `<div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border)">
      <span style="font-size:10px;font-weight:700">${label}</span>
      <span style="font-size:11px;color:var(--text2)">${d.date}</span>
      <span style="font-family:'IBM Plex Mono',monospace;font-size:12px">${nums.join('-')} | <span style="color:${d.game==='powerball'?'var(--danger)':'var(--accent)'}">${pb_ball}</span></span>
    </div>`;
  }).join('');
}

// ══════════════════════════════════════════════════════════════════════════════
// CALCULATOR — updateBigTotals + calcCompound
// ══════════════════════════════════════════════════════════════════════════════
function updateBigTotals(jackpotArg, stateRateArg) {
  // Called by calcAll() to update the banner cards at the top of the calculator
  const jackpot = jackpotArg || parseFloat((document.getElementById('c-jackpot')||{}).value) || 342000000;
  const stateRate = (stateRateArg !== undefined) ? stateRateArg : parseFloat(((document.getElementById('c-state')||{}).value||'0').split('|')[0]) / 100 || 0;
  const cashFactor = CASH_R;
  const cashInput = parseFloat((document.getElementById('c-cash')||{}).value || 0);
  const cashBefore = (cashInput > 0 && cashInput < jackpot) ? cashInput : jackpot * cashFactor;
  const totalTax = prizeTax(cashBefore, stateRate);
  const netHome = cashBefore - totalTax;
  const fmt = (n) => n >= 1e9 ? '$' + (n/1e9).toFixed(2) + 'B' : '$' + (n/1e6).toFixed(1) + 'M';
  const annEl = document.getElementById('big-ann-total');
  const cashEl = document.getElementById('big-cash-before');
  const taxEl = document.getElementById('big-tax-total');
  const netEl = document.getElementById('big-net-home');
  if (annEl) annEl.textContent = fmt(jackpot);
  if (cashEl) cashEl.textContent = fmt(cashBefore);
  if (taxEl) taxEl.textContent = '−' + fmt(totalTax);
  if (netEl) netEl.textContent = fmt(netHome);
}

let cgChartInst = null;

function calcCompound() {
  // Pull jackpot / state from the calculator inputs
  const jackpot = parseFloat(document.getElementById('c-jackpot')?.value) || 342000000;
  const sr = parseFloat((document.getElementById('c-state')?.value || '0|FL').split('|')[0]) / 100;
  const cashRaw = parseFloat(document.getElementById('c-cash')?.value || 0);
  const cash = (cashRaw > 0 && cashRaw < jackpot) ? cashRaw : jackpot * CASH_R;
  const net = cash - prizeTax(cash, sr);

  // Parameters
  const rate       = (parseFloat(document.getElementById('cg-rate')?.value)       || 7)  / 100;
  const wdPct      = (parseFloat(document.getElementById('cg-withdraw')?.value)    || 4)  / 100;
  const initSpend  =  parseFloat(document.getElementById('cg-spend')?.value)       || 5000000;
  const annInvPct  = (parseFloat(document.getElementById('cg-ann-invest')?.value)  || 40) / 100;
  const years      =  parseInt(document.getElementById('cg-years')?.value)         || 30;
  const cgTax      =  document.getElementById('cg-cgtax')?.checked !== false ? 0.20 : 0;
  const mode       =  document.getElementById('cg-chart-mode')?.value || 'balance';

  const labels = Array.from({length: years + 1}, (_, i) => 'Yr ' + i);

  // ── Strategy A: Park & Draw ──────────────────────────────
  let aBal = net, aInc = 0, aTax = 0;
  const aB = [aBal], aI = [0], aT = [0];
  for (let y = 1; y <= years; y++) {
    aBal *= (1 + rate);
    const wd = aBal * wdPct, tx = wd * cgTax;
    aBal -= wd; aInc += (wd - tx); aTax += tx;
    aB.push(aBal); aI.push(aInc); aT.push(aTax);
  }

  // ── Strategy B: Live Large First ────────────────────────
  let bBal = Math.max(0, net - initSpend), bInc = initSpend, bTax = 0;
  const bB = [bBal], bI = [bInc], bT = [0];
  for (let y = 1; y <= years; y++) {
    bBal *= (1 + rate);
    const wd = bBal * wdPct, tx = wd * cgTax;
    bBal -= wd; bInc += (wd - tx); bTax += tx;
    bB.push(bBal); bI.push(bInc); bT.push(bTax);
  }

  // ── Strategy C: Lottery Annuity ─────────────────────────
  let cInc = 0, cTax = 0;
  const cI = [0], cT = [0], cPmt = [0];
  for (let y = 1; y <= years; y++) {
    const g = annuityPayment(jackpot, y);
    const tx = prizeTax(g, sr), n = g - tx;
    cInc += n; cTax += tx;
    cI.push(cInc); cT.push(cTax); cPmt.push(n);
  }

  // ── Strategy D: Annuity + Invest ────────────────────────
  let dPort = 0, dSpent = 0, dTax = 0;
  const dB = [0], dI = [0], dT = [0];
  for (let y = 1; y <= years; y++) {
    dPort *= (1 + rate);
    if (y <= ANNUITY_YEARS) {
      const g = annuityPayment(jackpot, y), tx = prizeTax(g, sr), n = g - tx;
      dPort += n * annInvPct; dSpent += n * (1 - annInvPct); dTax += tx;
    }
    dB.push(dPort); dI.push(dSpent + dPort); dT.push(dTax);
  }

  const fmtM = n => n >= 1e9 ? '$' + (n/1e9).toFixed(2) + 'B' : '$' + (n/1e6).toFixed(1) + 'M';
  const set  = (id, v) => { const el = document.getElementById(id); if (el) el.textContent = v; };

  // Summary cards
  set('cg-a-end',   fmtM(aB[years])); set('cg-a-wdrawn', fmtM(aI[years])); set('cg-a-taxes', fmtM(aT[years]));
  set('cg-b-end',   fmtM(bB[years])); set('cg-b-wdrawn', fmtM(bI[years])); set('cg-b-taxes', fmtM(bT[years]));
  set('cg-ann-end', fmtM(cI[years])); set('cg-ann-gross', fmtM(jackpot));  set('cg-ann-taxes', fmtM(cT[years]));
  set('cg-d-end',   fmtM(dI[years])); set('cg-d-inv',    fmtM(dB[years])); set('cg-d-taxes', fmtM(dT[years]));

  // Choose dataset by display mode
  const getData = (b, i, t) => mode === 'balance' ? b : mode === 'income' ? i : t;
  const aData = getData(aB, aI, aT);
  const bData = getData(bB, bI, bT);
  // cData removed: was a paste error — result was unused; annuity handled directly in datasets below

  const datasets = [
    { label:'💼 Park & Draw',      data: getData(aB, aI, aT), borderColor:'#00b4d8', backgroundColor:'rgba(0,180,216,.06)', borderWidth:2.5, tension:0.35, fill:false, borderDash:[] },
    { label:'🏖️ Live Large First', data: getData(bB, bI, bT), borderColor:'#06d6a0', backgroundColor:'rgba(6,214,160,.06)',  borderWidth:2.5, tension:0.35, fill:false, borderDash:[6,3] },
    { label:'📬 Lottery Annuity',  data: mode==='balance' ? labels.map(()=>0) : getData(null, cI, cT), borderColor:'#ffd166', backgroundColor:'rgba(255,209,102,.06)', borderWidth:2.5, tension:0.35, fill:false, borderDash:[2,2] },
    { label:'📬💼 Annuity+Invest', data: getData(dB, dI, dT), borderColor:'#9b5de5', backgroundColor:'rgba(155,93,229,.06)', borderWidth:2.5, tension:0.35, fill:false, borderDash:[8,2,2,2] }
  ];

  if (cgChartInst) cgChartInst.destroy();
  const ctx = document.getElementById('compoundChart');
  if (ctx && typeof Chart !== 'undefined') {
    cgChartInst = new Chart(ctx, {
      type: 'line',
      data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { position:'top', labels:{ font:{size:11}, boxWidth:14, padding:10 } },
          tooltip: { callbacks: { label: c => ' ' + c.dataset.label + ': ' + fmtM(c.raw) } }
        },
        scales: {
          y: { ticks: { callback: v => v>=1e9?'$'+(v/1e9).toFixed(1)+'B':'$'+(v/1e6).toFixed(0)+'M', font:{size:10} }, grid:{color:'rgba(128,128,128,.1)'} },
          x: { ticks: { font:{size:10} }, grid:{display:false} }
        }
      }
    });
  }

  // Year-by-year table
  const tbody = document.getElementById('cg-tbody');
  if (tbody) {
    let rows = '';
    for (let y = 1; y <= years; y++) {
      const annG = annuityPayment(jackpot, y), yr1tax = annG > 0 ? prizeTax(annG, sr) : 0;
      rows += `<tr>
        <td style="font-family:'IBM Plex Mono',monospace">${y}</td>
        <td style="font-family:'IBM Plex Mono',monospace;color:var(--accent)">${fmtM(aB[y])}</td>
        <td style="font-family:'IBM Plex Mono',monospace;color:var(--accent)">${fmtM(aI[y])}</td>
        <td style="font-family:'IBM Plex Mono',monospace;color:var(--ok)">${fmtM(bB[y])}</td>
        <td style="font-family:'IBM Plex Mono',monospace;color:var(--ok)">${fmtM(bI[y])}</td>
        <td style="font-family:'IBM Plex Mono',monospace;color:var(--warn)">${fmtM(cPmt[y])}</td>
        <td style="font-family:'IBM Plex Mono',monospace;color:#9b5de5">${fmtM(dB[y])}</td>
        <td style="font-family:'IBM Plex Mono',monospace;color:var(--danger)">${fmtM(yr1tax)}</td>
      </tr>`;
    }
    tbody.innerHTML = rows;
  }
  const g = id => parseFloat(document.getElementById(id)?.value) || 0;
  window.track('calc_compound', { rate: g('cg-rate'), years: g('cg-years'), withdraw: g('cg-withdraw') });
}


// ══════════════════════════════════════════════════════════════════════════════
// NAV — MORE MENU TOGGLE
// ══════════════════════════════════════════════════════════════════════════════
function toggleNavMore() {
  const btn = document.getElementById('nav-more-btn');
  const panel = document.getElementById("nav-more-menu");
  if (!btn || !panel) return;
  const open = panel.style.display !== 'none' && panel.style.display !== '';
  panel.style.display = open ? 'none' : 'block';
  btn.setAttribute('aria-expanded', String(!open));
}

// ══════════════════════════════════════════════════════════════════════════════
// HOW TO PLAY — TAB
// ══════════════════════════════════════════════════════════════════════════════
function setHtpTab(game) {
  const pbPanel = document.getElementById('htp-panel-pb');
  const mmPanel = document.getElementById('htp-panel-mm');
  const pbBtn = document.getElementById('htp-tab-pb');
  const mmBtn = document.getElementById('htp-tab-mm');
  const isPb = game === 'pb';
  if (pbPanel) pbPanel.style.display = isPb ? '' : 'none';
  if (mmPanel) mmPanel.style.display = isPb ? 'none' : '';
  if (pbBtn) { pbBtn.className = isPb ? 'btn btn-primary' : 'btn btn-outline'; }
  if (mmBtn) { mmBtn.className = isPb ? 'btn btn-outline' : 'btn btn-primary'; }
  // Update live cash ratio display in How to Play panels
  const pctPb = document.getElementById('htp-pb-cash-ratio');
  const pctMm = document.getElementById('htp-mm-cash-ratio');
  const liveRatio = Math.round(CASH_R * 100);
  if (pctPb) pctPb.textContent = liveRatio;
  if (pctMm) pctMm.textContent = liveRatio;
}

// ══════════════════════════════════════════════════════════════════════════════
// HOME PAGE — EDIT JACKPOT INLINE
// ══════════════════════════════════════════════════════════════════════════════
// editJackpot removed — jackpots come from Supabase draw_news only

// ══════════════════════════════════════════════════════════════════════════════
// TRENDS PAGE — VIEW TOGGLE + TABLE SORT
// ══════════════════════════════════════════════════════════════════════════════
let _trendSortCol = 'count', _trendSortDir = -1;

function setTrendView(view) {
  const tableBtn = document.getElementById('tv-table');
  const chartBtn = document.getElementById('tv-chart');
  const tableEl = document.getElementById('trend-table-wrap');
  const chartEl = document.getElementById('trend-chart-wrap');
  const isTable = view === 'table';
  if (tableBtn) tableBtn.className = isTable ? 'btn btn-primary' : 'btn btn-outline';
  if (chartBtn) chartBtn.className = isTable ? 'btn btn-outline' : 'btn btn-primary';
  if (tableEl) tableEl.style.display = isTable ? '' : 'none';
  if (chartEl) chartEl.style.display = isTable ? 'none' : '';
}

function sortTrendTable(col) {
  if (_trendSortCol === col) _trendSortDir *= -1;
  else { _trendSortCol = col; _trendSortDir = -1; }
  const tbody = document.getElementById('trend-tbody');
  if (!tbody) return;
  const rows = Array.from(tbody.querySelectorAll('tr'));
  rows.sort((a, b) => {
    const aVal = a.dataset[col] !== undefined ? parseFloat(a.dataset[col]) : a.textContent;
    const bVal = b.dataset[col] !== undefined ? parseFloat(b.dataset[col]) : b.textContent;
    return _trendSortDir * (aVal > bVal ? 1 : aVal < bVal ? -1 : 0);
  });
  rows.forEach(r => tbody.appendChild(r));
}

// ══════════════════════════════════════════════════════════════════════════════
// CALCULATOR — VS MODE + FOCUS STRATEGY + MODE SWITCH
// ══════════════════════════════════════════════════════════════════════════════
function setCgMode(mode) {
  const allBtn = document.getElementById('cg-mode-all');
  const oneBtn = document.getElementById('cg-mode-one');
  const vsBtn = document.getElementById('cg-mode-vs');
  const allPanel = document.getElementById('cg-all-panel');
  const onePanel = document.getElementById('cg-one-panel');
  const vsPanel = document.getElementById('cg-vs-panel');
  [allBtn, oneBtn, vsBtn].forEach(b => { if(b) b.className = 'btn btn-outline'; });
  const activeBtn = mode==='all'?allBtn:mode==='one'?oneBtn:vsBtn;
  if (activeBtn) activeBtn.className = 'btn btn-primary';
  if (allPanel) allPanel.style.display = mode==='all' ? '' : 'none';
  if (onePanel) onePanel.style.display = mode==='one' ? '' : 'none';
  if (vsPanel) vsPanel.style.display = mode==='vs' ? '' : 'none';
  if (mode==='vs') calcVS();
}

function focusStrategy(strat) {
  document.querySelectorAll('.cg-strat-btn').forEach(b => b.className = 'btn btn-outline cg-strat-btn');
  const btn = document.querySelector(`.cg-strat-btn[data-strat="${strat}"]`);
  if (btn) btn.className = 'btn btn-primary cg-strat-btn';
  document.querySelectorAll('.cg-strat-card').forEach(c => c.style.display = 'none');
  const card = document.getElementById('cg-card-' + strat);
  if (card) card.style.display = '';
}

function calcVS() {
  const getState = (id) => parseFloat((document.getElementById(id)||{value:'0'}).value.split('|')[0]) / 100 || 0;
  const getJackpot = (id) => parseFloat((document.getElementById(id)||{value:'342000000'}).value) || 342000000;
  const getStrat = (id) => (document.getElementById(id)||{value:'parkdraw'}).value;
  // Federal and state tax both apply to the full cash amount (they don't stack multiplicatively).
  const calcNet = (jackpot, stateR) => { const cash = jackpot * CASH_R; return cash - prizeTax(cash, stateR); };
  const jA = getJackpot('vs-jackpot-a'), srA = getState('vs-state-a'), sA = getStrat('vs-strat-a');
  const jB = getJackpot('vs-jackpot-b'), srB = getState('vs-state-b'), sB = getStrat('vs-strat-b');
  const netA = calcNet(jA, srA), netB = calcNet(jB, srB);
  const fmtM = n => n >= 1e9 ? '$' + (n/1e9).toFixed(2)+'B' : '$' + (n/1e6).toFixed(1)+'M';
  const setT = (id,v) => { const e=document.getElementById(id); if(e) e.textContent=v; };
  setT('vs-net-a', fmtM(netA)); setT('vs-net-b', fmtM(netB));
  const diff = netA - netB;
  const winnerEl = document.getElementById('vs-winner');
  if (winnerEl) {
    if (Math.abs(diff) < 1000) winnerEl.textContent = '⚖️ Too close to call';
    else winnerEl.textContent = (diff > 0 ? 'Scenario A wins by ' : 'Scenario B wins by ') + fmtM(Math.abs(diff));
  }
  window.track('calc_lump_vs_annuity', {});
}

// ══════════════════════════════════════════════════════════════════════════════
// RANDOM GENERATOR PAGE
// ══════════════════════════════════════════════════════════════════════════════
function generateTrueRandom() {
  const gameEl   = document.getElementById('rng-game');
  const game     = gameEl ? gameEl.value : 'pb';
  const isPb     = game === 'pb';
  const maxBall  = isPb ? 69 : 70;
  const maxSpecial = isPb ? 26 : 24; // Mega Ball 1–24 since 2025-04-08
  const mainCount  = 5;
  const setsEl  = document.getElementById('rng-count');
  const sets    = Math.max(1, Math.min(50, parseInt(setsEl?.value) || 1));
  const noConsec    = !!document.getElementById('rng-no-consec')?.checked;
  const spreadDec   = !!document.getElementById('rng-no-all-same-decade')?.checked;

  // Crypto-secure unbiased integer in [0, n)
  const rand = (n) => {
    const arr = new Uint32Array(1);
    const bound = Math.floor(0xFFFFFFFF / n) * n;
    let v;
    do { crypto.getRandomValues(arr); v = arr[0]; } while (v >= bound);
    return v % n;
  };

  const drawSet = () => {
    // Up to 40 attempts to satisfy constraints
    for (let attempt = 0; attempt < 40; attempt++) {
      const pool = Array.from({ length: maxBall }, (_, i) => i + 1);
      const picked = [];
      for (let i = 0; i < mainCount; i++) {
        const idx = rand(pool.length);
        picked.push(pool.splice(idx, 1)[0]);
      }
      picked.sort((a, b) => a - b);
      if (noConsec) {
        let ok = true;
        for (let i = 1; i < picked.length; i++) if (picked[i] - picked[i - 1] === 1) { ok = false; break; }
        if (!ok) continue;
      }
      if (spreadDec) {
        const decades = new Set(picked.map(n => Math.floor((n - 1) / 10)));
        if (decades.size < 3) continue; // need at least 3 different decades
      }
      return picked;
    }
    // Fallback — return last unfiltered set (extremely unlikely)
    const pool = Array.from({ length: maxBall }, (_, i) => i + 1);
    const picked = [];
    for (let i = 0; i < mainCount; i++) { const idx = rand(pool.length); picked.push(pool.splice(idx, 1)[0]); }
    return picked.sort((a, b) => a - b);
  };

  const out = document.getElementById('rng-output');
  if (!out) { console.warn('rng-output element missing'); return; }
  out.innerHTML = '';

  const allSets = [];
  for (let s = 0; s < sets; s++) {
    const picks   = drawSet();
    let special = rand(maxSpecial) + 1;
    while (picks.includes(special)) special = rand(maxSpecial) + 1; // no repeat of a white ball
    allSets.push({ picks, special });

    const specialColor = isPb ? '#dc3232' : '#ffb300';
    const specialLabel = isPb ? 'PB' : 'MB';
    const ballHTML = picks.map(n =>
      `<span style="display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;background:var(--bg);border:2px solid var(--accent);font-weight:700;font-size:14px;color:var(--accent);font-family:'IBM Plex Mono',monospace">${n}</span>`
    ).join('');
    const specHTML = `<span style="display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:50%;background:${specialColor};color:#fff;font-weight:700;font-size:14px;font-family:'IBM Plex Mono',monospace">${special}</span><span style="font-size:10px;color:var(--text3);margin-left:4px;align-self:center">${specialLabel}</span>`;

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:6px;align-items:center;flex-wrap:wrap;padding:8px 10px;background:var(--bg2);border:1px solid var(--border);border-radius:8px;width:100%';
    row.innerHTML = `<span style="font-size:11px;color:var(--text3);font-family:'IBM Plex Mono',monospace;min-width:32px">#${s + 1}</span>${ballHTML}<span style="width:8px"></span>${specHTML}`;
    out.appendChild(row);
  }

  // Keep last single pick for "Test vs History" compatibility
  window._lastRng = { game, numbers: allSets[0].picks, special: allSets[0].special, ts: new Date().toISOString() };
  window._lastRngSets = allSets.map(s => ({ game, numbers: s.picks, special: s.special }));
  if (typeof window.track === 'function') window.track('rng_generate', { game, sets });
}

function testRngVsHistory() {
  if (!window._lastRng) { showToast('Generate numbers first'); return; }
  const { game, numbers, special } = window._lastRng;
  const gameKey = game === 'pb' ? 'powerball' : 'megamillions';
  let best = 0, bestDate = '', totalMatches = 0;
  DD.filter(d => d.game === gameKey).forEach(d => {
    const nums = d.picks || [];
    const spBall = d.pb;  // special_ball stored as d.pb for all games
    const mainMatch = numbers.filter(n => nums.includes(n)).length;
    const specMatch = spBall === special ? 1 : 0;
    const score = mainMatch + specMatch;
    if (score > best) { best = score; bestDate = d.date; }
    if (mainMatch >= 3) totalMatches++;
  });
  const resultEl = document.getElementById('rng-history-result');
  if (resultEl) {
    resultEl.innerHTML = `<strong>Best historical match:</strong> ${best}/6 balls (draw ${bestDate || 'none'}) · Draws with 3+ main matches: ${totalMatches}`;
    resultEl.style.display = '';
  }
}

function saveRngResults() {
  if (!window._lastRng) { showToast('Generate numbers first'); return; }
  let picks = [];
  try { picks = JSON.parse(localStorage.getItem('coldnumbers_saved_picks') || '[]'); } catch(e) {}
  const { game, numbers, special } = window._lastRng;
  picks.push({ game, numbers: numbers.join('-') + ' | ' + special, ts: new Date().toISOString() });
  localStorage.setItem('coldnumbers_saved_picks', JSON.stringify(picks));
  renderSavedPicks();
  showToast('Pick saved!');
}

function clearSavedRng() {
  if (!confirm('Clear all saved picks?')) return;
  localStorage.removeItem('coldnumbers_saved_picks');
  renderSavedPicks();
  showToast('Saved picks cleared');
}

// ══════════════════════════════════════════════════════════════════════════════
// SCANNER PAGE — OCR MODE + CONFIRM + RETRY + CHECK ALL
// ══════════════════════════════════════════════════════════════════════════════
let _ocrMode = 'tesseract';

function setOcrMode(mode) {
  _ocrMode = mode;
  const tessBtn = document.getElementById('ocr-mode-tess');
  const gemBtn = document.getElementById('ocr-mode-gemini');
  if (tessBtn) tessBtn.className = mode === 'tesseract' ? 'btn btn-primary' : 'btn btn-outline';
  if (gemBtn) gemBtn.className = mode === 'gemini' ? 'btn btn-primary' : 'btn btn-outline';
  showToast('OCR mode: ' + (mode === 'tesseract' ? 'Tesseract (free in-browser)' : 'Gemini Vision'));
}

// ── Ticket OCR ────────────────────────────────────────────────────────────────
// Reads the plays off a ticket photo and fills the manual-entry rows so the
// user can check them. Tesseract runs in the browser (free); Gemini mode calls
// the scan-ticket edge function (signed-in users only).
const _MONTHS = { JAN:1,FEB:2,MAR:3,APR:4,MAY:5,JUN:6,JUL:7,AUG:8,SEP:9,OCT:10,NOV:11,DEC:12 };
function parseTicketDate(text) {
  const t = String(text || '').toUpperCase();
  let m = t.match(/\b(0?[1-9]|1[0-2])[\/\-.](0?[1-9]|[12]\d|3[01])[\/\-.](\d{2}|\d{4})\b/);
  if (m) { const y = m[3].length === 2 ? 2000 + +m[3] : +m[3]; return `${y}-${String(+m[1]).padStart(2,'0')}-${String(+m[2]).padStart(2,'0')}`; }
  m = t.match(/\b(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)[A-Z]*\.?\s*(\d{1,2})[,\s]+(\d{2}|\d{4})\b/);
  if (m) { const y = m[3].length === 2 ? 2000 + +m[3] : +m[3]; return `${y}-${String(_MONTHS[m[1]]).padStart(2,'0')}-${String(+m[2]).padStart(2,'0')}`; }
  return null;
}
function parseTicketText(text, game) {
  const maxW = game === 'mm' ? 70 : 69, maxS = game === 'mm' ? 24 : 26;
  const plays = [];
  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.replace(/[Oo](?=\d)|(?<=\d)[Oo]/g, '0');          // common OCR O→0 slip
    if (/\$|\d{1,2}:\d{2}|\d+[\/.]\d+[\/.]\d+/.test(line)) continue;    // skip prices, times, dates
    const nums = (line.match(/\b\d{1,2}\b/g) || []).map(Number);
    if (nums.length < 6) continue;
    const six = nums.slice(-6), white = six.slice(0, 5), sp = six[5];
    if (new Set(white).size !== 5 || white.some(n => n < 1 || n > maxW) || sp < 1 || sp > maxS) continue;
    plays.push({ white: white.sort((a, b) => a - b), special: sp });
    if (plays.length === 5) break;
  }
  return plays;
}
function _fillManualRows(plays) {
  for (let row = 1; row <= 5; row++) {
    const inputs = [...document.querySelectorAll('.mnum-' + row)];
    const spEl = document.querySelector('.mpb-' + row);
    const p = plays[row - 1];
    inputs.forEach((el, k) => { el.value = p ? p.white[k] : ''; });
    if (spEl) spEl.value = p && p.special ? p.special : '';
  }
}
async function handleScan(input) {
  if (!input.files || !input.files[0]) return;
  const file = input.files[0];
  const game = _scanGame === 'mm' ? 'mm' : 'pb';
  const scanZone = document.getElementById('scanZone');
  const prog = document.getElementById('scanProg'), status = document.getElementById('scanStatus'), bar = document.getElementById('scanBar');
  const say = (msg, pct) => { if (status) status.textContent = msg; if (bar && pct != null) bar.style.width = pct + '%'; };
  if (scanZone) scanZone.className = 'scan-zone scanning';
  if (prog) prog.style.display = '';
  say('Reading image…', 5);
  const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = e => res(e.target.result); r.onerror = rej; r.readAsDataURL(file); });
  const preview = document.getElementById('ticketPreview');
  if (preview) { preview.src = dataUrl; preview.style.display = 'block'; }
  let plays = [], drawDate = null, note = '';
  try {
    if (_ocrMode === 'gemini') {
      say('Sending to Gemini…', 30);
      const { data, error } = await _supa.functions.invoke('scan-ticket', {
        body: { image_base64: dataUrl.split(',')[1], image_mime: file.type || 'image/jpeg', game: game === 'mm' ? 'megamillions' : 'powerball' }
      });
      if (error) { let msg = error.message; try { msg = (await error.context.json()).error || msg; } catch (_) {} throw new Error(msg); }
      plays = (data.scans || []).map(sc => ({ white: sc.white, special: sc.special }));
      drawDate = data.draw_date || null; note = data.note || '';
    } else {
      if (typeof Tesseract === 'undefined') {
        say('Loading the ticket reader…', 15);
        try { await cnLoadScript(CN_LIBS.tesseract); } catch (_) {}
      }
      if (typeof Tesseract === 'undefined') throw new Error('The in-browser OCR library did not load — try again or use Gemini.');
      const { data } = await Tesseract.recognize(dataUrl, 'eng', { logger: m => { if (m.status === 'recognizing text') say('Recognizing text…', 20 + Math.round(m.progress * 75)); } });
      plays = parseTicketText(data.text, game);
      drawDate = parseTicketDate(data.text);
    }
    say(plays.length ? `Found ${plays.length} play${plays.length > 1 ? 's' : ''}` : 'No plays recognized', 100);
  } catch (e) {
    say('Scan failed: ' + e.message, 100);
    showToast('Scan failed — enter the numbers below by hand', 4000);
  }
  if (scanZone) scanZone.className = 'scan-zone';
  if (plays.length) _fillManualRows(plays);
  const dateEl = document.getElementById('manual-draw-date');
  if (dateEl && drawDate) dateEl.value = drawDate;
  const confirmBox = document.getElementById('ocrConfirm'), detected = document.getElementById('ocrDetectedNums');
  if (confirmBox && detected) {
    detected.innerHTML = plays.length
      ? plays.map((p, i) => `<span style="font-family:'IBM Plex Mono',monospace;font-size:12px;padding:3px 7px;border:1px solid var(--border);border-radius:6px">#${i + 1} ${p.white.join('-')} | ${p.special ?? '?'}</span>`).join('') +
        `<div style="width:100%;font-size:11px;color:var(--text3);margin-top:4px">${drawDate ? 'Draw date read from ticket: ' + drawDate + '. ' : ''}${note} Numbers were copied into Manual Entry below — fix any mistakes there.</div>`
      : `<span style="font-size:12px;color:var(--text3)">Couldn't read any plays from this photo. Try a flatter, brighter photo, switch OCR mode, or type them in below.</span>`;
    confirmBox.style.display = '';
  }
}

function confirmOcr() {
  const ocrPanel = document.getElementById('ocrConfirm');
  if (ocrPanel) ocrPanel.style.display = 'none';
  checkAllManual();
  window.track('scan_ticket', { game: _scanGame, matched: 0 });
}

function retryOcr() {
  const scanFile = document.getElementById('scanFile');
  if (scanFile) { scanFile.value = ''; scanFile.click(); }
}


// ══════════════════════════════════════════════════════════════════════════════
// SCHEMA PAGE — COPY CODE BLOCK
// ══════════════════════════════════════════════════════════════════════════════
function copyBlock(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const isSuggestedBalls = id === 'suggestedBalls' || id === 'mm-suggestedBalls';
  navigator.clipboard.writeText(el.textContent).then(() => {
    showToast('Copied to clipboard!');
    if (isSuggestedBalls) window.track('copy_numbers', { source: 'analyzer' });
  }).catch(() => {
    // Fallback
    const ta = document.createElement('textarea');
    ta.value = el.textContent;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    showToast('Copied!');
    if (isSuggestedBalls) window.track('copy_numbers', { source: 'analyzer' });
  });
}


// ══════════════════════════════════════════════════════════════════════════════
// PRIMES SET (shared by PB and MM custom boost)
// ══════════════════════════════════════════════════════════════════════════════
const PRIMES = new Set([2,3,5,7,11,13,17,19,23,29,31,37,41,43,47,53,59,61,67,71]);

// ══════════════════════════════════════════════════════════════════════════════
// PB ANALYZER — STATE
// ══════════════════════════════════════════════════════════════════════════════
const activeMetrics = new Set(['temp', 'hum', 'pres', 'wind', 'hot', 'cold', 'pairs', 'sumprox']);
const ALL_METRICS = ['temp','hum','pres','wind','wdir','cond','moon','hot','cold','pairs','trips','sumprox'];
const weights = { temp: 20, hum: 15, pres: 15, wind: 10, wdir: 5, cond: 5, moon: 0, hot: 10, cold: 10, pairs: 8, trips: 2, sumprox: 15 };
const customMetrics = [];

// ══════════════════════════════════════════════════════════════════════════════
// PB ANALYZER — METRIC TOGGLE + WEIGHT CONTROLS
// ══════════════════════════════════════════════════════════════════════════════
function toggleMetric(btn) {
  const METRICS = ALL_METRICS;
  const m = btn.dataset.metric;
  if (activeMetrics.has(m)) {
    activeMetrics.delete(m);
    btn.classList.remove('on'); btn.setAttribute('aria-pressed', 'false');
    // Redistribute this metric's weight to active others
    const freed = weights[m];
    weights[m] = 0;
    const active = METRICS.filter(x => activeMetrics.has(x));
    if (active.length > 0 && freed > 0) {
      const share = Math.floor(freed / active.length);
      active.forEach(x => { weights[x] += share; });
      const remainder = freed - share * active.length;
      if (remainder > 0) weights[active[0]] += remainder;
    }
  } else {
    activeMetrics.add(m);
    btn.classList.add('on'); btn.setAttribute('aria-pressed', 'true');
    // Steal share from other active metrics to give this one 25%
    const target = 25;
    const active = METRICS.filter(x => activeMetrics.has(x) && x !== m);
    const steal = Math.min(target, active.reduce((s, x) => s + weights[x], 0));
    if (active.length > 0 && steal > 0) {
      const perOther = Math.floor(steal / active.length);
      active.forEach(x => { weights[x] = Math.max(0, weights[x] - perOther); });
    }
    weights[m] = steal;
    // Normalize to 100
    const total = METRICS.reduce((s, x) => s + weights[x], 0);
    if (total !== 100 && total > 0) {
      const scale = 100 / total;
      METRICS.forEach(x => { weights[x] = Math.round(weights[x] * scale); });
      const adj = 100 - METRICS.reduce((s, x) => s + weights[x], 0);
      if (adj !== 0) weights[METRICS.find(x => weights[x] + adj >= 0)] += adj;
    }
  }
  METRICS.forEach(x => {
    const sl = document.getElementById('r-' + x), lb = document.getElementById('w-' + x);
    if (sl) sl.value = weights[x]; if (lb) lb.textContent = weights[x] + '%';
  });
  updateWeightBar();
  // Re-run analyzer so toggle changes actually shift the picks
  if (document.body.dataset.page === 'analyzer' && typeof debouncedRerun === 'function') {
    debouncedRerun();
  }
}

function updateWeight(m, v) {
  const METRICS = ALL_METRICS;
  const newVal = Math.max(0, Math.min(100, +v));
  const delta = newVal - weights[m];
  if (delta === 0) return;
  // Zero-sum: distribute -delta proportionally across other active metrics
  const others = METRICS.filter(x => x !== m);
  const othersTotal = others.reduce((s, x) => s + weights[x], 0);
  if (othersTotal > 0) {
    others.forEach(x => {
      weights[x] = Math.max(0, Math.round(weights[x] - delta * (weights[x] / othersTotal)));
    });
    // Fix rounding drift so total stays exactly 100
    const total = newVal + others.reduce((s, x) => s + weights[x], 0);
    const drift = 100 - total;
    if (drift !== 0) {
      const fix = others.find(x => weights[x] + drift >= 0 && weights[x] + drift <= 100);
      if (fix) weights[fix] += drift;
    }
  } else {
    // All others are 0 — distribute remaining evenly
    const share = Math.round((100 - newVal) / others.length);
    others.forEach(x => { weights[x] = share; });
  }
  weights[m] = newVal;
  // Update all slider elements and labels
  METRICS.forEach(x => {
    const sl = document.getElementById('r-' + x);
    const lb = document.getElementById('w-' + x);
    if (sl) sl.value = weights[x];
    if (lb) lb.textContent = weights[x] + '%';
  });
  updateWeightBar();
  // Re-run analyzer so slider changes actually shift the picks
  if (document.body.dataset.page === 'analyzer' && typeof debouncedRerun === 'function') {
    debouncedRerun();
  }
}

function updateWeightBar() {
  const total = ALL_METRICS.reduce((s, m) => s + (weights[m] || 0), 0) || 1;
  ALL_METRICS.forEach(m => {
    const el = document.getElementById('wbar-' + m);
    if (el) el.style.width = Math.round((weights[m] || 0) / total * 100) + '%';
  });
  const el = document.getElementById('weightTotal');
  if (el) { el.textContent = 'Total: ' + total + '%'; el.style.color = total > 100 ? 'var(--danger)' : total === 100 ? 'var(--ok)' : 'var(--warn)'; }
}

function toggleMetricsHelp() {
  const p = document.getElementById('metricsHelp'), b = document.getElementById('metricsHelpBtn');
  if (!p || !b) return;
  const open = p.style.display === 'none';
  p.style.display = open ? 'block' : 'none';
  b.setAttribute('aria-expanded', open);
  b.textContent = open ? '✕ Close' : '❓ How it works';
}

const CM_ICONS = { high:'📈',low:'📉',mid:'⚖️',odd:'🔢',even:'🔢',prime:'🔵',seed:'🍀',lucky:'⭐',due:'⏰',hot:'🔥',cold:'🧊',sumrange:'🎯',noconsec:'🚫',decay:'📊',unpop:'🧮' };
const CM_HINTS = {
  decay:    'Fast-decay recency: weights recent draws far more heavily (half-life scales with intensity). Backtested winner.',
  unpop:    'Anti-popular (EV): damps common picks (1–31 birthdays, lucky 7/11/13, round numbers) and lifts 37+ to reduce jackpot-sharing.',
  high:     'Boosts numbers 47–69 by up to 50% at max intensity.',
  low:      'Boosts numbers 1–23 by up to 50% at max intensity.',
  mid:      'Boosts numbers 24–46. The "middle range" bias.',
  odd:      'Boosts all odd numbers.',
  even:     'Boosts all even numbers.',
  prime:    'Boosts prime numbers (2,3,5,7,11…).',
  seed:     'Boosts numbers near your seed (±5). Enter a number in the label.',
  lucky:    'Enter your lucky numbers in the Label field (comma-separated, e.g. "7,14,22"). Those exact balls get a boost.',
  due:      'Boosts ball numbers that have NOT appeared in recent draws. Intensity controls drought sensitivity (high = longer drought required to boost).',
  hot:      'Boosts numbers that have appeared frequently in recent draws (last intensity×5 draws). Higher intensity = smaller, hotter window.',
  cold:     'Boosts numbers that have appeared rarely in recent draws. Intensity controls window size. Pairs well with gambler&#39;s-fallacy plays.',
  sumrange: 'Enter target sum range in Label as "min-max" (e.g. "130-170"). Numbers near the target midpoint get a score boost. Historical PB mean ≈ 150.',
  noconsec: 'Penalizes ball numbers whose immediate neighbors (±1) also score high, reducing the chance of consecutive-number combos appearing.'
};

function updateCMHint() {
  const t = document.getElementById('cm-type').value;
  const el = document.getElementById('cm-hint');
  if (el) el.innerHTML = CM_HINTS[t] || '';
  // Show/hide label placeholder guidance
  const nameInput = document.getElementById('cm-name');
  if (nameInput) {
    const placeholders = {lucky:'Lucky nums: 7,14,22,…', sumrange:'Sum range: 130-170', due:'Label (optional)', hot:'Label (optional)', cold:'Label (optional)', noconsec:'Label (optional)'};
    nameInput.placeholder = placeholders[t] || 'Label (optional)';
  }
}

function addCustomMetric() {
  const type = document.getElementById('cm-type').value;
  const intensity = Math.min(10, Math.max(1, +document.getElementById('cm-intensity').value || 5));
  const name = document.getElementById('cm-name').value.trim() || type;
  customMetrics.push({ name, type, intensity });
  renderCustomMetrics();
  document.getElementById('cm-name').value = '';
  showToast('Added: ' + name);
}

function renderCustomMetrics() {
  const el = document.getElementById('custom-list');
  if (!el) return;
  el.innerHTML = customMetrics.map((m, i) => `
    <div style="display:flex;align-items:center;gap:7px;padding:6px 0;border-bottom:1px solid var(--border)">
      <span style="font-size:14px">${CM_ICONS[m.type] || '⭐'}</span>
      <span style="flex:1;font-size:12px;color:var(--text)">${m.name}</span>
      <span class="chip chip-warn" style="font-size:9px">×${m.intensity}</span>
      <button onclick="customMetrics.splice(${i},1);renderCustomMetrics()" style="background:none;border:none;color:var(--danger);cursor:pointer;font-size:15px;padding:0 2px" aria-label="Remove ${m.name}">×</button>
    </div>`).join('');
}

function _getRecentDrawNumbers(game, windowDraws) {
  // Returns a map: ball -> count in last windowDraws draws for the given game
  const draws = DD.filter(d => d.game === game && d.picks).slice(0, windowDraws);
  const counts = {};
  draws.forEach(d => d.picks.forEach(n => counts[n] = (counts[n]||0)+1));
  return { draws, counts };
}

function applyCustomBoost(scores, maxBall = 69) {
  customMetrics.forEach(m => {
    const boost = 1 + (m.intensity / 10) * 0.5;  // 1.0–1.5×
    const seed = parseInt(m.name) || 0;

    if (m.type === 'lucky') {
      // Parse comma-separated lucky numbers from name field
      const luckyNums = new Set(m.name.split(',').map(s=>parseInt(s.trim())).filter(n=>n>=1&&n<=maxBall));
      for (let n=1; n<=maxBall; n++) { if (luckyNums.has(n)) scores[n] *= boost; }

    } else if (m.type === 'due') {
      // Numbers not seen in the last (11-intensity)*8 draws get a boost
      const threshold = (11 - m.intensity) * 8;  // intensity 10 → 8 draws, intensity 1 → 80 draws
      const { draws } = _getRecentDrawNumbers('powerball', threshold);
      const seenRecently = new Set();
      draws.forEach(d => d.picks.forEach(n => seenRecently.add(n)));
      for (let n=1; n<=maxBall; n++) { if (!seenRecently.has(n)) scores[n] *= boost; }

    } else if (m.type === 'hot') {
      // Numbers appearing most in recent window get boosted
      const window = m.intensity * 5;
      const { counts } = _getRecentDrawNumbers('powerball', window);
      const maxCount = Math.max(1, ...Object.values(counts));
      for (let n=1; n<=maxBall; n++) {
        const freq = (counts[n]||0) / maxCount;
        if (freq > 0.5) scores[n] *= (1 + freq * (m.intensity/10) * 0.5);
      }

    } else if (m.type === 'cold') {
      // Numbers appearing least in recent window get boosted
      const window = m.intensity * 5;
      const { counts } = _getRecentDrawNumbers('powerball', window);
      const maxCount = Math.max(1, ...Object.values(counts));
      for (let n=1; n<=maxBall; n++) {
        const freq = (counts[n]||0) / maxCount;
        if (freq < 0.3) scores[n] *= boost;
      }

    } else if (m.type === 'sumrange') {
      // Parse "min-max" from name; proxy boost numbers near the midpoint/5
      const parts = m.name.match(/(\d+)\s*[-–]\s*(\d+)/);
      if (parts) {
        const targetMid = (parseInt(parts[1]) + parseInt(parts[2])) / 2;
        const targetAvgBall = targetMid / 5;  // 5 picks
        const tolerance = (parseInt(parts[2]) - parseInt(parts[1])) / 10;
        for (let n=1; n<=maxBall; n++) {
          if (Math.abs(n - targetAvgBall) <= tolerance) scores[n] *= boost;
        }
      }

    } else if (m.type === 'noconsec') {
      // Slight penalty for balls whose neighbors ±1 also have high scores (applied after base scoring)
      // Re-read current scores to find top quartile
      const vals = Object.values(scores).sort((a,b)=>b-a);
      const threshold_score = vals[Math.floor(vals.length*0.25)] || 0;
      const penalty = 1 / boost;
      for (let n=1; n<=maxBall; n++) {
        const hasConsecHighNeighbor = (scores[n-1] && scores[n-1] >= threshold_score) ||
                                      (scores[n+1] && scores[n+1] >= threshold_score);
        if (hasConsecHighNeighbor && scores[n] >= threshold_score) scores[n] *= penalty;
      }

    } else if (m.type === 'decay') {
      // Fast-decay recency: weight recent draws by 0.5^(i/halfLife)
      const halfLife = Math.max(8, m.intensity * 8);
      const recent = DD.filter(d => d.game === 'powerball' && d.picks && d.picks.length === 5);
      const dw = {};
      recent.forEach((d, i) => {
        const w = Math.pow(0.5, i / halfLife);
        d.picks.forEach(n => { if (n>=1 && n<=maxBall) dw[n] = (dw[n]||0) + w; });
      });
      const dvals = Object.values(dw);
      if (dvals.length) {
        const mx = Math.max(...dvals), mn = Math.min(...dvals), r = (mx-mn)||1;
        for (let n=1; n<=maxBall; n++) scores[n] *= 1 + (m.intensity/10) * ((dw[n]||mn)-mn)/r;
      }

    } else if (m.type === 'unpop') {
      // Anti-popular (EV): damp commonly-played numbers, lift 37+
      const lucky = new Set([7,11,13,21,3]);
      const damp = 1 - (m.intensity/10) * 0.4;
      const lift = 1 + (m.intensity/10) * 0.4;
      for (let n=1; n<=maxBall; n++) {
        if (n <= 31) scores[n] *= damp;
        if (lucky.has(n)) scores[n] *= damp;
        if (n % 10 === 0 || (n % 11 === 0 && n <= 99)) scores[n] *= damp;
        if (n >= 37) scores[n] *= lift;
      }

    } else {
      // Original types: high, low, mid, odd, even, prime, seed
      for (let n = 1; n <= maxBall; n++) {
        let match = false;
        if (m.type === 'high' && n >= 47) match = true;
        else if (m.type === 'low' && n <= 23) match = true;
        else if (m.type === 'mid' && n >= 24 && n <= 46) match = true;
        else if (m.type === 'odd' && n % 2 === 1) match = true;
        else if (m.type === 'even' && n % 2 === 0) match = true;
        else if (m.type === 'prime' && PRIMES.has(n)) match = true;
        else if (m.type === 'seed' && seed > 0 && Math.abs(n - seed) <= 5) match = true;
        if (match) scores[n] *= boost;
      }
    }
  });
}

// ══════════════════════════════════════════════════════════════════════════════
// WEIGHT PRESET PROFILES — PB + MM
// ══════════════════════════════════════════════════════════════════════════════

// ── PB Presets — data-driven from real R-values ──
function _applyWeights(newW, METRICS, prefix, bar, run) {
  Object.assign(prefix === '' ? weights : mmWeights, newW);
  METRICS.forEach(x => {
    const sl = document.getElementById((prefix||'r-')+x.replace('r-',''));
    const lb = document.getElementById((prefix==='mm-r-'?'mm-w-':'w-')+x);
    if (sl) sl.value = newW[x]||0;
    if (lb) lb.textContent = (newW[x]||0) + '%';
  });
  if (bar === 'mm') updateMMWeightBar(); else updateWeightBar();
  if (run && typeof runAnalyzer === 'function' && bar !== 'mm') runAnalyzer();
  if (run && typeof runMMAnalyzer === 'function' && bar === 'mm') runMMAnalyzer();
}

function _calcPresetWeights(game, mode, dFrom, dTo, method) {
  // mode: 'equal' | 'signal' (|R|) | 'amplified' (|R|^2)
  const METRICS = ['temp','hum','pres','wind'];
  const result = {};
  if (mode === 'equal') {
    const share = Math.round(100 / METRICS.length);
    METRICS.forEach((m,i) => { result[m] = i === METRICS.length-1 ? 100 - share*(METRICS.length-1) : share; });
    result.moon = 0; result.wdir = 0; result.cond = 0;
    return result;
  }
  const raw = _computeAutoWeights(game, method || 'pearson', dFrom, dTo);
  if (!raw) {
    // fallback: equal
    return _calcPresetWeights(game, 'equal', dFrom, dTo, method);
  }
  if (mode === 'amplified') {
    // square the R-proportions for more contrast
    const sq = {};
    METRICS.forEach(m => { sq[m] = (raw[m] / 100) ** 2; });
    const tot = Object.values(sq).reduce((s,v)=>s+v,0) || 1;
    METRICS.forEach((m,i) => { sq[m] = Math.round(sq[m]/tot*100); });
    const drift = 100 - METRICS.reduce((s,m)=>s+sq[m],0);
    if (drift) sq[METRICS[0]] += drift;
    sq.moon = 0; sq.wdir = 0; sq.cond = 0;
    return sq;
  }
  // 'signal' = linear |R| (same as _computeAutoWeights)
  raw.moon = 0; raw.wdir = 0; raw.cond = 0;
  return raw;
}

function applyPBPreset(preset) {
  const modeMap = { balanced:'equal', weather:'amplified', stats:'signal' };
  const mode = modeMap[preset] || 'equal';
  const newW = _calcPresetWeights('powerball', mode, pbDateFrom, pbDateTo, pbRMethod);
  // Merge with bonus defaults for keys the preset doesn't cover
  ALL_METRICS.forEach(m => { if (newW[m] == null) newW[m] = PB_DEFAULT_WEIGHTS[m] || 0; });
  Object.assign(weights, newW);
  ALL_METRICS.forEach(x => {
    const sl = document.getElementById('r-' + x), lb = document.getElementById('w-' + x);
    if (sl) sl.value = weights[x]||0; if (lb) lb.textContent = (weights[x]||0) + '%';
  });
  updateWeightBar();
  document.querySelectorAll('.pb-wpreset-btn').forEach(b => {
    b.className = b.id === 'pb-wpreset-' + preset
      ? 'btn btn-primary btn-sm pb-wpreset-btn'
      : 'btn btn-outline btn-sm pb-wpreset-btn';
  });
  if (typeof runAnalyzer === 'function') runAnalyzer();
}

async function savePBWeightPreset() {
  const name = prompt('Name this preset:');
  if (!name || !name.trim()) return;
  const preset = { name: name.trim(), game:'powerball', weights:{...weights}, custom_metrics:customMetrics.map(m=>({...m})), saved_at: new Date().toISOString() };
  const key = 'llm_presets_pb';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  existing.unshift(preset);
  localStorage.setItem(key, JSON.stringify(existing.slice(0,20)));
  // Save to Supabase if logged in
  try {
    const { data: { session } } = await _supa.auth.getSession();
    if (session?.user) {
      await _supa.from('metric_presets').insert({ user_id:session.user.id, name:preset.name, weights:preset.weights, custom_metrics:preset.custom_metrics });
    }
  } catch(e) { /* non-critical */ }
  renderPBSavedPresets();
  // Mark as custom preset
  document.querySelectorAll('.pb-wpreset-btn').forEach(b => b.className = 'btn btn-outline btn-sm pb-wpreset-btn');
  document.getElementById('pb-wpreset-custom').className = 'btn btn-primary btn-sm pb-wpreset-btn';
  document.getElementById('pb-wpreset-custom').style.pointerEvents = '';
  showToast('Preset "' + name.trim() + '" saved!');
}

function renderPBSavedPresets() {
  const el = document.getElementById('pb-saved-presets');
  if (!el) return;
  const presets = JSON.parse(localStorage.getItem('llm_presets_pb') || '[]');
  if (!presets.length) { el.innerHTML = ''; return; }
  el.innerHTML = '<div style="height:1px;background:var(--border);margin:6px 0"></div>' +
    presets.slice(0,5).map((p,i) => `
    <div style="display:flex;align-items:center;gap:6px;padding:3px 0">
      <span style="flex:1;font-size:11px;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${p.name}</span>
      <button onclick="loadPBPreset(${i})" class="btn btn-outline" style="font-size:10px;padding:2px 7px;flex-shrink:0">Load</button>
      <button onclick="deletePBPreset(${i})" style="background:none;border:none;color:var(--danger);cursor:pointer;font-size:14px;padding:0 2px;flex-shrink:0">×</button>
    </div>`).join('');
}

function loadPBPreset(i) {
  const presets = JSON.parse(localStorage.getItem('llm_presets_pb') || '[]');
  const p = presets[i];
  if (!p) return;
  Object.assign(weights, p.weights);
  ['temp','hum','pres','wind'].forEach(m => {
    const sl = document.getElementById('r-' + m), lb = document.getElementById('w-' + m);
    if (sl) sl.value = weights[m];
    if (lb) lb.textContent = weights[m] + '%';
  });
  updateWeightBar();
  if (p.custom_metrics) { customMetrics.splice(0, customMetrics.length, ...p.custom_metrics); renderCustomMetrics(); }
  document.querySelectorAll('.pb-wpreset-btn').forEach(b => b.className = 'btn btn-outline btn-sm pb-wpreset-btn');
  document.getElementById('pb-wpreset-custom').className = 'btn btn-primary btn-sm pb-wpreset-btn';
  showToast('Loaded: ' + p.name);
  if (typeof runAnalyzer === 'function') runAnalyzer();
}

function deletePBPreset(i) {
  const presets = JSON.parse(localStorage.getItem('llm_presets_pb') || '[]');
  presets.splice(i, 1);
  localStorage.setItem('llm_presets_pb', JSON.stringify(presets));
  renderPBSavedPresets();
}

// ── MM Presets — data-driven from real R-values ──
function applyMMPreset(preset) {
  const METRICS = ['temp','hum','pres','wind','moon','wdir','cond'];
  const modeMap = { balanced:'equal', weather:'amplified', stats:'signal' };
  const mode = modeMap[preset] || 'equal';
  const newW = _calcPresetWeights('megamillions', mode, mmDateFrom, mmDateTo, mmRMethod);
  Object.assign(mmWeights, newW);
  METRICS.forEach(x => {
    const sl = document.getElementById('mm-r-' + x), lb = document.getElementById('mm-w-' + x);
    if (sl) sl.value = mmWeights[x]||0; if (lb) lb.textContent = (mmWeights[x]||0) + '%';
  });
  updateMMWeightBar();
  document.querySelectorAll('.mm-wpreset-btn').forEach(b => {
    b.className = b.id === 'mm-wpreset-' + preset
      ? 'btn btn-primary btn-sm mm-wpreset-btn'
      : 'btn btn-outline btn-sm mm-wpreset-btn';
  });
  if (typeof runMMAnalyzer === 'function') runMMAnalyzer();
}

async function saveMMWeightPreset() {
  const name = prompt('Name this preset:');
  if (!name || !name.trim()) return;
  const preset = { name: name.trim(), game:'megamillions', weights:{...mmWeights}, custom_metrics:mmCustomMetrics.map(m=>({...m})), saved_at: new Date().toISOString() };
  const key = 'llm_presets_mm';
  const existing = JSON.parse(localStorage.getItem(key) || '[]');
  existing.unshift(preset);
  localStorage.setItem(key, JSON.stringify(existing.slice(0,20)));
  try {
    const { data: { session } } = await _supa.auth.getSession();
    if (session?.user) {
      await _supa.from('metric_presets').insert({ user_id:session.user.id, name:preset.name, weights:preset.weights, custom_metrics:preset.custom_metrics });
    }
  } catch(e) { /* non-critical */ }
  renderMMSavedPresets();
  document.querySelectorAll('.mm-wpreset-btn').forEach(b => b.className = 'btn btn-outline btn-sm mm-wpreset-btn');
  document.getElementById('mm-wpreset-custom').className = 'btn btn-primary btn-sm mm-wpreset-btn';
  document.getElementById('mm-wpreset-custom').style.pointerEvents = '';
  showToast('Preset "' + name.trim() + '" saved!');
}

function renderMMSavedPresets() {
  const el = document.getElementById('mm-saved-presets');
  if (!el) return;
  const presets = JSON.parse(localStorage.getItem('llm_presets_mm') || '[]');
  if (!presets.length) { el.innerHTML = ''; return; }
  el.innerHTML = '<div style="height:1px;background:var(--border);margin:6px 0"></div>' +
    presets.slice(0,5).map((p,i) => `
    <div style="display:flex;align-items:center;gap:6px;padding:3px 0">
      <span style="flex:1;font-size:11px;color:var(--text);overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${p.name}</span>
      <button onclick="loadMMPreset(${i})" class="btn btn-outline" style="font-size:10px;padding:2px 7px;flex-shrink:0">Load</button>
      <button onclick="deleteMMPreset(${i})" style="background:none;border:none;color:var(--danger);cursor:pointer;font-size:14px;padding:0 2px;flex-shrink:0">×</button>
    </div>`).join('');
}

function loadMMPreset(i) {
  const presets = JSON.parse(localStorage.getItem('llm_presets_mm') || '[]');
  const p = presets[i];
  if (!p) return;
  Object.assign(mmWeights, p.weights);
  ['temp','hum','pres','wind'].forEach(m => {
    const sl = document.getElementById('mm-r-' + m), lb = document.getElementById('mm-w-' + m);
    if (sl) sl.value = mmWeights[m];
    if (lb) lb.textContent = mmWeights[m] + '%';
  });
  updateMMWeightBar();
  if (p.custom_metrics) { mmCustomMetrics.splice(0, mmCustomMetrics.length, ...p.custom_metrics); renderMMCustomMetrics(); }
  document.querySelectorAll('.mm-wpreset-btn').forEach(b => b.className = 'btn btn-outline btn-sm mm-wpreset-btn');
  document.getElementById('mm-wpreset-custom').className = 'btn btn-primary btn-sm mm-wpreset-btn';
  showToast('Loaded: ' + p.name);
  if (typeof runMMAnalyzer === 'function') runMMAnalyzer();
}

function deleteMMPreset(i) {
  const presets = JSON.parse(localStorage.getItem('llm_presets_mm') || '[]');
  presets.splice(i, 1);
  localStorage.setItem('llm_presets_mm', JSON.stringify(presets));
  renderMMSavedPresets();
}

// Apply a preset loaded from Supabase
function applySupabasePreset(weightsObj, game) {
  const w = typeof weightsObj === 'string' ? JSON.parse(weightsObj) : weightsObj;
  if (!w) return;
  if (game === 'megamillions') {
    Object.assign(mmWeights, w);
    ['temp','hum','pres','wind'].forEach(m => {
      const sl = document.getElementById('mm-r-' + m), lb = document.getElementById('mm-w-' + m);
      if (sl) sl.value = mmWeights[m];
      if (lb) lb.textContent = mmWeights[m] + '%';
    });
    updateMMWeightBar();
    showToast('Applied MM preset from cloud');
    if (typeof runMMAnalyzer === 'function') { showPage('analyzerMM'); runMMAnalyzer(); }
  } else {
    Object.assign(weights, w);
    ['temp','hum','pres','wind'].forEach(m => {
      const sl = document.getElementById('r-' + m), lb = document.getElementById('w-' + m);
      if (sl) sl.value = weights[m];
      if (lb) lb.textContent = weights[m] + '%';
    });
    updateWeightBar();
    showToast('Applied PB preset from cloud');
    if (typeof runAnalyzer === 'function') { showPage('analyzer'); runAnalyzer(); }
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════════════════
// DEFAULT PICK — recency decay, half-life 6 (red ball: half-life 12)
// NOTE (2026-07 / re-checked 2026-10-11): the old "m2+ 12.71%, z=+12.3" result
// came from duplicated draw rows. On the cleaned data this method scores at
// chance (1,093 walk-forward draws: avg 0.386 matches vs 0.362 expected,
// z=+1.4; red ball 4.4% vs 3.85%, z=+0.9). It is kept as a deterministic
// default, and it only changes when a new draw is added. See METHOD_SUMMARY.md.
// ══════════════════════════════════════════════════════════════════════════════
function _fmtISO(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
// "Based on draws through …" line shown with the default pick, so it's clear
// why the numbers stay the same between draws.
function _pickAsOfHtml(asOf, game) {
  const next = getNextDrawDateISO(game);
  return `<div style="margin-bottom:6px"><strong>Based on draws through ${_fmtISO(asOf)}.</strong> ` +
    `These numbers only change when a new draw is added — next draw ${_fmtISO(next)}, ` +
    `usually on the site within an hour or two.</div>`;
}
// The special ball (Powerball / Mega Ball) comes from its own drum, so it may
// legally equal a white ball, but repeated numbers like 4·14·21·23·33 + 14 look
// like a mistake. Every generator on the site therefore takes the best-ranked
// special ball that is NOT one of the five white balls.
function pickSpecialAvoiding(ranked, whites, spMax) {
  const used = new Set((whites || []).map(Number));
  const order = (ranked || []).map(Number).filter(n => n >= 1 && n <= spMax);
  for (let n = 1; n <= spMax; n++) if (!order.includes(n)) order.push(n); // fallback: any unused
  const free = order.filter(n => !used.has(n));
  return { special: free[0] || 1, alts: free.slice(1, 4) };
}
function backtestPick(game) {
  const isPB = game === 'powerball';
  const maxBall = isPB ? 69 : 70;
  const spMax   = isPB ? 26 : 24; // Mega Ball 1–24 since 2025-04-08
  const pfx = isPB ? 'ana' : 'mm-ana';
  const g = id => parseFloat(document.getElementById(id)?.value);
  const tgt = {
    temperature_f: g(`${pfx}-temp`), humidity_pct: g(`${pfx}-hum`),
    pressure_inhg: g(`${pfx}-pres`), wind_speed_mph: g(`${pfx}-ws`),
    dew_point_f:   g(`${pfx}-dew`)
  };
  const draws = DD.filter(d => d.game === game && d.picks && d.picks.length === 5);
  if (draws.length < 30) return null;
  const HALF = 6;
  const decay = {};
  draws.forEach((d, i) => {
    const w = Math.pow(0.5, i / HALF);
    d.picks.forEach(n => { if (n>=1 && n<=maxBall) decay[n] = (decay[n]||0) + w; });
  });
  const SIG = { temp:6, hum:10, pres:0.25, wind:6, dew:6 };
  const wx = {};
  draws.forEach(d => {
    if (d.temp == null) return;
    let sc = 0, act = 0;
    const dims = [['temp',d.temp,tgt.temperature_f],['hum',d.hum,tgt.humidity_pct],
                  ['pres',d.pres,tgt.pressure_inhg],['wind',d.ws,tgt.wind_speed_mph],
                  ['dew',d.dew,tgt.dew_point_f]];
    dims.forEach(([k,dv,tv]) => {
      if (dv != null && tv != null && !isNaN(tv)) { const z=(dv-tv)/SIG[k]; sc += Math.exp(-z*z); act++; }
    });
    if (act) { sc/=act; d.picks.forEach(n => { if(n>=1&&n<=maxBall) wx[n]=(wx[n]||0)+sc; }); }
  });
  const normMap = (m) => {
    const vals = Object.values(m); if(!vals.length) return {};
    const mx = Math.max(...vals), mn = Math.min(...vals), r = (mx-mn)||1;
    const o={}; for (const k in m) o[k]=(m[k]-mn)/r; return o;
  };
  // Pure recency: weather (0.3x) and gap-2 spacing removed after backtest showed
  // both reduced match-3+ rate. Keep the wx calc above for the optional weather method.
  const dN = normMap(decay);
  const ranked = Object.keys(dN).map(Number).sort((a,b)=> dN[b]-dN[a]);
  const picks = ranked.slice(0, 5).sort((a,b)=>a-b);
  // Special ball — recency decay, half-life 12 (tests at chance; see note above).
  // Mega Millions only uses draws from the current 1–24 Mega Ball matrix.
  const SP_HALF = 12;
  const sp = {};
  draws.filter(d => isPB || d.iso >= '2025-04-08')
       .forEach((d,i) => { const v=d.pb; if(v>=1&&v<=spMax) sp[v]=(sp[v]||0)+Math.pow(0.5,i/SP_HALF); });
  const spRanked = Object.keys(sp).map(Number).sort((a,b)=> sp[b]-sp[a]);
  const { special, alts: specialAlts } = pickSpecialAvoiding(spRanked, picks, spMax);
  return { picks, special, specialAlts, asOf: draws[0]?.iso || null };
}

// PB ANALYZER — MAIN RUN FUNCTION
// ══════════════════════════════════════════════════════════════════════════════
function runAnalyzer() {
  try {
  // Button feedback
  const rbtn = document.getElementById('runAnalyzerBtn');
  if (rbtn) { rbtn.textContent = '⏳ Running...'; rbtn.disabled = true; }
  const pbDraws = DD.filter(d => d.game === 'powerball');
  const dcEl = document.getElementById('pb-draw-count-temp');
  if (dcEl) dcEl.textContent = pbDraws.length + ' PB draws';

  if (pbDraws.length === 0) {
    showToast('No Powerball data yet — run EF3 draw-feed first');
    const sd = document.getElementById('scoreDesc');
    if (sd) sd.textContent = 'No PB data loaded.';
    return;
  }

  const r = analyzerV2('powerball');
  if (!r) { showToast('Not enough data yet'); return; }

  const bt = backtestPick('powerball');
  const picks   = bt ? bt.picks   : r.picks;
  const special = bt ? bt.special : r.special;
  const { confScore, n, wxCount, usedDraws, binCount, _dbgTop5, _dbgBot3 } = r;
  const pickSum = picks.reduce((s,n)=>s+n,0);
  const col = 'var(--accent)'; // coverage gauge — neutral colour, it is not a strength rating

  // Score ring
  const ring = document.getElementById('scoreCircle');
  if (ring) { ring.style.strokeDashoffset = 289-(289*confScore/100); ring.style.stroke = col; }
  const sv = document.getElementById('scoreVal');
  if (sv) { sv.textContent = confScore + '%'; sv.style.color = col; }

  // Description — say what the numbers are based on and what the gauge means
  const desc = document.getElementById('scoreDesc');
  if (desc) desc.innerHTML = _pickAsOfHtml(bt?.asOf, 'pb') +
    `Gauge = weather coverage: <strong>${wxCount}</strong> of <strong>${n}</strong> draws have draw-night weather. ` +
    `It is not a chance of winning — every Powerball ticket has the same 1 in 292,201,338 jackpot odds, and no method here has beaten random picks in testing (Σ=${pickSum}).`;

  // Debug strip removed for production. (Kept console.log calls for dev tools.)
  const existingDbg = document.getElementById('v4debug');
  if (existingDbg) existingDbg.remove();

  // Balls
  const ballsEl = document.getElementById('suggestedBalls');
  if (ballsEl) ballsEl.innerHTML =
    picks.map(n=>`<div class="ball ball-w" aria-label="Number ${n}">${n}</div>`).join('')+
    `<span style="margin:0 6px;color:var(--text2);font-size:18px">+</span>`+
    `<div class="ball" style="background:var(--danger);color:#fff;border-radius:50%;width:44px;height:44px;display:inline-flex;align-items:center;justify-content:center;font-weight:700;font-size:15px" aria-label="Powerball ${special}">${special}</div>`;

  // Metric breakdown — ALL factors with live R-values
  const rv = window._liveRValues || {};
  const cb = document.getElementById('confBreakdown');
  if (cb) cb.innerHTML = [
    ['🌡️ Temperature','temp','#6ba3ff','1.4×','±5°F bins'],
    ['💧 Humidity','hum','var(--ok)','1.0×','±10% bins'],
    ['⬇️ Pressure','pres','var(--warn)','1.0×','±0.2" bins'],
    ['💨 Wind Speed','wind','#9b5de5','1.2×','±5mph bins'],
    ['🧭 Wind Dir','wdir','#ff9f43','1.3×','circular sin/cos'],
    ['🌥️ Condition','cond','#54a0ff','0.9×','point-biserial'],
    ['🌙 Moon Phase','moon','#f7a8ff','0.8×','ordinal illum%'],
    ['🔥 Hot Numbers','hot','#ff6b6b','—','last 20 draws'],
    ['❄️ Cold/Overdue','cold','#74b9ff','—','gap-length'],
    ['🔗 Lucky Pairs','pairs','#a29bfe','—','co-occurrence'],
    ['🔗 Triplets','trips','#fd79a8','—','triplet co-occur'],
    ['🎯 Sum Proximity','sumprox','#00cec9','—','Gaussian sum fit']
  ].map(([name,key,color,mult,margin])=>{
    const rData = rv[key];
    const rStr = rData && rData.best != null ? (rData.best >= 0.001 ? rData.best.toFixed(4) : '~0') : '—';
    const nStr = rData && rData.n ? rData.n + ' draws' : '';
    const noteStr = rData && rData.note ? rData.note : '';
    const rCol = rData && rData.best >= 0.03 ? 'var(--ok)' : rData && rData.best >= 0.01 ? 'var(--warn)' : 'var(--text3)';
    return `<div style="margin-bottom:6px;padding:5px 8px;background:var(--bg2);border-radius:6px;border:1px solid var(--border)" data-tip="${name}: |R|=${rStr} (${noteStr}). ${margin}. Weight controls how much this factor influences final picks.">
      <div style="display:flex;justify-content:space-between;margin-bottom:2px">
        <span style="font-size:11px;color:var(--text2)">${name} <span style="font-size:8px;opacity:.5">${mult} · ${margin}</span></span>
        <span style="font-size:11px;font-family:'IBM Plex Mono',monospace;color:${color}">${activeMetrics.has(key)?(weights[key]||0)+'%':'OFF'}</span>
      </div>
      <div class="prog" style="margin-bottom:2px"><div class="prog-fill" style="width:${activeMetrics.has(key)?(weights[key]||0):0}%;background:${color}"></div></div>
      <div style="display:flex;justify-content:space-between;font-size:9px">
        <span style="color:var(--text3)">|R| = <strong style="color:${rCol};font-family:'IBM Plex Mono',monospace">${rStr}</strong></span>
        <span style="color:var(--text3)">${nStr}</span>
      </div>
    </div>`;
  }).join('') +
  `<div style="font-size:9px;color:var(--text3);margin-top:6px;line-height:1.5">
    Auto-dims (always on): cond×temp 1.5× · comfort 1.2× · dew-spread 1.1× · dew point 1.0× · month 0.7× · DOW 0.7×
  </div>`;

  // ── PAIRS & TRIPLETS ──
  const pairsEl = document.getElementById('analyzerPairs');
  if (pairsEl) {
    const pool = pbDraws.slice(0, Math.min(300, pbDraws.length));
    // Pairs
    const pc = {};
    pool.forEach(d => {
      const p = d.picks.filter(x => x >= 1 && x <= 69);
      for (let i=0; i<p.length; i++)
        for (let j=i+1; j<p.length; j++) {
          const k = (p[i]<p[j]?p[i]+'-'+p[j]:p[j]+'-'+p[i]);
          pc[k] = (pc[k]||0)+1;
        }
    });
    const topPairs = Object.entries(pc).sort((a,b)=>b[1]-a[1]).slice(0,8);
    // Triplets
    const tc = {};
    pool.forEach(d => {
      const p = d.picks.filter(x => x >= 1 && x <= 69).sort((a,b)=>a-b);
      for (let i=0; i<p.length-2; i++)
        for (let j=i+1; j<p.length-1; j++)
          for (let k=j+1; k<p.length; k++) {
            const key = p[i]+'-'+p[j]+'-'+p[k];
            tc[key] = (tc[key]||0)+1;
          }
    });
    const topTrips = Object.entries(tc).sort((a,b)=>b[1]-a[1]).slice(0,5);
    // Hot numbers (last 20 draws)
    const r20 = pbDraws.slice(0, 20);
    const hotCnt = {};
    r20.forEach(d => d.picks.forEach(num => { hotCnt[num] = (hotCnt[num]||0)+1; }));
    const hotNums = Object.entries(hotCnt).sort((a,b)=>b[1]-a[1]).slice(0,8);
    // Overdue numbers
    const gapList = [];
    for (let num=1; num<=69; num++) {
      const idx = pbDraws.findIndex(d => d.picks.includes(num));
      gapList.push([num, idx===-1 ? pbDraws.length : idx]);
    }
    const overdueNums = gapList.sort((a,b)=>b[1]-a[1]).slice(0,8);
    const maxG = overdueNums[0]?.[1] || 1;

    pairsEl.innerHTML =
      `<div style="margin-bottom:12px">
        <div style="font-size:10px;font-weight:700;color:var(--accent);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">🔥 Hot Numbers <span style="font-weight:400;color:var(--text3)">(last 20 draws)</span></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap">${hotNums.map(([num,cnt])=>
          `<div style="display:flex;align-items:center;gap:4px;padding:4px 8px;background:rgba(6,214,160,.1);border:1px solid rgba(6,214,160,.3);border-radius:6px">
            <div class="ball ball-w ball-xs">${num}</div>
            <span style="font-size:11px;font-weight:600;color:var(--ok)">${cnt}×</span>
          </div>`).join('')}
        </div>
      </div>
      <div style="margin-bottom:12px">
        <div style="font-size:10px;font-weight:700;color:var(--danger);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">❄️ Overdue Numbers <span style="font-weight:400;color:var(--text3)">(draws since last seen)</span></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap">${overdueNums.map(([num,gap])=>
          `<div style="display:flex;align-items:center;gap:4px;padding:4px 8px;background:rgba(239,71,111,.1);border:1px solid rgba(239,71,111,.3);border-radius:6px">
            <div class="ball ball-w ball-xs">${num}</div>
            <span style="font-size:11px;font-weight:600;color:var(--danger)">${gap} draws</span>
          </div>`).join('')}
        </div>
      </div>
      <div style="margin-bottom:12px">
        <div style="font-size:10px;font-weight:700;color:var(--text);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">🔗 Top Pairs <span style="font-weight:400;color:var(--text3)">(${pool.length} draws)</span></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap">${topPairs.map(([pair,cnt])=>{
          const [a,b] = pair.split('-');
          return `<div style="padding:4px 8px;background:var(--bg2);border:1px solid var(--border);border-radius:6px;display:flex;align-items:center;gap:3px">
            <div class="ball ball-w ball-xs">${a}</div><span style="color:var(--text3);font-size:10px">+</span><div class="ball ball-w ball-xs">${b}</div>
            <span style="font-size:10px;font-weight:600;color:var(--accent);margin-left:2px">${cnt}×</span>
          </div>`;}).join('')}
        </div>
      </div>
      <div>
        <div style="font-size:10px;font-weight:700;color:var(--text);text-transform:uppercase;letter-spacing:.5px;margin-bottom:6px">🔗 Top Triplets <span style="font-weight:400;color:var(--text3)">(${pool.length} draws)</span></div>
        <div style="display:flex;gap:6px;flex-wrap:wrap">${topTrips.map(([trip,cnt])=>{
          const nums = trip.split('-');
          return `<div style="padding:4px 8px;background:var(--bg2);border:1px solid var(--border);border-radius:6px;display:flex;align-items:center;gap:3px">
            ${nums.map(n=>`<div class="ball ball-w ball-xs">${n}</div>`).join('<span style="color:var(--text3);font-size:8px">+</span>')}
            <span style="font-size:10px;font-weight:600;color:var(--accent);margin-left:2px">${cnt}×</span>
          </div>`;}).join('')}
        </div>
      </div>`;
  }

  // Similar draws display
  const md = document.getElementById('matchedDraws');
  if (md) md.innerHTML = usedDraws.slice(0,5).map(d=>
    `<div style="display:flex;align-items:center;gap:7px;padding:7px 0;border-bottom:1px solid rgba(255,255,255,.04)">
      <span style="font-size:11px;color:var(--text2);flex:0 0 76px">${d.date}</span>
      <div class="balls" style="gap:3px">${d.picks.map(n=>`<div class="ball ball-w ball-xs">${n}</div>`).join('')}
        <div class="ball ball-xs" style="background:var(--danger);color:#fff;border-radius:50%;width:22px;height:22px;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:700">${d.pb}</div>
      </div>
      <span style="margin-left:auto;font-size:10px;color:var(--text2)">${d.temp?d.temp+'°':''}${d.pres?'/'+d.pres+'"':''}</span>
    </div>`
  ).join('') || '<div style="color:var(--text3);font-size:12px">No weather-matched draws.</div>';

  // Restore button
  if (rbtn) { rbtn.textContent = '⚡ Re-Run Analyzer'; rbtn.disabled = false; }

  showToast(`PB pick → ${picks.join(', ')} + PB ${special} · draws through ${_fmtISO(bt?.asOf)}`);
  window.track('analyze_powerball', { score: confScore, draws: n, wx: wxCount });

  // Flash animation so user sees the update
  const ballsContainer = document.getElementById('suggestedBalls');
  if (ballsContainer) {
    ballsContainer.style.transition = 'none';
    ballsContainer.style.opacity = '0.3';
    ballsContainer.style.transform = 'scale(0.95)';
    requestAnimationFrame(() => {
      ballsContainer.style.transition = 'opacity .4s ease, transform .4s ease';
      ballsContainer.style.opacity = '1';
      ballsContainer.style.transform = 'scale(1)';
    });
  }

  } catch(err) {
    console.error('runAnalyzer error:', err);
    showToast('Analyzer error: ' + err.message, 5000);
    const rbtn2 = document.getElementById('runAnalyzerBtn');
    if (rbtn2) { rbtn2.textContent = '⚡ Re-Run Analyzer'; rbtn2.disabled = false; }
  }
}

// ── MM WRAPPER ────────────────────────────────────────────────────────────────
function runMMAnalyzer() {
  const mmDraws = DD.filter(d => d.game === 'megamillions');
  const dcEl = document.getElementById('mm-draw-count');
  if (dcEl) dcEl.textContent = mmDraws.length + ' MM draws';

  if (mmDraws.length === 0) {
    showToast('No Mega Millions data yet — run EF3 draw-feed first');
    const sd = document.getElementById('mm-scoreDesc');
    if (sd) sd.textContent = 'No MM data loaded.';
    return;
  }

  const r = analyzerV2('megamillions');
  if (!r) { showToast('Not enough MM data yet'); return; }

  const bt = backtestPick('megamillions');
  const picks   = bt ? bt.picks   : r.picks;
  const special = bt ? bt.special : r.special;
  const { confScore, n, wxCount, usedDraws, binCount } = r;
  const col = '#1e5ac8'; // coverage gauge — neutral colour

  const ring = document.getElementById('mm-scoreCircle');
  if (ring) { ring.style.strokeDashoffset = 289-(289*confScore/100); ring.style.stroke = col; }
  const sv = document.getElementById('mm-scoreVal');
  if (sv) { sv.textContent = confScore + '%'; sv.style.color = col; }

  const desc = document.getElementById('mm-scoreDesc');
  if (desc) desc.innerHTML = _pickAsOfHtml(bt?.asOf, 'mm') +
    `Gauge = weather coverage: <strong>${wxCount}</strong> of <strong>${n}</strong> draws have draw-night weather. ` +
    `It is not a chance of winning — every Mega Millions ticket has the same 1 in 290,472,336 jackpot odds.`;

  const ballsEl = document.getElementById('mm-suggestedBalls');
  if (ballsEl) ballsEl.innerHTML =
    picks.map(n=>`<div class="ball ball-w" aria-label="Number ${n}">${n}</div>`).join('')+
    `<span style="margin:0 6px;color:var(--text2);font-size:18px">+</span>`+
    `<div class="ball" style="background:#ffd166;color:#000;border-radius:50%;width:44px;height:44px;display:inline-flex;align-items:center;justify-content:center;font-weight:700;font-size:15px" aria-label="Mega Ball ${special}">${special}</div>`;

  const cb = document.getElementById('mm-confBreakdown');
  if (cb) cb.innerHTML = [
    ['Temperature','temp','#6ba3ff','1.4×'],
    ['Humidity','hum','var(--ok)','1.0×'],
    ['Pressure','pres','var(--warn)','1.0×'],
    ['Wind Speed','wind','#9b5de5','1.2×']
  ].map(([name,key,color,mult])=>
    `<div style="margin-bottom:6px">
      <div style="display:flex;justify-content:space-between;margin-bottom:2px">
        <span style="font-size:11px;color:var(--text2)">${name} <span style="font-size:10px;opacity:.6">(${mult} engine)</span></span>
        <span style="font-size:11px;font-family:'IBM Plex Mono',monospace;color:${color}">${mmActiveMetrics.has(key)?mmWeights[key]+'% slider':'OFF'}</span>
      </div>
      <div class="prog"><div class="prog-fill" style="width:${mmActiveMetrics.has(key)?mmWeights[key]:0}%;background:${color}"></div></div>
    </div>`
  ).join('') +
  `<div style="font-size:10px;color:var(--text3);margin-top:6px;line-height:1.6">
    Auto-dims: wind-dir 1.3× · cond×temp 1.5× · comfort 1.2× · dew-spread 1.1× · dew point 1.0× · month 0.7× · DOW 0.7×<br>
    Bonuses: gap/due 0.3× · hot streak 0.15× · pair co-occur 0.4×
  </div>`;

  const md = document.getElementById('mm-matchedDraws');
  if (md) md.innerHTML = usedDraws.slice(0,5).map(d=>
    `<div style="display:flex;align-items:center;gap:7px;padding:7px 0;border-bottom:1px solid rgba(255,255,255,.04)">
      <span style="font-size:11px;color:var(--text2);flex:0 0 76px">${d.date}</span>
      <div class="balls" style="gap:3px">${d.picks.map(n=>`<div class="ball ball-w ball-xs">${n}</div>`).join('')}
        <div class="ball ball-xs" style="background:#ffd166;color:#000;border-radius:50%;width:22px;height:22px;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:700">${d.pb}</div>
      </div>
      <span style="margin-left:auto;font-size:10px;color:var(--text2)">${d.temp?d.temp+'°':''}${d.pres?'/'+d.pres+'"':''}</span>
    </div>`
  ).join('') || '<div style="color:var(--text3);font-size:12px">No weather-matched draws.</div>';

  renderMMFreqBars(mmDraws);
  showToast(`MM pick · draws through ${_fmtISO(bt?.asOf)} · ${wxCount}/${n} draws with weather`);
  window.track('analyze_megamillions', { score: confScore, draws: n, wx: wxCount });
}


// ── DATE RANGE STATE ──
let pbDateFrom = null, pbDateTo = null;
let mmDateFrom = null, mmDateTo = null;
let mathDateFrom = null, mathDateTo = null;

// All three arguments are YYYY-MM-DD strings (pass d.iso, not d.date — d.date is
// "M-D-YYYY" for display and does not compare correctly as text).
function inRange(dateStr, from, to) {
  if (!dateStr) return true;
  if (from && dateStr < from) return false;
  if (to && dateStr > to) return false;
  return true;
}

function _presetDate(years) {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().slice(0,10);
}

function _updateRangePresetBtns(cls, preset) {
  const map = {'1y':'1yr','2y':'2yr','5y':'5yr','all':'All'};
  document.querySelectorAll('.'+cls).forEach(b => {
    b.className = b.textContent.trim() === map[preset]
      ? 'btn btn-primary btn-sm '+cls
      : 'btn btn-outline btn-sm '+cls;
  });
}

// ── PB Date Range ──
function setPBDateRange() {
  pbDateFrom = document.getElementById('pb-date-from')?.value || null;
  pbDateTo   = document.getElementById('pb-date-to')?.value   || null;
  _updatePBRangeUI();
  if (typeof autoPBWeights === 'function') autoPBWeights();
  else if (typeof runAnalyzer === 'function') runAnalyzer();
}
function setPBPreset(preset) {
  pbDateFrom = preset === 'all' ? null : _presetDate(parseInt(preset));
  pbDateTo = null;
  const fromEl = document.getElementById('pb-date-from');
  const toEl   = document.getElementById('pb-date-to');
  if (fromEl) fromEl.value = pbDateFrom || '';
  if (toEl)   toEl.value   = '';
  _updateRangePresetBtns('pb-range-preset', preset);
  _updatePBRangeUI();
  if (typeof autoPBWeights === 'function') autoPBWeights();
  else if (typeof runAnalyzer === 'function') runAnalyzer();
}
function _updatePBRangeUI() {
  const draws = DD.filter(d => d.game==='powerball' && inRange(d.iso, pbDateFrom, pbDateTo));
  const countEl = document.getElementById('pb-range-count');
  const warnEl  = document.getElementById('pb-range-warn');
  if (countEl) countEl.textContent = draws.length + ' draws in range';
  if (warnEl) {
    if (draws.length > 0 && draws.length < 30) {
      warnEl.style.display = '';
      warnEl.textContent = '⚠️ Only ' + draws.length + ' draws in range — results may not be statistically meaningful (30+ recommended).';
    } else { warnEl.style.display = 'none'; }
  }
}

// ── MM Date Range ──
function setMMDateRange() {
  mmDateFrom = document.getElementById('mm-date-from')?.value || null;
  mmDateTo   = document.getElementById('mm-date-to')?.value   || null;
  _updateMMRangeUI();
  if (typeof autoMMWeights === 'function') autoMMWeights();
  else if (typeof runMMAnalyzer === 'function') runMMAnalyzer();
}
function setMMPreset(preset) {
  mmDateFrom = preset === 'all' ? null : _presetDate(parseInt(preset));
  mmDateTo = null;
  const fromEl = document.getElementById('mm-date-from');
  const toEl   = document.getElementById('mm-date-to');
  if (fromEl) fromEl.value = mmDateFrom || '';
  if (toEl)   toEl.value   = '';
  _updateRangePresetBtns('mm-range-preset', preset);
  _updateMMRangeUI();
  if (typeof autoMMWeights === 'function') autoMMWeights();
  else if (typeof runMMAnalyzer === 'function') runMMAnalyzer();
}
function _updateMMRangeUI() {
  const draws = DD.filter(d => d.game==='megamillions' && inRange(d.iso, mmDateFrom, mmDateTo));
  const countEl = document.getElementById('mm-range-count');
  const warnEl  = document.getElementById('mm-range-warn');
  if (countEl) countEl.textContent = draws.length + ' draws in range';
  if (warnEl) {
    if (draws.length > 0 && draws.length < 30) {
      warnEl.style.display = '';
      warnEl.textContent = '⚠️ Only ' + draws.length + ' draws in range — results may not be statistically meaningful (30+ recommended).';
    } else { warnEl.style.display = 'none'; }
  }
}
// ════════════════════════════════════════════════════════════════
// Analyzer R-Method selectors
// ════════════════════════════════════════════════════════════════
// Debounced auto-rerun when weather inputs or controls change
// Debounced rerun — just reruns analyzer with current weights (no weight recalc)
function debouncedRerun() {
  clearTimeout(window._rerunTimer);
  window._rerunTimer = setTimeout(() => {
    if (document.body.dataset.page === 'analyzer' && DD.length > 0) {
      runAnalyzer();
    }
  }, 300);
}

// Full recompute — recalculates R-values, sets weights, then runs analyzer
function debouncedFullRecompute() {
  clearTimeout(window._rerunTimer);
  window._rerunTimer = setTimeout(() => {
    if (document.body.dataset.page === 'analyzer' && DD.length > 0) {
      autoPBWeights();
    }
  }, 300);
}

function setPBRMethod(method) {
  pbRMethod = method;
  document.querySelectorAll('.pb-rmethod').forEach(b => {
    b.classList.toggle('btn-primary', b.id === 'pb-rm-' + method);
    b.classList.toggle('btn-outline', b.id !== 'pb-rm-' + method);
  });
  // R-method change means R-values change → must recompute weights
  debouncedFullRecompute();
}
function setMMRMethod(method) {
  mmRMethod = method;
  document.querySelectorAll('.mm-rmethod').forEach(b => {
    b.classList.toggle('btn-primary', b.id === 'mm-rm-' + method);
    b.classList.toggle('btn-outline', b.id !== 'mm-rm-' + method);
    // Re-apply MM blue colour on active button
    if (b.id === 'mm-rm-' + method) { b.style.background='#1e5ac8'; b.style.borderColor='#1e5ac8'; }
    else { b.style.background=''; b.style.borderColor=''; }
  });
}

// ════════════════════════════════════════════════════════════════
// Auto-Weight: compute |R| for each weather metric then normalize
// Uses direct draw properties (d.temp, d.hum, d.pres, d.ws)
// Correlates weather against: avg(picks), number_sum, and per-ball frequency
// ════════════════════════════════════════════════════════════════
// Convert compass direction string to degrees (0-360)
function windDirToDeg(dir) {
  if (!dir || dir === 'CALM' || dir === 'VRB') return null;
  const map = {N:0,NNE:22.5,NE:45,ENE:67.5,E:90,ESE:112.5,SE:135,SSE:157.5,
    S:180,SSW:202.5,SW:225,WSW:247.5,W:270,WNW:292.5,NW:315,NNW:337.5};
  const deg = map[String(dir).toUpperCase().trim()];
  return deg !== undefined ? deg : null;
}

// Circular correlation for wind direction (degrees are circular: 0° ≈ 360°)
// Converts degrees to sin/cos components, correlates each with outcome, returns combined |R|
function circularCorr(method, degrees, ys) {
  const sinVals = degrees.map(d => Math.sin(d * Math.PI / 180));
  const cosVals = degrees.map(d => Math.cos(d * Math.PI / 180));
  const rSin = computeCorrWith(method, sinVals, ys);
  const rCos = computeCorrWith(method, cosVals, ys);
  const absSin = rSin !== null ? Math.abs(rSin) : 0;
  const absCos = rCos !== null ? Math.abs(rCos) : 0;
  // Combined circular R using sqrt(rSin² + rCos²) capped at 1
  return Math.min(1, Math.sqrt(absSin * absSin + absCos * absCos));
}

function _computeAutoWeights(game, method, dFrom, dTo) {
  const METRICS = ['temp','hum','pres','wind','wdir'];
  const wxKeys = { temp:'temp', hum:'hum', pres:'pres', wind:'ws', wdir:'wind' };
  const draws = DD.filter(d => d.game === game && inRange(d.iso, dFrom, dTo) && d.picks && d.picks.length >= 5);
  if (draws.length < 10) return null;

  const wxDraws = draws.filter(d => d.temp != null);
  if (wxDraws.length < 10) return null;

  const ysAvg = wxDraws.map(d => d.picks.reduce((s,n)=>s+n,0) / d.picks.length);
  const ysSum = wxDraws.map(d => d.sum || d.picks.reduce((s,n)=>s+n,0));

  const rVals = {};
  window._liveRValues = window._liveRValues || {};

  // ── Standard numeric weather metrics ──
  METRICS.forEach(mx => {
    const wKey = wxKeys[mx];

    if (mx === 'wdir') {
      const degsAvg = [], filtYsA = [], filtYsS = [];
      wxDraws.forEach((d, i) => {
        const deg = windDirToDeg(d[wKey]);
        if (deg !== null) { degsAvg.push(deg); filtYsA.push(ysAvg[i]); filtYsS.push(ysSum[i]); }
      });
      if (degsAvg.length < 10) { rVals[mx] = 0; window._liveRValues[mx] = { rAvg: 0, rSum: 0, best: 0, n: 0, method, note: 'circular' }; return; }
      const rAvg = circularCorr(method, degsAvg, filtYsA);
      const rSum = circularCorr(method, degsAvg, filtYsS);
      rVals[mx] = Math.max(rAvg, rSum);
      window._liveRValues[mx] = { rAvg, rSum, best: rVals[mx], n: degsAvg.length, method, note: 'circular (sin/cos)' };
      return;
    }

    const xs = wxDraws.map(d => d[wKey] != null ? +d[wKey] : null);
    const prs = xs.map((x, i) => ({ x, yA: ysAvg[i], yS: ysSum[i] })).filter(p => p.x !== null && isFinite(p.x));
    if (prs.length < 10) { rVals[mx] = 0; return; }
    const pxs = prs.map(p=>p.x), pysA = prs.map(p=>p.yA), pysS = prs.map(p=>p.yS);
    const rAvg = computeCorrWith(method, pxs, pysA);
    const rSum = computeCorrWith(method, pxs, pysS);
    rVals[mx] = Math.max(rAvg!==null?Math.abs(rAvg):0, rSum!==null?Math.abs(rSum):0);
    window._liveRValues[mx] = { rAvg, rSum, best: rVals[mx], n: prs.length, method };
  });

  // ── Condition: point-biserial R (encode each condition category as 0/1) ──
  {
    const condCategories = {};
    wxDraws.forEach(d => { if (d.cond) condCategories[d.cond] = true; });
    let bestCondR = 0;
    Object.keys(condCategories).forEach(cat => {
      const xs = wxDraws.map(d => d.cond === cat ? 1 : 0);
      const rA = computeCorrWith(method, xs, ysAvg);
      const rS = computeCorrWith(method, xs, ysSum);
      const best = Math.max(rA!==null?Math.abs(rA):0, rS!==null?Math.abs(rS):0);
      if (best > bestCondR) bestCondR = best;
    });
    rVals.cond = bestCondR;
    window._liveRValues.cond = { rAvg: bestCondR, rSum: bestCondR, best: bestCondR, n: wxDraws.length, method, note: 'point-biserial (best category)' };
  }

  // ── Moon: ordinal R (illumination % vs avg ball number) ──
  // Uses ALL draws (not just wxDraws) since moon doesn't need weather
  {
    const moonXs = [], moonYsA = [], moonYsS = [];
    draws.forEach(d => {
      const illum = moonIllumForDate(d.year, d.month, d.day);
      if (illum != null && d.picks && d.picks.length >= 5) {
        moonXs.push(illum);
        moonYsA.push(d.picks.reduce((s,n)=>s+n,0) / d.picks.length);
        moonYsS.push(d.sum || d.picks.reduce((s,n)=>s+n,0));
      }
    });
    if (moonXs.length >= 10) {
      const rA = computeCorrWith(method, moonXs, moonYsA);
      const rS = computeCorrWith(method, moonXs, moonYsS);
      rVals.moon = Math.max(rA!==null?Math.abs(rA):0, rS!==null?Math.abs(rS):0);
      window._liveRValues.moon = { rAvg: rA, rSum: rS, best: rVals.moon, n: moonXs.length, method, note: 'ordinal (illumination %)' };
    } else {
      rVals.moon = 0;
      window._liveRValues.moon = { rAvg: 0, rSum: 0, best: 0, n: 0, method, note: 'ordinal' };
    }
  }

  // ── Hot/Cold/Pairs/Trips: frequency-based metrics ──
  // These use ALL draws (not just wxDraws) — build their own Y series
  {
    const maxBall = game === 'powerball' ? 69 : 70;
    // Build Y series from all draws (not wxDraws)
    const allYsAvg = draws.map(d => d.picks.reduce((s,n)=>s+n,0) / d.picks.length);

    // Hot: correlation between recent frequency and avg ball
    const last20 = draws.slice(0, 20);
    const hotFreq = {};
    for (let i=1; i<=maxBall; i++) hotFreq[i] = 0;
    last20.forEach(d => d.picks.forEach(n => { hotFreq[n] = (hotFreq[n]||0)+1; }));
    const hotXs = draws.map(d => d.picks.reduce((s,n) => s + (hotFreq[n]||0), 0) / 5);
    const rHot = computeCorrWith(method, hotXs, allYsAvg);
    const hotR = rHot !== null ? Math.abs(rHot) : 0;
    window._liveRValues.hot = { rAvg: rHot, rSum: rHot, best: hotR, n: draws.length, method, note: 'frequency correlation' };

    // Cold: correlation between gap length and avg ball
    const gapsMap = {};
    for (let num=1; num<=maxBall; num++) {
      const idx = draws.findIndex(d => d.picks.includes(num));
      gapsMap[num] = idx === -1 ? draws.length : idx;
    }
    const coldXs = draws.map(d => d.picks.reduce((s,n) => s + (gapsMap[n]||0), 0) / 5);
    const rCold = computeCorrWith(method, coldXs, allYsAvg);
    const coldR = rCold !== null ? Math.abs(rCold) : 0;
    window._liveRValues.cold = { rAvg: rCold, rSum: rCold, best: coldR, n: draws.length, method, note: 'gap-length correlation' };

    // Pairs: avg pair co-occurrence rate → correlate with avg ball
    const pairPool = draws.slice(0, Math.min(300, draws.length));
    const pairCnt = {};
    pairPool.forEach(d => {
      const p = d.picks.filter(x => x >= 1 && x <= maxBall);
      for (let i=0; i<p.length; i++)
        for (let j=i+1; j<p.length; j++) {
          const k = p[i]<p[j] ? p[i]+'-'+p[j] : p[j]+'-'+p[i];
          pairCnt[k] = (pairCnt[k]||0)+1;
        }
    });
    const pp = pairPool.length || 1;
    const pairXs = draws.map(d => {
      let sum = 0, cnt = 0;
      const p = d.picks.filter(x => x >= 1 && x <= maxBall);
      for (let i=0; i<p.length; i++)
        for (let j=i+1; j<p.length; j++) {
          const k = p[i]<p[j] ? p[i]+'-'+p[j] : p[j]+'-'+p[i];
          sum += (pairCnt[k]||0) / pp; cnt++;
        }
      return cnt > 0 ? sum / cnt : 0;
    });
    const rPairs = computeCorrWith(method, pairXs, allYsAvg);
    const pairsR = rPairs !== null ? Math.abs(rPairs) : 0;
    window._liveRValues.pairs = { rAvg: rPairs, rSum: rPairs, best: pairsR, n: draws.length, method, note: 'co-occurrence rate' };

    // Trips: triplet co-occurrence → correlate with avg ball
    const tripCnt = {};
    pairPool.forEach(d => {
      const p = d.picks.filter(x => x >= 1 && x <= maxBall).sort((a,b)=>a-b);
      for (let i=0; i<p.length-2; i++)
        for (let j=i+1; j<p.length-1; j++)
          for (let k=j+1; k<p.length; k++) {
            const key = p[i]+'-'+p[j]+'-'+p[k];
            tripCnt[key] = (tripCnt[key]||0)+1;
          }
    });
    const tripXs = draws.map(d => {
      let sum = 0, cnt = 0;
      const p = d.picks.filter(x => x >= 1 && x <= maxBall).sort((a,b)=>a-b);
      for (let i=0; i<p.length-2; i++)
        for (let j=i+1; j<p.length-1; j++)
          for (let k=j+1; k<p.length; k++) {
            const key = p[i]+'-'+p[j]+'-'+p[k];
            sum += (tripCnt[key]||0) / pp; cnt++;
          }
      return cnt > 0 ? sum / cnt : 0;
    });
    const rTrips = computeCorrWith(method, tripXs, allYsAvg);
    const tripsR = rTrips !== null ? Math.abs(rTrips) : 0;
    window._liveRValues.trips = { rAvg: rTrips, rSum: rTrips, best: tripsR, n: draws.length, method, note: 'triplet co-occurrence' };

    // Sum Proximity: not a per-draw correlation — it's a combo-level Gaussian fit.
    // Set a fixed R placeholder so the metric breakdown displays properly.
    window._liveRValues.sumprox = { rAvg: null, rSum: null, best: 0, n: draws.length, method, note: 'combo-level Gaussian (not per-draw R)' };
  }
  const total = Object.values(rVals).reduce((s,v)=>s+v, 0);
  if (total === 0) return null;
  // Normalize to integers summing to 100
  const raw = {};
  METRICS.forEach(mx => { raw[mx] = Math.round(rVals[mx] / total * 100); });
  // Fix rounding drift
  const rawTotal = Object.values(raw).reduce((s,v)=>s+v, 0);
  const drift = 100 - rawTotal;
  if (drift !== 0) {
    const fix = METRICS.slice().sort((a,b)=>raw[b]-raw[a])[0];
    raw[fix] += drift;
  }
  return raw;
}

function autoPBWeights() {
  const result = _computeAutoWeights('powerball', pbRMethod, pbDateFrom, pbDateTo);
  if (!result) {
    showToast('Not enough weather data for R-based weights — using defaults');
    // Still run analyzer with current weights even if we can't compute R-based weights
    if (typeof runAnalyzer === 'function') runAnalyzer();
    return;
  }
  // Use all live R-values to set weights proportionally
  // Pull live R from window._liveRValues (populated by _computeAutoWeights)
  const rv = window._liveRValues || {};
  const autoW = {};
  const allR = {};
  ALL_METRICS.forEach(m => {
    allR[m] = (rv[m] && rv[m].best != null) ? rv[m].best : 0;
    // Give non-weather factors a minimum floor so they're never zero
    if (['hot','cold','pairs','trips','sumprox'].includes(m) && allR[m] < 0.005) allR[m] = 0.005;
  });
  const totalR = ALL_METRICS.reduce((s,m) => s + allR[m], 0);
  if (totalR > 0) {
    ALL_METRICS.forEach(m => {
      autoW[m] = Math.max(0, Math.round(allR[m] / totalR * 100));
    });
  } else {
    // Fallback to defaults
    Object.assign(autoW, PB_DEFAULT_WEIGHTS);
  }
  // Fix total to 100
  const tot = ALL_METRICS.reduce((s,m) => s + (autoW[m]||0), 0);
  const drift = 100 - tot;
  if (drift !== 0) {
    const biggest = ALL_METRICS.slice().sort((a,b)=>(autoW[b]||0)-(autoW[a]||0))[0];
    autoW[biggest] = (autoW[biggest]||0) + drift;
  }
  Object.assign(weights, autoW);
  ALL_METRICS.forEach(x => {
    const sl = document.getElementById('r-' + x), lb = document.getElementById('w-' + x);
    if (sl) sl.value = weights[x] || 0; if (lb) lb.textContent = (weights[x] || 0) + '%';
    const btn = document.querySelector(`[data-metric="${x}"]`);
    if (btn && weights[x] > 0 && !activeMetrics.has(x)) {
      activeMetrics.add(x);
      btn.classList.add('on'); btn.setAttribute('aria-pressed', 'true');
    }
  });
  updateWeightBar();
  showToast('⚡ Auto-weighted all ' + ALL_METRICS.length + ' factors using |' + pbRMethod + ' R|');
  if (typeof runAnalyzer === 'function') runAnalyzer();
}

function autoMMWeights() {
  const result = _computeAutoWeights('megamillions', mmRMethod, mmDateFrom, mmDateTo);
  if (!result) { showToast('Not enough data with weather for auto-weight (need 10+ draws)'); return; }
  const METRICS = ['temp','hum','pres','wind'];
  Object.assign(mmWeights, result);
  METRICS.forEach(x => {
    const sl = document.getElementById('mm-r-' + x), lb = document.getElementById('mm-w-' + x);
    if (sl) sl.value = mmWeights[x]; if (lb) lb.textContent = mmWeights[x] + '%';
  });
  updateMMWeightBar();
  showToast('⚡ MM Weights set to |' + mmRMethod.charAt(0).toUpperCase() + mmRMethod.slice(1) + ' R| proportions');
  if (typeof runMMAnalyzer === 'function') runMMAnalyzer();
}

// ════════════════════════════════════════════════════════════════
// Reset weights to defaults
// ════════════════════════════════════════════════════════════════
const PB_DEFAULT_WEIGHTS = { temp:20, hum:15, pres:15, wind:10, wdir:5, cond:5, moon:0, hot:10, cold:10, pairs:8, trips:2, sumprox:15 };
const MM_DEFAULT_WEIGHTS = { temp:30, hum:25, pres:25, wind:20, moon:0, wdir:0, cond:0 };

function resetPBWeights() {
  Object.assign(weights, PB_DEFAULT_WEIGHTS);
  ALL_METRICS.forEach(x => {
    const sl = document.getElementById('r-' + x), lb = document.getElementById('w-' + x);
    if (sl) sl.value = weights[x] || 0; if (lb) lb.textContent = (weights[x] || 0) + '%';
  });
  updateWeightBar();
  showToast('Weights reset to defaults');
  if (typeof runAnalyzer === 'function') runAnalyzer();
}

function resetMMWeights() {
  Object.assign(mmWeights, MM_DEFAULT_WEIGHTS);
  const METRICS = ['temp','hum','pres','wind'];
  METRICS.forEach(x => {
    const sl = document.getElementById('mm-r-' + x), lb = document.getElementById('mm-w-' + x);
    if (sl) sl.value = mmWeights[x]; if (lb) lb.textContent = mmWeights[x] + '%';
  });
  updateMMWeightBar();
  showToast('MM Weights reset to defaults');
  if (typeof runMMAnalyzer === 'function') runMMAnalyzer();
}



// ── Math Stats Date Range ──
function setMathDateRange() {
  mathDateFrom = document.getElementById('math-date-from')?.value || null;
  mathDateTo   = document.getElementById('math-date-to')?.value   || null;
  _updateMathRangeUI();
  if (typeof computeMathStats === 'function') computeMathStats();
}
function setMathPreset(preset) {
  mathDateFrom = preset === 'all' ? null : _presetDate(parseInt(preset));
  mathDateTo = null;
  const fromEl = document.getElementById('math-date-from');
  const toEl   = document.getElementById('math-date-to');
  if (fromEl) fromEl.value = mathDateFrom || '';
  if (toEl)   toEl.value   = '';
  _updateRangePresetBtns('math-range-preset', preset);
  _updateMathRangeUI();
  if (typeof computeMathStats === 'function') computeMathStats();
}
function _updateMathRangeUI() {
  const gameKey = mathGame === 'pb' ? 'powerball' : 'megamillions';
  const draws = DD.filter(d => d.game===gameKey && inRange(d.iso, mathDateFrom, mathDateTo));
  const el = document.getElementById('math-range-count');
  if (el) el.textContent = '— ' + draws.length + ' draws in range';
}

// ── MATH/STATS PAGE ──
let mathGame = 'pb';
let mathRMethod = 'pearson';
let pbRMethod   = 'pearson';   // PB Analyzer R-value method
let mmRMethod   = 'pearson';   // MM Analyzer R-value method
let _mathWindowSize = 30;
function setMathGame(g) {
  mathGame = g;
  document.getElementById('math-tab-pb').className = g === 'pb' ? 'btn btn-primary' : 'btn btn-outline';
  document.getElementById('math-tab-mm').className = g === 'mm' ? 'btn btn-primary' : 'btn btn-outline';
  computeMathStats();
}

function pearsonR(xs, ys) {
  const n = xs.length;
  if (n < 3) return null;
  const mx = xs.reduce((s,x)=>s+x,0)/n, my = ys.reduce((s,y)=>s+y,0)/n;
  let num=0,dx2=0,dy2=0;
  for (let i=0;i<n;i++){const dx=xs[i]-mx,dy=ys[i]-my;num+=dx*dy;dx2+=dx*dx;dy2+=dy*dy;}
  const denom=Math.sqrt(dx2*dy2);
  return denom===0?0:num/denom;
}

// Spearman Rho: rank-based correlation (more robust to outliers & non-linear monotonic relationships)
function spearmanR(xs, ys) {
  const n = xs.length;
  if (n < 3) return null;
  function rankArray(arr) {
    const sorted = arr.map((v,i)=>({v,i})).sort((a,b)=>a.v-b.v);
    const ranks = new Array(n);
    let i = 0;
    while (i < n) {
      let j = i;
      while (j < n-1 && sorted[j+1].v === sorted[j].v) j++;
      const avg = (i+j)/2 + 1;
      for (let k=i; k<=j; k++) ranks[sorted[k].i] = avg;
      i = j+1;
    }
    return ranks;
  }
  return pearsonR(rankArray(xs), rankArray(ys));
}

// Kendall Tau-b: concordance-based (most robust to ties and small samples)
function kendallTau(xs, ys) {
  const n = xs.length;
  if (n < 3) return null;
  let C=0, D=0, Tx=0, Ty=0;
  for (let i=0; i<n-1; i++) {
    for (let j=i+1; j<n; j++) {
      const dx = xs[j]-xs[i], dy = ys[j]-ys[i];
      if (dx===0 && dy===0) continue;
      if (dx===0) { Tx++; continue; }
      if (dy===0) { Ty++; continue; }
      if (dx*dy > 0) C++; else D++;
    }
  }
  const denom = Math.sqrt((C+D+Tx)*(C+D+Ty));
  return denom===0 ? 0 : (C-D)/denom;
}

// Unified correlation dispatcher
function computeCorr(xs, ys) {
  switch(mathRMethod) {
    case 'spearman': return spearmanR(xs, ys);
    case 'kendall':  return kendallTau(xs, ys);
    default:         return pearsonR(xs, ys);
  }
}
function computeCorrWith(method, xs, ys) {
  switch(method) {
    case 'spearman': return spearmanR(xs, ys);
    case 'kendall':  return kendallTau(xs, ys);
    default:         return pearsonR(xs, ys);
  }
}

// Moving Window R: Pearson r over a sliding window — shows if correlation is strengthening/weakening
function movingWindowR(xs, ys, windowSize) {
  const n = xs.length;
  if (n < windowSize) return [];
  const results = [];
  for (let start=0; start <= n-windowSize; start++) {
    results.push({ pos: start, r: pearsonR(xs.slice(start, start+windowSize), ys.slice(start, start+windowSize)) });
  }
  return results;
}

function onWindowSliderChange(v) {
  _mathWindowSize = +v;
  const valEl = document.getElementById('math-window-val');
  if (valEl) valEl.textContent = v + ' draws';
  computeMathStats();
}

function setMathRMethod(method) {
  mathRMethod = method;
  document.querySelectorAll('.math-rmethod-btn').forEach(b => {
    b.className = b.id === 'rmethod-'+method ? 'btn btn-primary btn-sm math-rmethod-btn' : 'btn btn-outline btn-sm math-rmethod-btn';
  });
  const descs = {
    pearson:       '<strong>Pearson r:</strong> Measures linear correlation between weather value and number outcome. Best when expecting a straight-line relationship. Already used by the Analyzer to suggest weights.',
    spearman:      '<strong>Spearman &#961; (rho):</strong> Rank-based correlation. Converts values to ranks first, making it more robust to outliers and non-linear monotonic relationships. Good when weather effects aren&#39;t strictly linear.',
    kendall:       '<strong>Kendall &#964; (tau):</strong> Counts concordant vs discordant pairs. Most robust to ties and small samples — ideal when you&#39;ve selected a short time frame with fewer draws. Slightly smaller magnitude than Pearson/Spearman but more reliable.',
    pointbiserial: '<strong>Point-Biserial r:</strong> Correlates binary outcomes (did a specific ball appear: yes/no) vs continuous weather values. Reveals which individual ball numbers are most weather-sensitive. Shows avg |r| across all balls plus the top positively/negatively correlated numbers.',
    moving:        '<strong>Moving Window r:</strong> Computes Pearson r over a sliding window of draws. Reveals whether weather-number correlations are strengthening or weakening recently. Adjust window size to control how many draws are included per point.'
  };
  const descEl = document.getElementById('rmethod-desc');
  if (descEl) descEl.innerHTML = descs[method] || descs.pearson;
  const mwCard = document.getElementById('math-moving-window-card');
  if (mwCard) mwCard.style.display = method === 'moving' ? '' : 'none';
  computeMathStats();
}

function renderMovingWindowChart(draws, windowSize) {
  const el = document.getElementById('math-moving-chart');
  if (!el) return;
  const metrics = [
    {key:'temp', label:'Temp',  color:'#6ba3ff', get: d=>d.temp},
    {key:'hum',  label:'Hum',   color:'var(--ok)',   get: d=>d.hum},
    {key:'pres', label:'Pres',  color:'var(--warn)',  get: d=>d.pres},
    {key:'ws',   label:'Wind',  color:'#9b5de5', get: d=>d.ws||0},
  ];
  const sumOf = d => d.picks.reduce((s,n)=>s+n,0);

  const chartData = metrics.map(m => {
    const valid = draws.filter(d => m.get(d)!==null && m.get(d)!==undefined);
    if (valid.length < windowSize) return null;
    return { ...m, windows: movingWindowR(valid.map(m.get), valid.map(sumOf), windowSize) };
  }).filter(Boolean);

  if (chartData.length === 0 || chartData.every(m => m.windows.length === 0)) {
    el.innerHTML = '<div style="color:var(--text3);font-size:12px">Not enough draws for this window size. Reduce window size or add more draw data.</div>';
    return;
  }

  const maxW = Math.max(...chartData.map(m => m.windows.length));
  const svgW = Math.max(500, maxW * 3);
  const svgH = 140;
  const pad = {t:14, b:24, l:34, r:60};
  const cW = svgW-pad.l-pad.r, cH = svgH-pad.t-pad.b;

  const toX = i => pad.l + (i / Math.max(maxW-1,1)) * cW;
  const toY = r => pad.t + ((1-(r||0))/2) * cH;

  let grid = '';
  [-1,-0.5,0,0.5,1].forEach(r => {
    const y = toY(r).toFixed(1);
    grid += `<line x1="${pad.l}" y1="${y}" x2="${pad.l+cW}" y2="${y}" stroke="var(--border)" stroke-width="${r===0?1.5:.5}" stroke-dasharray="${r===0?'':'3,3'}" opacity=".6"/>`;
    grid += `<text x="${pad.l-4}" y="${y}" fill="var(--text3)" font-size="8" text-anchor="end" dominant-baseline="middle">${r>0?'+':''}${r}</text>`;
  });

  let paths = '';
  chartData.forEach(m => {
    if (m.windows.length < 2) return;
    const d = m.windows.map((w,i) => `${i===0?'M':'L'}${toX(i).toFixed(1)},${toY(w.r).toFixed(1)}`).join(' ');
    paths += `<path d="${d}" stroke="${m.color}" stroke-width="1.5" fill="none" opacity=".9"/>`;
    const last = m.windows[m.windows.length-1];
    paths += `<text x="${(toX(m.windows.length-1)+5).toFixed(0)}" y="${toY(last.r).toFixed(0)}" fill="${m.color}" font-size="9" dominant-baseline="middle">${m.label} ${(last.r||0)>=0?'+':''}${(last.r||0).toFixed(2)}</text>`;
  });

  let xTicks = '';
  const tickPositions = [0, Math.floor(maxW*.25), Math.floor(maxW*.5), Math.floor(maxW*.75), maxW-1].filter((v,i,a)=>a.indexOf(v)===i);
  tickPositions.forEach(i => {
    const x = toX(i).toFixed(1);
    xTicks += `<line x1="${x}" y1="${pad.t+cH}" x2="${x}" y2="${pad.t+cH+4}" stroke="var(--border)" stroke-width="1"/>`;
    xTicks += `<text x="${x}" y="${pad.t+cH+13}" fill="var(--text3)" font-size="8" text-anchor="middle">w${i+1}</text>`;
  });

  el.innerHTML = `<div style="overflow-x:auto"><svg width="100%" viewBox="0 0 ${svgW} ${svgH}" style="font-family:var(--font);min-width:320px">${grid}${paths}${xTicks}<text x="${pad.l+cW/2}" y="${svgH}" fill="var(--text3)" font-size="8" text-anchor="middle">Window position (oldest draws ← → newest draws)</text></svg></div>
  <div style="display:flex;flex-wrap:wrap;gap:12px;margin-top:8px;font-size:11px">
    ${chartData.map(m=>`<span style="display:flex;align-items:center;gap:5px"><span style="display:inline-block;width:14px;height:2px;background:${m.color};border-radius:1px"></span><span style="color:var(--text2)">${m.label}</span><span style="font-family:monospace;color:${m.color}">${(m.windows[m.windows.length-1]?.r||0)>=0?'+':''}${(m.windows[m.windows.length-1]?.r||0).toFixed(3)}</span></span>`).join('')}
    <span style="color:var(--text3);margin-left:4px">(latest window r vs Sum)</span>
  </div>`;
}

function computeMathStats() {
  const gameKey = mathGame === 'pb' ? 'powerball' : 'megamillions';
  const gameMaxBall = mathGame === 'pb' ? 69 : 70;
  let draws = DD.filter(d=>d.game===gameKey && d.temp!==null && d.picks && d.picks.length===5);
  // Apply date range filter
  if (mathDateFrom || mathDateTo) {
    draws = draws.filter(d => inRange(d.iso, mathDateFrom, mathDateTo));
  }
  _updateMathRangeUI();
  const el = document.getElementById('math-corr-table');
  if (!el) return;
  if (draws.length < 10) {
    el.innerHTML = '<div style="color:var(--text3);padding:20px;text-align:center">Not enough draws with weather data yet. Need at least 10 draws. Run the draw-feed edge function to populate data.</div>';
    return;
  }

  const metrics = [
    {key:'temp', label:'🌡️ Temperature', get: d=>d.temp, unit:'°F'},
    {key:'hum',  label:'💧 Humidity',    get: d=>d.hum,  unit:'%'},
    {key:'pres', label:'⬇️ Pressure',    get: d=>d.pres, unit:'"'},
    {key:'ws',   label:'💨 Wind Speed',  get: d=>d.ws||0, unit:'mph'},
  ];

  const sumOf   = d => d.picks.reduce((s,n)=>s+n,0);
  const avgOf   = d => sumOf(d)/d.picks.length;
  const highFrac = d => d.picks.filter(n=>n>35).length/d.picks.length;
  const lowFrac  = d => d.picks.filter(n=>n<=35).length/d.picks.length;

  const methodNames = {pearson:'Pearson r', spearman:'Spearman &#961;', kendall:'Kendall &#964;', pointbiserial:'Point-Biserial r', moving:'Moving Window r'};
  const methodName = methodNames[mathRMethod]||'Pearson r';

  // Sample size warning
  let sampleWarn = '';
  if (mathRMethod==='kendall' && draws.length < 30) {
    sampleWarn = `<div style="background:rgba(255,209,102,.15);border:1px solid var(--warn);border-radius:6px;padding:8px 12px;font-size:11px;color:var(--warn);margin-bottom:10px">⚠️ Only ${draws.length} draws with weather data — Kendall &#964; is most reliable with 30+ samples.</div>`;
  } else if (draws.length < 30) {
    sampleWarn = `<div style="background:rgba(0,180,216,.08);border:1px solid rgba(0,180,216,.2);border-radius:6px;padding:8px 12px;font-size:11px;color:var(--text2);margin-bottom:10px">ℹ️ ${draws.length} draws with weather data. Results improve with 30+ draws (30 minimum recommended for statistical reliability).</div>`;
  }

  // Standard table header HTML (reused across modes)
  const stdHeader = `<thead><tr style="border-bottom:2px solid var(--border)">
    <th style="text-align:left;padding:6px 8px">Parameter</th>
    <th title="Correlation with sum of all 5 picked numbers">r vs Sum</th>
    <th title="Correlation with fraction of numbers above 35">r vs High%</th>
    <th title="Correlation with fraction of numbers 35 or below">r vs Low%</th>
    <th title="Strongest correlation found across all outcomes">Best |r|</th>
    <th>Strength</th>
    <th title="Suggested slider weight in Analyzer based on correlation strength">Suggested Wt</th>
  </tr></thead>`;

  // ── Point-Biserial mode ──
  if (mathRMethod === 'pointbiserial') {
    const rows = metrics.map(m => {
      const vd = draws.filter(d => m.get(d)!==null && m.get(d)!==undefined);
      if (vd.length < 3) return `<tr><td colspan="6" style="color:var(--text3);padding:8px;text-align:center">${m.label}: insufficient data</td></tr>`;
      const xs = vd.map(d => m.get(d));
      const xMean = xs.reduce((s,v)=>s+v,0)/xs.length;
      const xStd  = Math.sqrt(xs.reduce((s,v)=>s+Math.pow(v-xMean,2),0)/xs.length);
      if (xStd === 0) return `<tr><td colspan="6" style="color:var(--text3);padding:8px;text-align:center">${m.label}: no variance</td></tr>`;
      const n = vd.length;
      const ballRs = [];
      for (let ball=1; ball<=gameMaxBall; ball++) {
        const bin = vd.map(d => d.picks.includes(ball) ? 1 : 0);
        const n1 = bin.reduce((s,b)=>s+b,0), n0 = n-n1;
        if (!n1 || !n0) continue;
        const M1 = xs.reduce((s,v,i)=>bin[i]?s+v:s,0)/n1;
        const M0 = xs.reduce((s,v,i)=>!bin[i]?s+v:s,0)/n0;
        ballRs.push({ball, rpb: ((M1-M0)/xStd)*Math.sqrt((n1*n0)/(n*n))});
      }
      if (!ballRs.length) return `<tr><td colspan="6" style="color:var(--text3);padding:8px;text-align:center">${m.label}: no data</td></tr>`;
      const avgAbsR = ballRs.reduce((s,b)=>s+Math.abs(b.rpb),0)/ballRs.length;
      const strength = avgAbsR;
      const col   = strength>=0.15?'var(--ok)':strength>=0.08?'var(--warn)':strength>=0.03?'var(--accent)':'var(--text3)';
      const lbl   = strength>=0.15?'Strong':strength>=0.08?'Moderate':strength>=0.03?'Weak':'Negligible';
      const sugg  = Math.round(Math.min(50,Math.max(5,strength*250)));
      const topPos = [...ballRs].sort((a,b)=>b.rpb-a.rpb).slice(0,3);
      const topNeg = [...ballRs].sort((a,b)=>a.rpb-b.rpb).slice(0,3);
      const chip = (b,r) => `<span style="background:${r>=0?'rgba(6,214,160,.15)':'rgba(239,71,111,.15)'};color:${r>=0?'var(--ok)':'var(--danger)'};padding:1px 6px;border-radius:10px;font-size:10px;font-weight:700;font-family:monospace;display:inline-block;margin:1px">${b} <span style="opacity:.7">${r>=0?'+':''}${r.toFixed(2)}</span></span>`;
      return `<tr>
        <td style="font-weight:600;padding:6px 8px">${m.label}</td>
        <td style="padding:5px 8px">${topPos.map(({ball,rpb})=>chip(ball,rpb)).join('')}<div style="font-size:9px;color:var(--text3);margin-top:2px">↑ high-${m.unit} draws</div></td>
        <td style="padding:5px 8px">${topNeg.map(({ball,rpb})=>chip(ball,rpb)).join('')}<div style="font-size:9px;color:var(--text3);margin-top:2px">↑ low-${m.unit} draws</div></td>
        <td style="text-align:center;color:${col};font-weight:700;padding:6px 8px">${avgAbsR.toFixed(3)}</td>
        <td style="text-align:center;padding:6px 8px"><span style="color:${col};font-weight:700">${lbl}</span></td>
        <td style="text-align:center;padding:6px 8px"><span style="background:rgba(0,180,216,.15);color:var(--accent);padding:2px 8px;border-radius:10px;font-size:11px;font-weight:700">~${sugg}%</span></td>
      </tr>`;
    });

    el.innerHTML = `${sampleWarn}<div style="font-size:11px;color:var(--text2);margin-bottom:8px"><strong style="color:var(--accent)">Point-Biserial r</strong> — ${draws.length} draws · Shows which ball numbers are most associated with each weather condition</div>
    <div style="overflow-x:auto"><table style="font-size:12px;width:100%;border-collapse:collapse">
      <thead><tr style="border-bottom:2px solid var(--border)">
        <th style="text-align:left;padding:6px 8px">Parameter</th>
        <th>Top balls (high-value favored)</th>
        <th>Top balls (low-value favored)</th>
        <th title="Average absolute point-biserial r across all ball numbers">Avg |r_pb|</th>
        <th>Strength</th>
        <th>Suggested Wt</th>
      </tr></thead>
      <tbody>${rows.join('')}</tbody>
    </table></div>
    <div style="font-size:11px;color:var(--text3);margin-top:8px">Each ball number 1–${gameMaxBall} gets a point-biserial r measuring how weather affects its appearance frequency. Avg |r_pb| is averaged across all ${gameMaxBall} numbers.</div>`;
    renderMathWindChart(draws);
    return;
  }

  // ── Moving Window mode ──
  if (mathRMethod === 'moving') {
    const ws = _mathWindowSize;
    const rows = metrics.map(m => {
      const vd = draws.filter(d => m.get(d)!==null && m.get(d)!==undefined);
      const useN = Math.min(vd.length, ws);
      const sliced = vd.slice(-useN);
      const sliceXs = sliced.map(d => m.get(d));
      const rSum  = pearsonR(sliceXs, sliced.map(sumOf));
      const rHigh = pearsonR(sliceXs, sliced.map(highFrac));
      const rLow  = pearsonR(sliceXs, sliced.map(lowFrac));
      const rMax  = [rSum,rHigh,rLow].reduce((a,b)=>Math.abs(a)>Math.abs(b)?a:b,0);
      const strength = Math.abs(rMax);
      const col  = strength>=0.7?'var(--ok)':strength>=0.4?'var(--warn)':strength>=0.1?'var(--accent)':'var(--text3)';
      const lbl  = strength>=0.7?'Strong':strength>=0.4?'Moderate':strength>=0.1?'Weak':'Negligible';
      const sugg = Math.round(Math.min(50,Math.max(5,strength*70)));
      const fmtR = r => `<span style="font-family:monospace;color:${(r||0)>=0?'var(--ok)':'var(--danger)'}">${(r||0).toFixed(3)}</span>`;
      return `<tr>
        <td style="font-weight:600;padding:6px 8px">${m.label}</td>
        <td style="text-align:center;padding:6px 8px">${fmtR(rSum)}</td>
        <td style="text-align:center;padding:6px 8px">${fmtR(rHigh)}</td>
        <td style="text-align:center;padding:6px 8px">${fmtR(rLow)}</td>
        <td style="text-align:center;color:${col};font-weight:700;padding:6px 8px">${(rMax||0).toFixed(3)}</td>
        <td style="text-align:center;padding:6px 8px"><span style="color:${col};font-weight:700">${lbl}</span></td>
        <td style="text-align:center;padding:6px 8px"><span style="background:rgba(0,180,216,.15);color:var(--accent);padding:2px 8px;border-radius:10px;font-size:11px;font-weight:700">~${sugg}%</span></td>
      </tr>`;
    });
    el.innerHTML = `${sampleWarn}<div style="font-size:11px;color:var(--text2);margin-bottom:8px"><strong style="color:var(--accent)">Moving Window r</strong> — showing last <strong>${ws}</strong> draws · ${gameKey==='powerball'?'Tallahassee, FL':'Atlanta, GA'}</div>
    <div style="overflow-x:auto"><table style="font-size:12px;width:100%;border-collapse:collapse">${stdHeader}<tbody>${rows.join('')}</tbody></table></div>
    <div style="font-size:11px;color:var(--text3);margin-top:8px">r values computed over the most recent ${ws} draws with weather data. Adjust the window slider to see how correlations change.</div>`;
    renderMovingWindowChart(draws, ws);
    renderMathWindChart(draws);
    return;
  }

  // ── Standard modes: Pearson / Spearman / Kendall ──
  const rows = metrics.map(m => {
    const vd = draws.filter(d => m.get(d)!==null && m.get(d)!==undefined);
    const xs = vd.map(d => m.get(d));
    const rSum  = computeCorr(xs, vd.map(sumOf));
    const rAvg  = computeCorr(xs, vd.map(avgOf));
    const rHigh = computeCorr(xs, vd.map(highFrac));
    const rLow  = computeCorr(xs, vd.map(lowFrac));
    const rMax  = [rSum,rAvg,rHigh,rLow].reduce((a,b)=>Math.abs(a)>Math.abs(b)?a:b,0);
    const strength = Math.abs(rMax);
    const col  = strength>=0.7?'var(--ok)':strength>=0.4?'var(--warn)':strength>=0.1?'var(--accent)':'var(--text3)';
    const lbl  = strength>=0.7?'Strong':strength>=0.4?'Moderate':strength>=0.1?'Weak':'Negligible';
    const sugg = Math.round(Math.min(50,Math.max(5,strength*70)));
    const fmtR = r => `<span style="font-family:monospace;color:${(r||0)>=0?'var(--ok)':'var(--danger)'}">${(r||0).toFixed(3)}</span>`;
    return `<tr>
      <td style="font-weight:600;padding:6px 8px">${m.label}</td>
      <td style="text-align:center;padding:6px 8px">${fmtR(rSum)}</td>
      <td style="text-align:center;padding:6px 8px">${fmtR(rHigh)}</td>
      <td style="text-align:center;padding:6px 8px">${fmtR(rLow)}</td>
      <td style="text-align:center;color:${col};font-weight:700;padding:6px 8px">${(rMax||0).toFixed(3)}</td>
      <td style="text-align:center;padding:6px 8px"><span style="color:${col};font-weight:700">${lbl}</span></td>
      <td style="text-align:center;padding:6px 8px"><span style="background:rgba(0,180,216,.15);color:var(--accent);padding:2px 8px;border-radius:10px;font-size:11px;font-weight:700">~${sugg}%</span></td>
    </tr>`;
  });

  el.innerHTML = `${sampleWarn}<div style="font-size:11px;color:var(--text2);margin-bottom:8px">Method: <strong style="color:var(--accent)">${methodName}</strong> — <strong>${draws.length}</strong> draws with weather data (${gameKey==='powerball'?'Tallahassee, FL':'Atlanta, GA'})</div>
  <div style="overflow-x:auto"><table style="font-size:12px;width:100%;border-collapse:collapse">${stdHeader}<tbody>${rows.join('')}</tbody></table></div>
  <div style="font-size:11px;color:var(--text3);margin-top:8px">Computed fresh from your draw history using <strong>${methodName}</strong>. Higher |r| = stronger weather-number signal = recommend higher slider weight in the Analyzer.</div>`;

  renderMathWindChart(draws);
}

function renderMathWindChart(draws) {
  const el = document.getElementById('math-wind-chart');
  if (!el) return;
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW','CALM'];
  const buckets = {};
  dirs.forEach(d => buckets[d] = []);
  const overallMean = draws.reduce((s,d)=>s+(d.picks.reduce((a,n)=>a+n,0)/d.picks.length),0)/draws.length;
  draws.forEach(d => {
    if (d.wind && buckets[d.wind]) {
      const avg = d.picks.reduce((s,n)=>s+n,0)/d.picks.length;
      buckets[d.wind].push(avg);
    }
  });
  const dirData = dirs.filter(d => buckets[d].length >= 3).map(dir => {
    const vals = buckets[dir];
    const mean = vals.reduce((s,v)=>s+v,0)/vals.length;
    return { dir, mean, n: vals.length, dev: mean - overallMean };
  }).sort((a,b) => b.dev - a.dev);

  if (dirData.length === 0) {
    el.innerHTML = '<div style="color:var(--text3);font-size:12px">Not enough wind direction data yet.</div>';
    return;
  }

  const maxDev = Math.max(...dirData.map(d=>Math.abs(d.dev)));
  el.innerHTML = dirData.map(d => {
    const pct = maxDev > 0 ? Math.round(Math.abs(d.dev)/maxDev*100) : 0;
    const col = d.dev > 0 ? 'var(--ok)' : 'var(--danger)';
    return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px">
      <span style="width:40px;font-size:11px;font-weight:700;text-align:right">${d.dir}</span>
      <div style="flex:1;height:20px;background:var(--bg2);border-radius:3px;overflow:hidden;position:relative">
        <div style="position:absolute;${d.dev>=0?'left:50%':'right:50%'};width:${pct/2}%;height:100%;background:${col};opacity:.8"></div>
        <div style="position:absolute;left:50%;top:0;bottom:0;width:1px;background:var(--border)"></div>
      </div>
      <span style="width:55px;font-size:11px;font-family:monospace;color:${col}">${d.dev>0?'+':''}${d.dev.toFixed(1)}</span>
      <span style="width:30px;font-size:10px;color:var(--text3)">(${d.n})</span>
    </div>`;
  }).join('') + `<div style="font-size:11px;color:var(--text3);margin-top:8px">Deviation from overall mean avg number (${overallMean.toFixed(1)}). Positive = draws with this wind tend to have higher numbers. Count in parentheses.</div>`;
}

function showMathBand(band) {
  document.querySelectorAll('.math-band-btn').forEach(b => b.className = 'btn btn-outline btn-sm math-band-btn');
  document.querySelector(`.math-band-btn[data-band="${band}"]`).className = 'btn btn-primary btn-sm math-band-btn';

  const gameKey = mathGame === 'pb' ? 'powerball' : 'megamillions';
  const bands = { cold: d=>d.temp<55, cool: d=>d.temp>=55&&d.temp<65, mild: d=>d.temp>=65&&d.temp<75, warm: d=>d.temp>=75&&d.temp<85, hot: d=>d.temp>=85 };
  const draws = DD.filter(d=>d.game===gameKey && d.temp!==null && bands[band](d));
  const el = document.getElementById('math-pairs-result');

  if (draws.length < 5) {
    el.innerHTML = `<div style="color:var(--text3);font-size:12px">Fewer than 5 draws in this temperature band — not enough data.</div>`;
    return;
  }

  const pairs = {};
  draws.forEach(d => {
    const sorted = [...d.picks].sort((a,b)=>a-b);
    for (let i=0;i<sorted.length;i++) for (let j=i+1;j<sorted.length;j++) {
      const k = sorted[i]+'-'+sorted[j]; pairs[k]=(pairs[k]||0)+1;
    }
  });
  const top10 = Object.entries(pairs).sort((a,b)=>b[1]-a[1]).slice(0,10);
  const maxC = top10[0]?.[1]||1;

  el.innerHTML = `<div style="font-size:11px;color:var(--text2);margin-bottom:8px">Top pairs in ${draws.length} draws (${band} weather):</div>
  <div style="display:flex;flex-wrap:wrap;gap:8px">
    ${top10.map(([pair,count]) => `
      <div style="background:var(--bg2);border:1px solid var(--border);border-radius:8px;padding:8px 12px;text-align:center;min-width:80px">
        <div style="font-family:monospace;font-weight:700;font-size:13px;color:var(--accent)">${pair}</div>
        <div style="font-size:11px;color:var(--text2);margin-top:2px">${count}× (${Math.round(count/draws.length*100)}%)</div>
        <div style="height:3px;background:var(--bg);border-radius:2px;margin-top:4px;overflow:hidden">
          <div style="height:100%;width:${Math.round(count/maxC*100)}%;background:var(--accent)"></div>
        </div>
      </div>`).join('')}
  </div>`;
}



function calcQuickTax(game) {
  const jackpot = parseFloat(document.getElementById('qtax-jackpot-'+game)?.value) || 0;
  // Prefer user-entered cash, else use live DB value, else ratio fallback
  const cashInput = parseFloat(document.getElementById('qtax-cash-'+game)?.value) || 0;
  const liveJPCash = game==='pb' ? _liveJP.pbCash : _liveJP.mmCash;
  const cash = cashInput > 0 ? cashInput : (liveJPCash > 0 ? liveJPCash : jackpot * CASH_R);
  const state = parseFloat(document.getElementById('qtax-state-'+game)?.value) || 0;
  const fedWh = cash * 0.24, fedAdd = cash * 0.13, stateTax = cash * (state/100);
  const net = cash - fedWh - fedAdd - stateTax;
  const ratio = jackpot > 0 ? (cash/jackpot*100).toFixed(1) : '—';
  const el = document.getElementById('qtax-result-'+game);
  const fmtM = n => !n||!isFinite(n) ? '—' : n>=1e9?'$'+(n/1e9).toFixed(2)+'B':'$'+(n/1e6).toFixed(1)+'M';
  if (el) el.innerHTML = jackpot > 0
    ? `Cash option: <strong style="color:var(--accent)">${fmtM(cash)}</strong> (${ratio}% of jackpot)&ensp;→&ensp;After fed 37% + state ${state}%: <strong style="color:var(--ok)">${fmtM(net)}</strong>`
    : `<span style="color:var(--text3)">Waiting for jackpot data from database…</span>`;
  window.track('calc_quick_tax', { game });
}



// ── SCANNER GAME TOGGLE ──
let _scanGame = 'pb';
function setScanGame(g) {
  _scanGame = g;
  document.getElementById('scan-tab-pb').className = g==='pb'?'btn btn-primary':'btn btn-outline';
  document.getElementById('scan-tab-mm').className = g==='mm'?'btn btn-primary':'btn btn-outline';
  const maxBall = g==='pb'?69:70, maxSpecial = g==='pb'?26:24, spLabel = g==='pb'?'PB':'MB';
  document.querySelectorAll('.mnum').forEach(i=>{i.max=maxBall;i.placeholder='N'+i.placeholder.replace(/\D/g,'');});
  document.querySelectorAll('.mpb').forEach(i=>{i.max=maxSpecial;i.placeholder=spLabel;});
  // Update scanner page banner elements
  const banner = document.getElementById('scanner-game-banner');
  const nameEl = document.getElementById('scanner-game-name');
  if (banner) banner.textContent = g==='pb' ? '🔴 Powerball® Ticket' : '🔵 Mega Millions® Ticket';
  if (nameEl) nameEl.textContent = g==='pb' ? 'Powerball®' : 'Mega Millions®';
}

function setRngGame(g) {
  const sel = document.getElementById('rng-game');
  if (sel) sel.value = g;
}

// Base (un-multiplied) prize table for a game on a given draw date.
// Mega Millions changed on 2025-04-08: $5 ticket, Mega Ball 1–24, and every
// play carries a 2×–10× multiplier, so post-change prizes are base × multiplier.
function prizeTable(game, iso) {
  if (game === 'powerball' || game === 'pb')
    return { '5+1':'JACKPOT','5+0':1000000,'4+1':50000,'4+0':100,'3+1':100,'3+0':7,'2+1':7,'1+1':4,'0+1':4 };
  if (!iso || iso >= '2025-04-08')
    return { '5+1':'JACKPOT','5+0':1000000,'4+1':10000,'4+0':500,'3+1':200,'3+0':10,'2+1':10,'1+1':7,'0+1':5 };
  return { '5+1':'JACKPOT','5+0':1000000,'4+1':10000,'4+0':500,'3+1':200,'3+0':10,'2+1':10,'1+1':4,'0+1':2 };
}
const mmHasBuiltInMultiplier = (game, iso) => (game === 'megamillions' || game === 'mm') && (!iso || iso >= '2025-04-08');

function checkAllManual() {
  const gameKey = _scanGame === 'pb' ? 'powerball' : 'megamillions';
  let drawDate = document.getElementById('manual-draw-date')?.value;
  const maxRow = 5;
  let usedLatest = false;
  if (!drawDate) {                       // blank date → check against the latest draw
    const latest = DD.find(d => d.game === gameKey);
    if (latest) { drawDate = latest.iso; usedLatest = true; }
  }
  const prizes = prizeTable(gameKey, drawDate);
  const multNote = mmHasBuiltInMultiplier(gameKey, drawDate);

  let winDraw = null;
  if (drawDate) winDraw = DD.find(d=>d.game===gameKey && d.iso===drawDate);

  let html = '';
  if (winDraw) {
    html += `<div style="padding:8px;background:rgba(0,180,216,.08);border-radius:7px;margin-bottom:10px;font-size:12px">
      <strong>Winning numbers (${winDraw.date}${usedLatest ? ' — latest draw; set the date on your ticket if different' : ''}):</strong>
      <span style="font-family:monospace;font-weight:700;margin-left:6px">${winDraw.picks.join(' - ')} | <span style="color:var(--danger)">${winDraw.pb}</span></span>
    </div>`;
  } else if (drawDate) {
    html += `<div style="padding:8px;background:rgba(255,209,102,.08);border-radius:7px;margin-bottom:10px;font-size:12px;color:var(--warn)">⚠️ No draw found for ${drawDate} in the database. Check the date or run the draw-feed to update.</div>`;
  }

  let totalWon = 0;
  for (let row = 1; row <= maxRow; row++) {
    const nums = [...document.querySelectorAll('.mnum-'+row)].map(i=>+i.value).filter(n=>n>0);
    const special = +document.querySelector('.mpb-'+row)?.value;
    if (nums.length < 5) continue;
    if (winDraw) {
      const mainMatch = nums.filter(n=>winDraw.picks.includes(n)).length;
      const specMatch = special === winDraw.pb ? 1 : 0;
      const key = `${mainMatch}+${specMatch}`;
      const base = prizes[key];
      let hitJackpot = false;
      if (base === 'JACKPOT') hitJackpot = true;
      else if (base) totalWon += multNote ? base * 2 : base; // MM: at least 2× (your ticket shows the exact multiplier)
      const prize = base === 'JACKPOT' ? 'JACKPOT' : base ? ('$' + base.toLocaleString() + (multNote ? ' × your multiplier' : '')) : null;
      if (hitJackpot) window._scanJackpot = true;
      html += `<div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border);font-size:12px">
        <span style="color:var(--text3);width:20px">#${row}</span>
        <span style="font-family:monospace">${nums.join('-')} | ${special||'?'}</span>
        <span style="margin-left:auto;color:${prize?'var(--ok)':'var(--text3)'};font-weight:700">${prize||'No win'} (${mainMatch}+${specMatch})</span>
      </div>`;
    } else {
      html += `<div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border);font-size:12px">
        <span style="color:var(--text3);width:20px">#${row}</span>
        <span style="font-family:monospace">${nums.join('-')} | ${special||'?'}</span>
        <span style="margin-left:auto;font-size:11px;color:var(--text3)">Select draw date to check prize</span>
      </div>`;
    }
  }
  if (!html.includes('border-bottom')) html = '<div style="color:var(--text3);font-size:12px">No valid tickets entered.</div>';
  if (winDraw && window._scanJackpot) html += `<div style="margin-top:10px;font-size:14px;font-weight:700;color:var(--ok)">🏆 Jackpot match — sign the ticket and contact your state lottery.</div>`;
  if (winDraw && totalWon > 0) html += `<div style="margin-top:10px;font-size:14px;font-weight:700;color:var(--ok)">🎉 Total won this draw: ${multNote ? 'at least ' : ''}$${totalWon.toLocaleString()}</div>` +
    (multNote ? `<div style="font-size:11px;color:var(--text3)">Mega Millions prizes are multiplied 2×–10× by the multiplier printed on each play; this total assumes the minimum 2×.</div>` : '');
  window._scanJackpot = false;
  const resultEl = document.getElementById('manual-check-results');
  if (resultEl) { resultEl.innerHTML = html; resultEl.style.display = ''; }
}



// ── POOL V2: PAYOUT PRIZE TABLE FILL ──────────────────────────────────────
function updateAnalyzerPrizeTables() {
  // Read current jackpot amounts from the home page (set by the draw_news
  // fetch). The analyzer cards show just the headline jackpot number; the
  // detailed cash/net breakdown lives on the calculator page.
  const pbJpEl = document.getElementById('home-pb-jackpot');
  const mmJpEl = document.getElementById('home-mm-jackpot');
  function parseM(txt) {
    if (!txt) return null;
    const n = parseFloat(String(txt).replace(/[$,BMK]/gi, ''));
    if (!isFinite(n)) return null;
    if (/B/i.test(txt)) return n * 1e9;
    if (/M/i.test(txt)) return n * 1e6;
    if (/K/i.test(txt)) return n * 1e3;
    return n;
  }
  const fmtJp = (n) => {
    if (n == null) return '—';
    if (n >= 1e9) return '$' + (Math.round(n / 1e8) / 10) + 'B';
    return '$' + Math.round(n / 1e6) + 'M';
  };

  const pbJp = parseM(pbJpEl?.textContent);
  const mmJp = parseM(mmJpEl?.textContent);

  // Powerball analyzer card (#prize-jackpot)
  const pbTarget = document.getElementById('prize-jackpot');
  if (pbTarget && pbJp) pbTarget.textContent = fmtJp(pbJp);

  // Mega Millions analyzer card (#mm-prize-jackpot)
  const mmTarget = document.getElementById('mm-prize-jackpot');
  if (mmTarget && mmJp) mmTarget.textContent = fmtJp(mmJp);
}

// ══════════════════════════════════════════════════════════════
// SUPABASE AUTH — Email Magic Link + Google OAuth
// ══════════════════════════════════════════════════════════════

// ── Google OAuth sign-in ──────────────────────────────────────
async function authSignInGoogle() {
  const btn = document.getElementById('btn-google-signin');
  if (btn) { btn.disabled = true; btn.textContent = '⏳ Redirecting…'; }
  const { error } = await _supa.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.href }
  });
  if (error) {
    showToast('Google sign-in error: ' + error.message, 4000);
    if (btn) { btn.disabled = false; btn.innerHTML = '<img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" style="width:18px;height:18px"> Continue with Google'; }
  }
}

// ── Email magic link ──────────────────────────────────────────
async function authSendMagicLink() {
  const email = (document.getElementById('auth-email-input')?.value || '').trim();
  const msgEl = document.getElementById('auth-email-msg');
  const errEl = document.getElementById('auth-email-err');
  const btn   = document.getElementById('btn-magic-link');
  if (msgEl) msgEl.style.display = 'none';
  if (errEl) errEl.style.display = 'none';
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    if (errEl) { errEl.textContent = '⚠️ Enter a valid email address.'; errEl.style.display = 'block'; }
    return;
  }
  if (btn) { btn.disabled = true; btn.textContent = '⏳ Sending…'; }
  // Bot check (Cloudflare Turnstile via assets/signup.js) once a site key is set;
  // Supabase Auth verifies the token when CAPTCHA protection is turned on.
  let captchaToken = null;
  if (window.cnTurnstile && window.cnTurnstile.siteKey) {
    captchaToken = await window.cnTurnstile.getToken(document.getElementById('auth-ts'), 'login');
    if (!captchaToken) {
      if (btn) { btn.disabled = false; btn.textContent = '📧 Send Link'; }
      if (errEl) { errEl.textContent = '⚠️ Please complete the quick bot check, then press Send Link again.'; errEl.style.display = 'block'; }
      return;
    }
  }
  const { error } = await _supa.auth.signInWithOtp({
    email,
    options: Object.assign({ emailRedirectTo: window.location.href }, captchaToken ? { captchaToken } : {})
  });
  if (btn) { btn.disabled = false; btn.textContent = '📧 Send Link'; }
  if (error) {
    if (errEl) { errEl.textContent = '❌ ' + error.message; errEl.style.display = 'block'; }
  } else {
    if (msgEl) msgEl.style.display = 'block';
    if (document.getElementById('auth-email-input')) document.getElementById('auth-email-input').value = '';
  }
}

// ── Sign out ──────────────────────────────────────────────────
async function authSignOut() {
  await _supa.auth.signOut();
  showToast('Signed out');
}

// ── Update UI for auth state ──────────────────────────────────
function authUpdateUI(session) {
  const loggedOut = document.getElementById('auth-logged-out');
  const loggedIn  = document.getElementById('auth-logged-in');
  if (!loggedOut || !loggedIn) return;
  if (session?.user) {
    loggedOut.style.display = 'none';
    loggedIn.style.display  = 'block';
    const u = session.user;
    const displayName = u.user_metadata?.full_name || u.user_metadata?.name || u.email?.split('@')[0] || 'You';
    const avatarEl = document.getElementById('user-avatar');
    const nameEl   = document.getElementById('user-name');
    const emailEl  = document.getElementById('user-email');
    if (nameEl)  nameEl.textContent  = displayName;
    if (emailEl) emailEl.textContent = u.email || '';
    // Avatar: photo from Google, or initial letter
    if (avatarEl) {
      const photo = u.user_metadata?.avatar_url || u.user_metadata?.picture;
      if (photo) {
        avatarEl.innerHTML = `<img src="${photo}" alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%">`;
      } else {
        avatarEl.textContent = displayName.charAt(0).toUpperCase();
        avatarEl.innerHTML = displayName.charAt(0).toUpperCase();
      }
    }
    // Load user data from Supabase
    authUpsertProfile(u);
    authLoadTickets(u.id);
    authLoadPresets(u.id);
    // Enable sign-in-only controls (the email / phone alert boxes work without an account)
    document.querySelectorAll('#page-account [data-needs-auth][disabled]').forEach(el => el.removeAttribute('disabled'));
  } else {
    loggedOut.style.display = 'block';
    loggedIn.style.display  = 'none';
    // Reset saved lists
    const tl = document.getElementById('saved-tickets-list');
    const pl = document.getElementById('saved-presets-list');
    if (tl) tl.innerHTML = '<div style="color:var(--text3);font-size:12px;text-align:center;padding:20px 0">Sign in to see your saved tickets</div>';
    if (pl) pl.innerHTML = '<div style="color:var(--text3);font-size:12px;text-align:center;padding:20px 0">Sign in to see saved analyzer presets</div>';
    // Re-disable sign-in-only controls
    document.querySelectorAll('#page-account [data-needs-auth]').forEach(el => el.setAttribute('disabled',''));
  }
}

// ── Upsert user_profiles on sign-in ──────────────────────────
async function authUpsertProfile(user) {
  try {
    await _supa.from('user_profiles').upsert({
      id:           user.id,
      display_name: user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || null,
      email:        user.email,
      updated_at:   new Date().toISOString()
    }, { onConflict: 'id', ignoreDuplicates: false });
  } catch(e) { console.warn('profile upsert error:', e); }
}

// ── Load saved tickets ────────────────────────────────────────
async function authLoadTickets(userId) {
  const el = document.getElementById('saved-tickets-list');
  if (!el) return;
  el.innerHTML = '<div style="color:var(--text3);font-size:12px;padding:10px 0">⏳ Loading…</div>';
  try {
    const { data, error } = await _supa
      .from('ticket_scans')
      .select('id,game,scanned_at,numbers,special_ball,source')
      .eq('user_id', userId)
      .order('scanned_at', { ascending: false })
      .limit(20);
    if (error || !data?.length) {
      el.innerHTML = '<div style="color:var(--text3);font-size:12px;text-align:center;padding:20px 0">No saved tickets yet — scan or enter a ticket to save it.</div>';
      return;
    }
    el.innerHTML = data.map(t => {
      const d = t.scanned_at ? new Date(t.scanned_at).toLocaleDateString() : '—';
      const nums = Array.isArray(t.numbers) ? t.numbers.join(', ') : (t.numbers || '—');
      const game = t.game === 'powerball' ? '🔴 PB' : t.game === 'megamillions' ? '🔵 MM' : t.game || '?';
      const sb   = t.special_ball ? ' · <strong>' + t.special_ball + '</strong>' : '';
      return `<div style="padding:8px 10px;background:var(--bg2);border-radius:7px;border:1px solid var(--border);margin-bottom:6px;font-size:12px">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px">
          <span style="font-weight:700;color:var(--text)">${game} · ${nums}${sb}</span>
          <span style="color:var(--text3);white-space:nowrap">${d}</span>
        </div>
        ${t.source ? `<div style="font-size:10px;color:var(--text3);margin-top:2px">via ${t.source}</div>` : ''}
      </div>`;
    }).join('');
  } catch(e) {
    el.innerHTML = '<div style="color:var(--danger);font-size:12px;padding:8px 0">⚠️ Could not load tickets.</div>';
  }
}

// ── Load saved presets ────────────────────────────────────────
async function authLoadPresets(userId) {
  const el = document.getElementById('saved-presets-list');
  if (!el) return;
  el.innerHTML = '<div style="color:var(--text3);font-size:12px;padding:10px 0">⏳ Loading…</div>';
  try {
    const { data, error } = await _supa
      .from('metric_presets')
      .select('id,preset_name,game,created_at,weights')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(20);
    if (error || !data?.length) {
      el.innerHTML = '<div style="color:var(--text3);font-size:12px;text-align:center;padding:20px 0">No saved presets yet — save a weight configuration from the Analyzer.</div>';
      return;
    }
    el.innerHTML = data.map(p => {
      const d    = p.created_at ? new Date(p.created_at).toLocaleDateString() : '—';
      const game = p.game === 'powerball' ? '🔴 PB' : p.game === 'megamillions' ? '🔵 MM' : (p.game || '');
      const ws = JSON.stringify(p.weights || {}).replace(/"/g,'&quot;');
      return `<div style="padding:8px 10px;background:var(--bg2);border-radius:7px;border:1px solid var(--border);margin-bottom:6px;font-size:12px;display:flex;align-items:center;gap:8px">
        <div style="flex:1">
          <div style="font-weight:700;color:var(--text)">${game} ${p.preset_name || p.name || 'Untitled'}</div>
          <div style="font-size:10px;color:var(--text3);margin-top:1px">${d}</div>
        </div>
        <button class="btn btn-outline" style="font-size:10px;padding:3px 9px;white-space:nowrap" onclick="applySupabasePreset(${JSON.stringify(p.weights||{}).replace(/"/g,'&quot;')},${JSON.stringify(p.game||'').replace(/"/g,'&quot;')})">Apply</button>
      </div>`;
    }).join('');
  } catch(e) {
    el.innerHTML = '<div style="color:var(--danger);font-size:12px;padding:8px 0">⚠️ Could not load presets.</div>';
  }
}

// ── Bootstrap auth listener ───────────────────────────────────
(async function initAuth() {
  // Handle OAuth redirect token in URL hash/query (magic link & Google callback)
  const { data: { session: initialSession } } = await _supa.auth.getSession();
  authUpdateUI(initialSession);
  // Pool Manager: unlock + load the account's saved pool, or show the sign-in gate
  gpGateApply(initialSession);

  _supa.auth.onAuthStateChange((_event, session) => {
    authUpdateUI(session);
    // Clean up URL after OAuth/magic-link redirect
    if (_event === 'SIGNED_IN' && (window.location.hash || window.location.search.includes('access_token'))) {
      try { window.history.replaceState({}, document.title, window.location.pathname + window.location.search.replace(/[?&](access_token|code)=[^&]*/g, '')); } catch(_) {}
      if (session?.user) showToast('✅ Signed in as ' + (session.user.email || 'you'), 3000);
    }
    if (_event === 'SIGNED_IN') gpGateApply(session);
    if (_event === 'SIGNED_OUT') {
      // The pool belongs to the account: don't leave a copy on this device
      gpClearLocal();
      _gpSyncedFor = null;
      showToast('Signed out', 2000);
      if (document.body.dataset.page === 'groupplay') setTimeout(() => location.reload(), 600);
    }
  });
})();

// ══════════════════════════════════════════════════════════════
// CLOUD SYNC HELPERS — tickets, presets, pools
// ══════════════════════════════════════════════════════════════

// Returns the current signed-in user or null if logged out.
async function currentUser() {
  try {
    const { data: { session } } = await _supa.auth.getSession();
    return session?.user || null;
  } catch (_) { return null; }
}

// ── SCANNER: save the 5 manual-entry rows to ticket_scans ─────
async function saveScannedTickets() {
  const msgEl = document.getElementById('save-tickets-msg');
  const setMsg = (t, c) => { if (msgEl) { msgEl.textContent = t; msgEl.style.color = c || 'var(--text2)'; } };

  const user = await currentUser();
  if (!user) {
    setMsg('🔒 Sign in on your Account page to save tickets to the cloud.', 'var(--warn)');
    return;
  }

  const game = _scanGame === 'pb' ? 'powerball' : 'megamillions';
  const drawDate = document.getElementById('manual-draw-date')?.value || null;

  const rows = [];
  for (let row = 1; row <= 5; row++) {
    const nums = [...document.querySelectorAll('.mnum-' + row)]
      .map(i => +i.value)
      .filter(n => n > 0);
    const special = +document.querySelector('.mpb-' + row)?.value || null;
    if (nums.length === 5 && special) {
      rows.push({
        user_id:         user.id,
        game,
        numbers:         nums,
        special_ball:    special,
        scan_method:     'manual',
        draw_date_check: drawDate || null,
        scanned_at:      new Date().toISOString()
      });
    }
  }
  if (!rows.length) { setMsg('⚠️ Enter at least one complete ticket (5 numbers + special).', 'var(--danger)'); return; }

  setMsg('⏳ Saving ' + rows.length + ' ticket(s)…');
  const { error } = await _supa.from('ticket_scans').insert(rows);
  if (error) {
    setMsg('❌ ' + error.message, 'var(--danger)');
    return;
  }
  setMsg('✅ Saved ' + rows.length + ' ticket(s) — view on your Account page.', 'var(--ok)');
  if (typeof window.track === 'function') {
    window.track('save_tickets', { game, count: rows.length, scan_method: 'manual' });
  }
}

// ── GROUP PLAY: the pool is saved to the signed-in user's account ─────────
// The Pool Manager is free but needs a sign-in: group-play.html shows a gate
// (gpGate*) until the visitor is signed in. localStorage keeps a working copy
// so the tool stays instant; user_pools (one row per user, row-level security:
// owner only) is the saved copy. 'coldnumbers_gp_owner' records whose pool the
// local copy is, so a different account signing in on the same browser never
// sees, or uploads, someone else's pool. Signing out clears the local copy.
const GP_KEYS = ['coldnumbers_gp_pool', 'coldnumbers_gp_rules', 'coldnumbers_gp_members', 'coldnumbers_gp_draws',
  'coldnumbers_gp_current', 'coldnumbers_gp_log', 'coldnumbers_gp_terms', 'coldnumbers_gp_demo_seeded'];
const GP_OWNER = 'coldnumbers_gp_owner';
let _gpSyncedFor = null;   // user id, once that account's saved pool has been loaded
let _cloudPushTimer = null;

function gpIsDemo() {
  return !!(gpPool && gpPool.name === 'Demo Office Pool' && gpMembers.length &&
    gpMembers.every(m => m.id >= 1001 && m.id <= 1006));
}
function gpClearLocal() {
  GP_KEYS.concat(GP_OWNER).forEach(k => { try { localStorage.removeItem(k); } catch (_) {} });
}
function gpSaveState(state) {
  const el = document.getElementById('gp-save-state');
  if (!el) return;
  el.textContent = state === 'saving' ? 'Saving…' : state === 'saved' ? '✓ All changes saved'
    : state === 'error' ? "⚠️ Couldn't save — check your connection" : '';
  el.style.color = state === 'error' ? 'var(--danger)' : '';
}

function cloudSchedulePoolPush() {
  // Only once the account's saved copy is loaded (pushing earlier could
  // overwrite it with the sample pool); debounced: many edits = one save.
  if (!_gpSyncedFor) return;
  clearTimeout(_cloudPushTimer);
  gpSaveState('saving');
  _cloudPushTimer = setTimeout(cloudSavePool, 800);
}

// Returns true once the pool is saved to the account.
async function cloudSavePool() {
  const user = await currentUser();
  if (!user || user.id !== _gpSyncedFor) return false;
  try {
    const { error } = await _supa.from('user_pools').upsert({
      user_id: user.id,
      pool:    Object.assign({}, gpPool || {}, { customTerms: gpCustomTerms || '' }),
      rules:   gpRules   || {},
      members: gpMembers || [],
      draws:   gpDrawHistory || [],
      current: gpCurrentDraw || {},
      log:     gpLog     || [],
      updated_at: new Date().toISOString()
    }, { onConflict: 'user_id' });
    if (error) console.warn('[cloud] pool save error:', error.message);
    gpSaveState(error ? 'error' : 'saved');
    return !error;
  } catch (e) { console.warn('[cloud] pool save threw:', e); gpSaveState('error'); return false; }
}

// Loads the account's saved pool into memory + localStorage. Returns true when
// the account has one, false when it doesn't yet; throws on a network error.
async function cloudLoadPool(userId) {
  const { data, error } = await _supa
    .from('user_pools')
    .select('pool,rules,members,draws,current,log,updated_at')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return false;
  const { customTerms, ...pool } = data.pool || {};
  if (Object.keys(pool).length) gpPool = pool;
  gpCustomTerms = customTerms || '';
  if (data.rules && Object.keys(data.rules).length) gpRules = data.rules;
  gpMembers     = data.members || [];
  gpDrawHistory = data.draws   || [];
  if (data.current && Object.keys(data.current).length) gpCurrentDraw = data.current;
  gpLog         = data.log     || [];
  // Local working copy (no save is triggered: _gpSyncedFor isn't set yet)
  localStorage.setItem('coldnumbers_gp_pool',    JSON.stringify(gpPool));
  localStorage.setItem('coldnumbers_gp_rules',   JSON.stringify(gpRules));
  localStorage.setItem('coldnumbers_gp_members', JSON.stringify(gpMembers));
  localStorage.setItem('coldnumbers_gp_draws',   JSON.stringify(gpDrawHistory));
  localStorage.setItem('coldnumbers_gp_current', JSON.stringify(gpCurrentDraw));
  localStorage.setItem('coldnumbers_gp_log',     JSON.stringify(gpLog));
  localStorage.setItem('coldnumbers_gp_terms',   gpCustomTerms);
  localStorage.setItem('coldnumbers_gp_demo_seeded', '1');
  return true;
}

// Make the local copy this account's pool. Returns 'loaded' | 'empty' |
// 'reload' (another account's pool was cleared; reload to start clean) |
// 'error'.
async function gpSyncForUser(user) {
  if (_gpSyncedFor === user.id) return 'loaded';
  const owner = localStorage.getItem(GP_OWNER);
  if (owner && owner !== user.id) {
    gpClearLocal();
    localStorage.setItem(GP_OWNER, user.id);
    return 'reload';
  }
  let loaded;
  try { loaded = await cloudLoadPool(user.id); }
  catch (e) { console.warn('[cloud] pool load failed:', e); return 'error'; }
  localStorage.setItem(GP_OWNER, user.id);
  _gpSyncedFor = user.id;
  // First sign-in on a browser that already had a real pool (made before a
  // sign-in was needed): keep it by saving it to the account.
  if (!loaded && !owner && (gpMembers.length || gpDrawHistory.length) && !gpIsDemo()) cloudSavePool();
  return loaded ? 'loaded' : 'empty';
}

// ── Pool Manager sign-in gate (group-play.html) ───────────────────────────
let _googleEnabled = null;
async function authGoogleEnabled() {
  // Shows "Continue with Google" only once Google sign-in is turned on in
  // Supabase (Authentication → Providers → Google).
  if (_googleEnabled !== null) return _googleEnabled;
  try {
    const r = await fetch(SITE_CONFIG.supabaseUrl + '/auth/v1/settings', { headers: { apikey: SITE_CONFIG.supabaseKey } });
    const j = r.ok ? await r.json() : {};
    _googleEnabled = !!(j.external && j.external.google);
  } catch (_) { _googleEnabled = false; }
  return _googleEnabled;
}

function gpGateShow(state, user) {
  const $ = id => document.getElementById(id);
  const gate = $('gp-gate'), app = $('gp-app');
  if (!gate || !app) return;
  const open = state === 'open';
  gate.hidden = open;
  if ($('gp-gate-checking')) $('gp-gate-checking').hidden = state !== 'checking' && state !== 'error';
  if ($('gp-gate-signin')) $('gp-gate-signin').hidden = state !== 'signin';
  if (state === 'error' && $('gp-gate-checking')) $('gp-gate-checking').textContent = "⚠️ Couldn't load your saved pool. Check your connection and refresh the page.";
  app.classList.toggle('gp-locked', !open);
  if (open) { app.removeAttribute('inert'); app.removeAttribute('aria-hidden'); }
  else { app.setAttribute('inert', ''); app.setAttribute('aria-hidden', 'true'); }
  if ($('gp-account-bar')) $('gp-account-bar').hidden = !open;
  if ($('gp-account-email') && user) $('gp-account-email').textContent = user.email ? '(' + user.email + ')' : '';
  if ($('gp-demo-banner')) $('gp-demo-banner').hidden = !(open && gpIsDemo());
  if (open) gpShareRender(); else if ($('gp-share-panel')) $('gp-share-panel').hidden = true;
  if (state === 'signin') {
    authGoogleEnabled().then(on => { const g = $('gp-google-btn'); if (g) g.hidden = !on; });
    if (typeof window.track === 'function') window.track('pool_signin_prompt', {});
  }
}

let _gpGateFor = undefined, _gpGateRun = null;
function gpGateApply(session) {
  if (document.body.dataset.page !== 'groupplay') return null;
  const uid = session?.user?.id || null;
  if (_gpGateRun && _gpGateFor === uid) return _gpGateRun;   // already applied for this state
  _gpGateFor = uid;
  _gpGateRun = (async () => {
    if (!uid) { gpGateShow('signin'); return; }
    gpGateShow('checking');
    const r = await gpSyncForUser(session.user);
    if (r === 'reload') { location.reload(); return; }
    if (r === 'error') { gpGateShow('error'); return; }
    if (typeof gpInitRedesign === 'function') gpInitRedesign();   // re-render with the account's pool
    gpGateShow('open', session.user);
    if (r === 'empty' && typeof window.track === 'function') window.track('pool_first_open', {});
  })();
  return _gpGateRun;
}

// The sample pool → a fresh pool of your own (no confirm needed: nothing to lose)
function gpStartOwnPool() {
  gpResetToEmptyPool('My Lottery Pool');
  const b = document.getElementById('gp-demo-banner'); if (b) b.hidden = true;
  showToast('New pool started — start with the setup questions', 3000);
  const card = document.getElementById('gp-setup-card'); if (card) card.scrollIntoView({ behavior: 'smooth', block: 'start' });
}
function gpResetToEmptyPool(name) {
  gpPool = { name: name || 'New Lottery Pool', game: 'pb', ticketPrice: 2, ticketsPerPerson: 1 };
  gpRules = { multiplier: 'yes', split: 'equal', miss: 'skip', payout: 'payout', payoutThreshold: 50, jackpotAfter: 'end', saved: false, v: 2 };
  gpMembers = [];
  gpDrawHistory = [];
  gpCurrentDraw = { v: 2, kittyStart: 0 };
  gpLog = [];
  localStorage.setItem('coldnumbers_gp_pool',    JSON.stringify(gpPool));
  localStorage.setItem('coldnumbers_gp_rules',   JSON.stringify(gpRules));
  localStorage.setItem('coldnumbers_gp_members', JSON.stringify(gpMembers));
  localStorage.setItem('coldnumbers_gp_draws',   JSON.stringify(gpDrawHistory));
  localStorage.setItem('coldnumbers_gp_current', JSON.stringify(gpCurrentDraw));
  localStorage.setItem('coldnumbers_gp_log',     JSON.stringify(gpLog));
  localStorage.setItem('coldnumbers_gp_demo_seeded', '1');
  if (typeof gpInitRedesign === 'function') gpInitRedesign();
  gpShareRender();   // a new pool isn't shared: the old members' link stops working
}

// ── Members' link: a read-only view of the pool (pool.html) ───────────────
// The token lives in the organizer's own saved pool (pool.shareToken), so only
// they can create or remove it; supabase/functions/pool-share looks the pool up
// by owner id + token and returns members-safe fields only. The link carries
// both after '#', which browsers never send to the server or to analytics.
function gpShareUrl() {
  if (!gpPool || !gpPool.shareToken || !_gpSyncedFor) return '';
  return location.origin + '/pool.html#' + _gpSyncedFor + '.' + gpPool.shareToken;
}
function gpNewShareToken() {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return btoa(String.fromCharCode.apply(null, b)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function gpShareRender(msg) {
  const $ = id => document.getElementById(id);
  const url = gpShareUrl(), panel = $('gp-share-panel'), btn = $('gp-share-btn');
  if (btn) btn.textContent = url ? "🔗 Members' link" : '🔗 Share with members';
  if (!panel) return;
  if (!url) { panel.hidden = true; if (btn) btn.setAttribute('aria-expanded', 'false'); return; }
  if ($('gp-share-url')) $('gp-share-url').value = url;
  if ($('gp-share-open')) $('gp-share-open').href = url;
  if ($('gp-share-email')) $('gp-share-email').href = 'mailto:?subject=' + encodeURIComponent((gpPool.name || 'Our lottery pool') + ' — tickets & results') +
    '&body=' + encodeURIComponent("Here's our lottery pool: the tickets, results, who's paid up and the rules. It's kept up to date after every draw.\n\n" + url + '\n');
  if ($('gp-share-msg')) $('gp-share-msg').textContent = msg || '';
}
async function gpShareToggle() {
  const panel = document.getElementById('gp-share-panel'), btn = document.getElementById('gp-share-btn');
  if (!panel) return;
  if (!panel.hidden) { panel.hidden = true; if (btn) btn.setAttribute('aria-expanded', 'false'); return; }
  if (gpIsDemo()) { showToast("This is the sample pool — start your own pool first, then share it"); return; }
  if (!_gpSyncedFor) { showToast('Sign in to share your pool'); return; }
  let created = false;
  if (!gpPool.shareToken) {
    gpPool.shareToken = gpNewShareToken();
    localStorage.setItem('coldnumbers_gp_pool', JSON.stringify(gpPool));
    clearTimeout(_cloudPushTimer);           // save now rather than in 800 ms
    if (!(await cloudSavePool())) {
      delete gpPool.shareToken;
      localStorage.setItem('coldnumbers_gp_pool', JSON.stringify(gpPool));
      clearTimeout(_cloudPushTimer); gpSaveState('error');
      showToast("Couldn't make the link — check your connection and try again", 4000);
      return;
    }
    created = true;
    if (typeof window.track === 'function') window.track('pool_share_create', { members: gpMembers.length });
  }
  gpShareRender(created ? '✓ Link ready — it shows your latest changes within a minute.' : '');
  panel.hidden = false;
  if (btn) btn.setAttribute('aria-expanded', 'true');
}
async function gpShareCopy() {
  const url = gpShareUrl(), input = document.getElementById('gp-share-url');
  if (!url) return;
  let ok = false;
  try { await navigator.clipboard.writeText(url); ok = true; }
  catch (_) { try { input.focus(); input.select(); ok = document.execCommand('copy'); } catch (_) {} }
  gpShareRender(ok ? '✓ Copied — paste it into an email, text or group chat.' : 'Select the link above and copy it.');
  if (typeof window.track === 'function') window.track('pool_share_copy', {});
}
async function gpShareStop() {
  if (!gpPool.shareToken) return;
  if (!confirm("Stop sharing? The link you sent will stop working. You can make a new link any time.")) return;
  const old = gpPool.shareToken;
  delete gpPool.shareToken;
  localStorage.setItem('coldnumbers_gp_pool', JSON.stringify(gpPool));
  clearTimeout(_cloudPushTimer);
  if (!(await cloudSavePool())) {
    gpPool.shareToken = old;
    localStorage.setItem('coldnumbers_gp_pool', JSON.stringify(gpPool));
    clearTimeout(_cloudPushTimer); gpSaveState('error');
    showToast("Couldn't stop sharing — check your connection and try again", 4000);
    return;
  }
  gpShareRender();
  showToast('Sharing stopped — the old link no longer works');
  if (typeof window.track === 'function') window.track('pool_share_stop', {});
}

// Every write to a pool key also saves to the account (once signed in + loaded).
;(function patchPoolPersistence() {
  const orig = Storage.prototype.setItem;
  Storage.prototype.setItem = function (key, value) {
    orig.call(this, key, value);
    if (typeof key === 'string' && key.startsWith('coldnumbers_gp_')
        && key !== 'coldnumbers_gp_demo_seeded' && key !== GP_OWNER) {
      try { cloudSchedulePoolPush(); } catch (_) {}
    }
  };
})();

// The Pool Manager screens are in assets/pool-manager.js (money rules: assets/pool-ledger.js).

// ══════════════════════════════════════════════════════════════

document.addEventListener('DOMContentLoaded', async () => {
  markActiveNav();
  applyTheme();

  const pg = document.body.dataset.page || 'home';

  // Pages that need draw history data
  const needsDraws = ['home','analyzer','analyzerMM','trends','results','calc','random','map','scanner','account','math'];
  if (needsDraws.includes(pg)) {
    await loadDraws();
    loadStateTaxes();
    loadFedBrackets();
  }

  if (DD.length) showToast('coldnumbers · ' + DD.length + ' draws loaded (PB: ' + DD.filter(d => d.game === 'powerball').length + ' · MM: ' + DD.filter(d => d.game === 'megamillions').length + ')', 4000);

  // ── HOME ──────────────────────────────────────────────────────
  if (pg === 'home') {
    syncHomeWeather();
    loadLiveForecast();
    loadLiveMMForecast();
    updateHomeJackpots();
    renderLastDrawNumbers();
    setInterval(updateCountdown, 30000); updateCountdown();
    setInterval(updateMMCountdown, 30000); updateMMCountdown();
    checkLiveDrawPopup();
    setInterval(() => { updateHomeJackpots(); }, 600000);
    if (typeof initHomeMap === 'function') cnWhenVisible(document.getElementById('homeMap'), initHomeMap);
  }

  // ── POWERBALL ANALYZER ───────────────────────────────────────
  if (pg === 'analyzer') {
    const urlGame = new URLSearchParams(location.search).get('game');
    if (urlGame) setRngGame && setRngGame(urlGame);
    await loadLiveForecast();  // fetch real draw-night weather first
    renderForecast();
    selectDrawDateWeather();
    updateWeightBar();
    setTimeout(updateAnalyzerPrizeTables, 1500);
    // Show next draw date + countdown
    updateAnalyzerDrawDate();
    setInterval(updateAnalyzerDrawDate, 60000);
    // Auto-run analyzer once data is loaded
    setTimeout(() => { if (DD.length > 0) { autoPBWeights(); } }, 600);
  }

  // ── MEGA MILLIONS ANALYZER ───────────────────────────────────
  if (pg === 'analyzerMM') {
    await loadLiveMMForecast();  // fetch real draw-night weather first
    renderMMForecast();
    selectMMDrawDateWeather();
    updateMMWeightBar();
  }

  // ── TRENDS ───────────────────────────────────────────────────
  if (pg === 'trends') {
    const urlGame = new URLSearchParams(location.search).get('game');
    if (urlGame) setTrendGame(urlGame);
    initTrends();
  }

  // ── RESULTS ──────────────────────────────────────────────────
  if (pg === 'results') {
    const urlGame = new URLSearchParams(location.search).get('game');
    if (urlGame) setResultsGame(urlGame); // internally calls loadResults()
    else loadResults();
  }

  // ── CALCULATOR ───────────────────────────────────────────────
  if (pg === 'calc') {
    const urlGame = new URLSearchParams(location.search).get('game');
    // Prefill current jackpot + cash value from draw_news, THEN apply the game.
    // updateHomeJackpots() populates _liveJP and fills #c-jackpot / #c-cash when
    // they haven't been user-edited, and fires calcAll() at the end.
    const fireCompound = () => {
      if (typeof calcCompound === 'function' && document.getElementById('compoundChart')) {
        try { calcCompound(); } catch(_){}
      }
      if (typeof calcVS === 'function' && document.getElementById('vsChart')) {
        try { calcVS(); } catch(_){}
      }
    };
    updateHomeJackpots().then(() => {
      if (urlGame) setCalcGame(urlGame); // switches game + recalcs with live data
      else setCalcGame('pb'); // default PB, ensures live jackpot + cash get written
      // Render Growth Strategy chart on load (don't wait for user to click Run)
      fireCompound();
    }).catch(() => {
      if (urlGame) setCalcGame(urlGame);
      else calcAll();
      fireCompound();
    });
    // Refresh jackpot every 10 min in case user leaves calc tab open through a draw
    setInterval(updateHomeJackpots, 600000);
  }

  // ── RANDOM / QUICK PICK ──────────────────────────────────────
  if (pg === 'random') {
    const urlGame = new URLSearchParams(location.search).get('game');
    if (urlGame && typeof setRngGame === 'function') setRngGame(urlGame);
    renderSavedPicks();
  }

  // ── GROUP / POOL PLAY ─────────────────────────────────────────
  if (pg === 'groupplay') {
    gpInitRedesign();
  }

  // ── SCANNER ───────────────────────────────────────────────────
  if (pg === 'scanner') {
    const urlGame = new URLSearchParams(location.search).get('game');
    if (urlGame && typeof setScanGame === 'function') setScanGame(urlGame);
  }

  // ── WINNER MAP ────────────────────────────────────────────────
  if (pg === 'map') {
    if (typeof initMap === 'function') setTimeout(initMap, 300);
  }

  // ── ACCOUNT ───────────────────────────────────────────────────
  if (pg === 'account') {
    renderAccountStats();
  }

  // ── MATH & STATISTICS ─────────────────────────────────────────
  if (pg === 'math') {
    const urlGame = new URLSearchParams(location.search).get('game');
    if (urlGame && typeof setMathGame === 'function') setMathGame(urlGame);
    if (typeof computeMathStats === 'function') computeMathStats();
  }
});
// ── GA4 DELEGATED EVENT TRACKING ──────────────────────────────────────────
// State nav clicks
document.addEventListener('click', function(e) {
  const a = e.target.closest('a[href]');
  if (!a) return;
  const href = a.getAttribute('href') || '';

  // State navigation (link/affiliate/button clicks in general: assets/analytics.js)
  if (/states\/[a-z-]+(\.html)?(?:[?#]|$)/.test(href)) {
    const slug = href.replace(/.*states\//, '').replace(/\.html.*|[?#].*/, '');
    window.track('state_nav', { to: slug });
  }
});
